import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  generateAllPlans,
  generatePlan,
  polishPlan,
  uploadFiles,
  getCalendar,
  getProblemStatements,
  saveVersion,
  syncDailyPlan,
  setBasePlan,
  getDailyPlan,
  getWeeklyPlan,
  syncWeeklyPlan,
  getHomeworkPlan,
  syncHomeworkPlan,
  getPlannerInputs,
  savePlannerInputs,
  fetchCampusSyllabus,
  lookupScheduleRepo,
  saveScheduleRepo,
} from "../api";
import Btn from "../components/Btn";
import {
  saveTeamMembersBulk,
  getTeamMembers,
  generateTeamSchedule,
  getTeamSchedule,
  saveTeamScheduleEdits,
  quickCreateProblemStatement,
} from "../api";
import Badge from "../components/Badge";
import Card from "../components/Card";
import TeamMemberPlanCard from "../components/TeamMemberPlanCard";
import { UploadZone } from "../components/UploadZone";
import AssessmentContentPanel, { defaultAssessmentConfig } from "../components/AssessmentContentPanel";
import { ModuleCard } from "../components/ModuleCard";
import HomeworkPlanView from "../components/HomeworkPlanView";
import ProblemFrameworkPanel from "../components/ProblemFrameworkPanel";
import LevelSelectionCards from "../components/LevelSelectionCards";
import ProblemDuplicateWatcher from "../components/ProblemDuplicateWatcher";
import { removeFeedsNext } from "../utils/sanitizeLearningContent";
import RequiredChecklist from "../components/RequiredChecklist";
import { getRequiredIssues, missingOnPage } from "../utils/requiredFields";
import ProductStagePlanPanel from "../components/ProductStagePlanPanel";
import PlanReviewPanel from "../components/PlanReviewPanel";
import { formatProductStageBlock, parseProductStagePlan, spreadProductStages, dtPlaybookMapPayload } from "../utils/productStagePlan";
import ProfileSuggestionChips from "../components/ProfileSuggestionChips";
import SkillRatingsEditor, {
  pruneSkillRatings,
  formatRatedItems,
  normalizeItemRatings,
  emptyItemRatings,
} from "../components/SkillRatingsEditor";
import MemberAssessPanel, { assessFilledCount } from "../components/MemberAssessPanel";
import SubjectPreferencesPanel, {
  mergeSubjectPreferences,
  parseSubjectsFromSyllabusText,
  subjectPrefKey,
} from "../components/SubjectPreferencesPanel";
import DreamCompanyPicker from "../components/DreamCompanyPicker";
import {
  FancyInput,
  FancySelect,
  NativeTimeField,
  FancyTextarea,
  FieldGroup,
  FieldShell,
  fancyControlStyle,
  formatTime12,
  formatDurationLabel,
  validateDayTiming,
  timeToMinutes,
  addMinutesToTime,
  minutesBetween,
  paceTone,
} from "../components/FormControls";
import {
  SNS_CAMPUSES,
  getCampusByName,
  isSchoolCampus,
  departmentsForCollege,
  SEMS_FOR_YEAR,
  YEAR_FROM_SEM,
} from "../constants/snsCampuses";
import {
  downloadRosterTemplate,
  downloadIndividualsTemplate,
  downloadTeamsTemplate,
  exportRosterSheet,
  parseRosterSheet,
  groupRosterTeams,
  isMultiTeamRoster,
} from "../utils/rosterSheet";
import TeamProblemMap from "../components/TeamProblemMap";
import {
  DEFAULT_ENABLED_MODULES,
  mergeEnabledModules,
  FEATURE_TOGGLES,
  buildWizardPages,
  PageIncludeToggle,
  FormSection,
  WizardStepper,
  WizardFooter,
  WizardPageShell,
  WizardLayout,
  PlannerStickyHeader,
} from "../components/InputWizard";
import {
  Box,
  Paper,
  Stack,
  Button,
  Alert,
  Typography,
} from "@mui/material";
import { LAYOUT } from "../constants/colors";
import DayActivityEditor from "../components/DayActivityEditor";
import { slotOverrideInstruction, SLOT_OVERRIDE_KEY, splitStoredOverrides, activeSlotOverrides, DAY_ACTIVITY_SLOTS, HOMEWORK_ACTIVITY_SLOTS, makeTimingKey, syncDailySlotsToTiming, visibleDailyBlueprint } from "../constants/dayActivities";
import { homeworkHasBody, homeworkPlanFromDaily, homeworkRowsOnly, withHomeworkRows } from "../utils/homeworkRows";
import { buildWeeklyFromDaily } from "../utils/weeklyFromDaily";
import {
  C, MODULES, MAX_IA,
  SHARED_PARAMS, SUBJECT_PARAMS, DSA_SD_PARAMS, STUDENT_PARAMS,
  DT_STAGES,
  getRolesForCompany, roleSelectValue, resolveDreamCompany,
} from "../constants";
import { toDD } from "../utils";
import { formatApiError } from "../utils/apiError";
import { friendlyNotice } from "../utils/userNotice";
import { useAppNotify } from "../components/AppNotify";
import { adoptWorkspaceUser, consumeFreshWorkspace, clearWorkspaceCache } from "../utils/workspaceIsolation";

// ── Local constants ───────────────────────────────────────────────────────────
const LS = {
  user:         "ap_user",
  step:         "ap_step",
  textInputs:   "ap_textInputs",
  startDate:    "ap_startDate",
  endDate:      "ap_endDate",
  calendarData: "ap_calendarData",
  exams:        "ap_exams",
  plans:        "ap_plans",
  activeTab:    "ap_activeTab",
  lastGenInputs:"ap_lastGenInputs",
  plannerMode:  "ap_plannerMode",
  numDays:      "ap_numDays",
  dailyStart:   "ap_dailyStart",
  dailyEnd:     "ap_dailyEnd",
  lunchStart:   "ap_lunchStart",
  lunchEnd:     "ap_lunchEnd",
  breaks:       "ap_breaks",
  dtActivated:  "ap_dtActivated",
  productStagePlan: "ap_productStagePlan",
};

const EMPTY_TEXT_INPUTS = {
  problem: "", syllabus: "", questions: "", instructions: "",
  dsaSyllabus: "", systemDesign: "", assessment: "",
  capability: "", difficulties: "", interest: "", dislikes: "", domain: "",
  studyMedium: "auto",
  manualPrompt: "",
  skillRatings: {},
  itemRatings: emptyItemRatings(),
};

const EMPTY_FILES = {
  problem: [], syllabus: [], questions: [], instructions: [],
  dsaSyllabus: [], systemDesign: [], assessment: [],
  capability: [], interest: [], domain: [], assessmentBank: [],
  manualPrompt: [],
};

function blankTeamMember() {
  const campus = SNS_CAMPUSES[0];
  return {
    name: "", rollNumber: "", email: "", leetcodeUsername: "", targetRole: "", targetCompany: "",
    college: campus.name,
    collegeYear: 2, semester: 3, schoolGrade: 10, department: campus.departments[0],
    skillLevel: 3, capability: "", difficulties: "", interest: "", dislikes: "", domain: "", assessment: "",
    studyMedium: "auto",
    teamName: "",
    problemStatement: "",
    subjectPreferences: [],
    skillRatings: {},
    itemRatings: emptyItemRatings(),
  };
}

const lsGet = (key, fallback) => {
  try {
    const val = localStorage.getItem(key);
    return val !== null ? JSON.parse(val) : fallback;
  } catch { return fallback; }
};
const lsSet = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch (_) {}
};

function examTypeBadge(examType) {
  if (examType === "AU")  return { label: "AU",  bg: C.auBg,    color: C.auColor };
  if (examType === "IA3") return { label: "IA3", bg: "#E7F0F6", color: "#1B4F72" };
  if (examType === "IA2") return { label: "IA2", bg: "#E7F0F6", color: "#143B56" };
  return                         { label: "IA1", bg: "#EEF2FF", color: C.primary };
}

// ── DT Stage Flow Visualizer ──────────────────────────────────────────────────
function DTPlaybookFlow({ activeStage }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 0,
      overflowX: "auto", padding: "8px 0", marginBottom: 12,
    }}>
      {DT_STAGES.map((stage, i) => {
        const isActive = activeStage === stage.key;
        const isDone = DT_STAGES.findIndex((s) => s.key === activeStage) > i;
        return (
          <React.Fragment key={stage.key}>
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 4, minWidth: 90,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: isDone || isActive ? stage.color : C.border,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: isActive ? `3px solid ${stage.color}` : "none",
                boxShadow: isActive ? `0 0 0 4px ${stage.color}22` : "none",
                transition: "all 0.2s",
              }}>
                <i className={`ti ${stage.icon}`} style={{
                  color: (isDone || isActive) ? "#fff" : C.muted, fontSize: 16,
                }} />
              </div>
              <span style={{
                fontSize: 10, fontWeight: isActive ? 700 : 500,
                color: isActive || isDone ? stage.color : C.muted,
                textAlign: "center", lineHeight: 1.2,
              }}>
                {stage.label}
              </span>
            </div>
            {i < DT_STAGES.length - 1 && (
              <div style={{
                flex: 1, height: 2, minWidth: 20,
                background: isDone ? DT_STAGES[i].color : C.border,
                marginBottom: 18,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function applyFieldWithRatings(prev, key, value) {
  const ir = normalizeItemRatings(prev);
  if (Object.prototype.hasOwnProperty.call(ir, key)) {
    ir[key] = pruneSkillRatings(value, ir[key]);
  }
  return {
    ...prev,
    [key]: value,
    itemRatings: ir,
    skillRatings: ir.capability,
  };
}

function applyKindRatings(prev, kind, ratings) {
  const ir = normalizeItemRatings(prev);
  ir[kind] = ratings || {};
  return { ...prev, itemRatings: ir, skillRatings: ir.capability };
}

// ── Student Profile Section ───────────────────────────────────────────────────
function StudentProfile({
  members,
  onChange,
  soloNotice,
  generateBtnLabel = "Generate Plans",
  assessment = "",
  onAssessmentChange,
  showTeamGoals = false,
  files = {},
  setFiles,
  embedded = false,
  teamName,
  onTeamNameChange,
  onRosterImported,
  showToast,
  rosterMode = "team",
}) {
  // soloNotice=true  → only 1 member entered, show a gentle info note
  // soloNotice=false → 2+ members, show full team fields without any save button

  const SKILL_LABELS = {
    1: "1 · Slow",
    2: "2 · Steady",
    3: "3 · Average",
    4: "4 · Fast",
    5: "5 · Fast+",
  };
  const FIELD_META = {
    name: { tone: "blue", icon: "ti-user" },
    rollNumber: { tone: "slate", icon: "ti-id" },
    email: { tone: "teal", icon: "ti-mail" },
    leetcodeUsername: { tone: "amber", icon: "ti-code" },
  };
  const fields = [
    { key: "name",          label: "Name *",        placeholder: "e.g. Arun Kumar"  },
    { key: "rollNumber",    label: "Roll Number",   placeholder: "e.g. 21IT001"     },
    { key: "email",         label: "Email",         placeholder: "arun@college.edu" },
    { key: "leetcodeUsername", label: "LeetCode username", placeholder: "handle only, no URL" },
  ];

  const blank = {
    name: "", rollNumber: "", email: "", leetcodeUsername: "", targetRole: "", targetCompany: "",
    college: SNS_CAMPUSES[0].name,
    collegeYear: 2, semester: 3, schoolGrade: 10, department: SNS_CAMPUSES[0].departments[0],
    skillLevel: 3, capability: "", difficulties: "", interest: "", dislikes: "", domain: "", assessment: "",
    studyMedium: "auto",
    teamName: "",
    problemStatement: "",
    subjectPreferences: [],
    skillRatings: {},
    itemRatings: emptyItemRatings(),
  };

  const list  = members && members.length ? members : [blank];
  const isTeam = list.length > 1;
  const rosterTeams = groupRosterTeams(list);
  const multiTeam = rosterTeams.filter((t) => t.members.length).length >= 2;
  const [openMemberIdx, setOpenMemberIdx] = useState(0);
  const [filterTeamKey, setFilterTeamKey] = useState("");

  function setCampus(i, campusName) {
    const campus = getCampusByName(campusName) || SNS_CAMPUSES[0];
    const depts = campus.departments || ["Other"];
    onChange(list.map((m, idx) => {
      if (idx !== i) return m;
      const next = {
        ...m,
        college: campus.name,
        department: depts.includes(m.department) ? m.department : depts[0],
      };
      if (campus.kind === "school") {
        next.schoolGrade = clampGrade(m.schoolGrade);
        next.collegeYear = null;
        next.semester = null;
      } else {
        const y = Number(m.collegeYear) || 2;
        const pair = SEMS_FOR_YEAR[y] || [3, 4];
        next.collegeYear = y;
        next.semester = pair.includes(Number(m.semester)) ? Number(m.semester) : pair[0];
        next.schoolGrade = null;
      }
      return next;
    }));
  }

  function clampGrade(g) {
    const n = Number(g);
    if (!Number.isFinite(n)) return 10;
    return Math.min(12, Math.max(1, Math.round(n)));
  }

  function updateMember(i, key, value) { onChange(list.map((m, idx) => idx === i ? { ...m, [key]: value } : m)); }
  function setMemberField(i, key, value) {
    onChange(list.map((m, idx) => (idx === i ? applyFieldWithRatings(m, key, value) : m)));
  }
  function setMemberRatings(i, kind, ratings) {
    onChange(list.map((m, idx) => (idx === i ? applyKindRatings(m, kind, ratings) : m)));
  }

  // Subject like/dislike controls are shown in Step 6 (Subjects page),
  // so we do not load per-member subject catalogs here.

  function setCompany(i, selectVal) {
    onChange(list.map((m, idx) => {
      if (idx !== i) return m;
      if (!selectVal) return { ...m, targetCompany: "", targetRole: "" };
      const roles = getRolesForCompany(selectVal);
      const roleStillValid = roles.includes(m.targetRole);
      return {
        ...m,
        targetCompany: selectVal,
        targetRole: roleStillValid ? m.targetRole : "",
      };
    }));
  }

  function setRole(i, selectVal) {
    updateMember(i, "targetRole", selectVal || "");
  }
  function addMember() {
    const src = filterTeamKey
      ? rosterTeams.find((t) => t.key === filterTeamKey)
      : rosterTeams[0];
    onChange([
      ...list,
      {
        ...blank,
        teamName: src?.name || "",
        problemStatement: src?.problemStatement || "",
      },
    ]);
  }

  function updateTeamMeta(teamKey, patch) {
    onChange(list.map((m) => {
      const key = String(m.teamName || "").trim() || "Unassigned";
      if (key !== teamKey) return m;
      const next = { ...m };
      if (patch.name != null) next.teamName = patch.name;
      if (patch.problemStatement != null) next.problemStatement = patch.problemStatement;
      return next;
    }));
    if (patch.name != null && filterTeamKey === teamKey) {
      setFilterTeamKey(String(patch.name || "").trim() || "Unassigned");
    }
    if (patch.name != null && onTeamNameChange && (filterTeamKey === teamKey || !filterTeamKey)) {
      onTeamNameChange(patch.name);
    }
  }
  function removeMember(i) {
    if (list.length <= 1) return;
    onChange(list.filter((_, idx) => idx !== i));
    if (setFiles) {
      setFiles((prev) => {
        const next = { ...prev };
        delete next[`m${i}_capability`];
        delete next[`m${i}_interest`];
        delete next[`m${i}_domain`];
        delete next[`m${i}_assessment`];
        for (let j = i + 1; j < list.length; j++) {
          next[`m${j - 1}_capability`] = next[`m${j}_capability`] || [];
          next[`m${j - 1}_interest`] = next[`m${j}_interest`] || [];
          next[`m${j - 1}_domain`] = next[`m${j}_domain`] || [];
          next[`m${j - 1}_assessment`] = next[`m${j}_assessment`] || [];
          delete next[`m${j}_capability`];
          delete next[`m${j}_interest`];
          delete next[`m${j}_domain`];
          delete next[`m${j}_assessment`];
        }
        return next;
      });
    }
  }

  const importInputRef = useRef(null);

  async function handleImportSheet(file) {
    if (!file) return;
    try {
      const parsed = await parseRosterSheet(file);
      onChange(parsed.members);
      if (parsed.teamName && onTeamNameChange) onTeamNameChange(parsed.teamName);
      if (parsed.sharedAssessment && onAssessmentChange) onAssessmentChange(parsed.sharedAssessment);
      const teams = parsed.teams || groupRosterTeams(parsed.members);
      if (teams.length) setFilterTeamKey(teams[0].key);
      onRosterImported?.(parsed);
      const namedTeams = teams.filter((t) => t.members.length);
      showToast?.(
        "success",
        parsed.mode === "individuals"
          ? `Imported ${parsed.members.length} individuals (skills only — rate them after import). Review → Generate will build a separate schedule for each person.`
          : namedTeams.length >= 2
            ? `Imported ${parsed.members.length} members across ${namedTeams.length} teams. Each team keeps its own problem statement — check the mapping, then Review → Generate.`
            : `Imported ${parsed.members.length} member${parsed.members.length === 1 ? "" : "s"} (skills only — set star ratings and progress after import). Go to Review → Generate.`
      );
    } catch (e) {
      showToast?.("error", e?.message || "Could not import sheet");
    }
  }

  function handleExportSheet() {
    try {
      exportRosterSheet(list, { teamName, sharedAssessment: assessment, mode: rosterMode });
      showToast?.("success", "Roster exported as Excel.");
    } catch (e) {
      showToast?.("error", e?.message || "Export failed");
    }
  }

  const body = (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {!embedded && (
          <p style={{ fontWeight: 700, fontSize: 15, color: C.text, margin: 0 }}>
            {isTeam ? `Team Members (${list.length})` : "Your profile"}
          </p>
        )}
        {embedded && (
          <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
            {isTeam ? `${list.length} members` : "Solo — add a member to unlock team mode"}
          </p>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            title="College: Year + Semester. School: School Class 1–12 (not marks). Learning Pace 1–5 = Slow→Fast."
            onClick={() => downloadRosterTemplate()}
            style={{
              padding: "9px 12px", borderRadius: 10,
              border: `1px solid ${C.border}`, background: C.surface, color: C.text,
              fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}
          >
            <i className="ti ti-download" style={{ marginRight: 5 }} />
            Team template
          </button>
          <button
            type="button"
            title="Any number of teams. Same Team Name = one team. Each team keeps its own Problem Statement."
            onClick={() => downloadTeamsTemplate()}
            style={{
              padding: "9px 12px", borderRadius: 10,
              border: `1px solid ${C.primary}55`, background: C.primarySoft, color: C.primaryDark,
              fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}
          >
            <i className="ti ti-download" style={{ marginRight: 5 }} />
            Teams template
          </button>
          <button
            type="button"
            title="College: Year + Semester. School: School Class 1–12 (not marks). Learning Pace 1–5 = Slow→Fast."
            onClick={() => downloadIndividualsTemplate()}
            style={{
              padding: "9px 12px", borderRadius: 10,
              border: `1px solid ${C.border}`, background: C.surface, color: C.text,
              fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}
          >
            <i className="ti ti-download" style={{ marginRight: 5 }} />
            Individuals template
          </button>
          <button
            type="button"
            onClick={() => importInputRef.current?.click()}
            style={{
              padding: "9px 12px", borderRadius: 10,
              border: `1px solid ${C.primary}55`, background: C.primarySoft, color: C.primaryDark,
              fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}
          >
            <i className="ti ti-file-import" style={{ marginRight: 5 }} />
            Import sheet
          </button>
          <button
            type="button"
            onClick={handleExportSheet}
            disabled={!list.some((m) => m?.name?.trim())}
            style={{
              padding: "9px 12px", borderRadius: 10,
              border: `1px solid ${C.border}`, background: C.surface, color: C.text,
              fontSize: 12.5, fontWeight: 700, cursor: list.some((m) => m?.name?.trim()) ? "pointer" : "not-allowed",
              opacity: list.some((m) => m?.name?.trim()) ? 1 : 0.55,
            }}
          >
            <i className="ti ti-file-export" style={{ marginRight: 5 }} />
            Export sheet
          </button>
          <button type="button" onClick={addMember} style={{
            padding: "10px 16px", borderRadius: 10, border: "none",
            background: C.primarySoft, color: C.primaryDark, fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            <i className="ti ti-user-plus" style={{ marginRight: 6 }} />
            Add member
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) handleImportSheet(f);
            }}
          />
        </div>
      </div>

      <div style={{
        fontSize: 12.5, padding: "11px 13px", marginBottom: 14, borderRadius: 12,
        background: C.surfaceAlt, color: C.muted, fontWeight: 600, lineHeight: 1.45,
        border: `1px solid ${C.border}`,
      }}>
        {rosterMode === "individuals" ? (
          <>
            <strong style={{ color: C.text }}>Individuals mode:</strong> each row gets a separate solo schedule on Generate. Template columns: <em>Year + Semester</em> for college, <em>School Class (1–12)</em> for school (not marks), <em>Learning Pace (1–5)</em> for Slow→Fast classroom speed (not per-skill stars). Rate skills in Assessment after import.
          </>
        ) : multiTeam ? (
          <>
            Same <em>Team Name</em> = one team, each with its own problem. Switch teams with the chips below.
          </>
        ) : (
          <>
            <strong style={{ color: C.text }}>Team mode:</strong> one team if every row shares the same Team Name (or leave it blank). For many teams, put a Team Name + Problem Statement on each row. Use <em>Teams template</em>, or <em>Individuals template</em> when each person needs their own schedule.
          </>
        )}
      </div>

      {rosterMode !== "individuals" && multiTeam && (
        <TeamProblemMap
          teams={rosterTeams}
          activeKey={filterTeamKey}
          onSelect={setFilterTeamKey}
          onChangeTeam={updateTeamMeta}
          editable
        />
      )}

      {isTeam && onTeamNameChange && rosterMode !== "individuals" && !multiTeam && (
        <div style={{
          marginBottom: 16, padding: "12px 14px", borderRadius: 10,
          background: "#F8FAFC",
          border: `1px solid ${C.border}`,
        }}>
          <FancyInput
            tone="navy"
            icon="ti-users-group"
            label="Team name"
            value={teamName || ""}
            onChange={(e) => onTeamNameChange(e.target.value)}
            placeholder="e.g. Team Alpha"
          />
        </div>
      )}

      {isTeam && (
        <div style={{
          fontSize: 13, padding: "12px 14px", marginBottom: 16, borderRadius: 12,
          background: "#E7F0F6", color: "#143B56", fontWeight: 600,
          border: "1px solid #1B4F7222",
          display: "flex", alignItems: "flex-start", gap: 10,
        }}>
          <i className="ti ti-users" style={{ fontSize: 18, marginTop: 1, flexShrink: 0 }} />
          <div>
            <div>Team mode — shared goals plus one member at a time.</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginTop: 4 }}>
              Open a member → identity & campus stay visible. Assessment & persona opens as tabs (Goals, Strengths, Likes…) so the page stays light.
            </div>
          </div>
        </div>
      )}

      {soloNotice && !isTeam && (
        <div style={{
          fontSize: 13, padding: "12px 14px", marginBottom: 16, borderRadius: 12,
          background: C.surfaceAlt, color: C.text, fontWeight: 500,
          border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="ti ti-info-circle" style={{ fontSize: 16, color: C.primary, flexShrink: 0 }} />
          Solo mode. Add another member anytime to switch to team inputs.
        </div>
      )}

      {isTeam && (
        <FieldGroup title="Team assessment & goals (shared)" icon="ti-chart-bar" tone="ink" style={{ marginBottom: 18, marginTop: 0 }}>
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.45 }}>
            Company JD, package band, certifications — shared by everyone. Members can still add personal goals below.
          </p>
          <FancyTextarea
            tone="ink"
            value={assessment || ""}
            onChange={(e) => onAssessmentChange?.(e.target.value)}
            placeholder="e.g. Team target 6–10 LPA · Zoho / Freshworks · AWS CCP + LeetCode 150"
            style={{ minHeight: 88 }}
          />
          {setFiles && (
            <UploadZone
              paramKey="assessment"
              files={files}
              setFiles={setFiles}
              compact
              hint="PDF, DOCX, Excel, images, CSV"
            />
          )}
        </FieldGroup>
      )}

      {list.map((profile, i) => {
        const teamKey = String(profile.teamName || "").trim() || "Unassigned";
        if (multiTeam && filterTeamKey && teamKey !== filterTeamKey) return null;
        const memberCollapsed = isTeam && openMemberIdx !== i;
        const filledN = assessFilledCount(profile);
        return (
        <div key={i} style={{
          border: `1px solid ${!isTeam || openMemberIdx === i ? C.borderStrong : C.border}`,
          borderRadius: 10,
          padding: memberCollapsed ? "12px 14px" : 16,
          marginBottom: i === list.length - 1 ? 0 : 12,
          background: C.surface,
          boxShadow: "none",
        }}>
          {isTeam && (
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: memberCollapsed ? 0 : 14, gap: 10, flexWrap: "wrap",
            }}>
              <button
                type="button"
                onClick={() => setOpenMemberIdx(i)}
                style={{
                  border: "none", background: "transparent", cursor: "pointer", padding: 0,
                  fontSize: 14, fontWeight: 600, color: C.text,
                  display: "inline-flex", alignItems: "center", gap: 8, textAlign: "left",
                }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: C.primarySoft, color: C.primary,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 600,
                }}>{i + 1}</span>
                Member {i + 1}{profile.name ? ` · ${profile.name}` : ""}
                {multiTeam && (
                  <span style={{
                    fontSize: 11, fontWeight: 800, color: C.primaryDark,
                    background: C.primarySoft, borderRadius: 999, padding: "2px 8px",
                  }}>
                    {profile.teamName || "Unassigned"}
                  </span>
                )}
                <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>
                  {memberCollapsed ? "▸" : "▾"}
                </span>
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {memberCollapsed && (
                  <span style={{
                    fontSize: 11.5, fontWeight: 700, color: C.muted,
                    background: C.surfaceAlt, border: `1px solid ${C.border}`,
                    borderRadius: 999, padding: "4px 10px",
                  }}>
                    {filledN}/6 assessment · pace {profile.skillLevel ?? 3}/5
                  </span>
                )}
                {memberCollapsed && (
                  <button
                    type="button"
                    onClick={() => setOpenMemberIdx(i)}
                    style={{
                      border: `1px solid ${C.primary}55`, background: C.primarySoft, color: C.primaryDark,
                      fontSize: 12, fontWeight: 750, padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                )}
                <button type="button" onClick={() => removeMember(i)}
                  style={{ border: "none", background: C.errorBg, color: C.error, fontSize: 12, cursor: "pointer", fontWeight: 650, padding: "6px 10px", borderRadius: 8 }}>
                  Remove
                </button>
              </div>
            </div>
          )}
          {memberCollapsed ? null : (
          <>
          <FieldGroup title="Identity" icon="ti-user" tone="blue" style={{ marginTop: 0 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, alignItems: "start" }}>
              {fields.filter((f) => f.key !== "leetcodeUsername").map((f) => {
                const meta = FIELD_META[f.key] || { tone: "blue", icon: "ti-forms" };
                return (
                  <FancyInput
                    key={f.key}
                    tone={meta.tone}
                    icon={meta.icon}
                    label={f.label}
                    hint=" "
                    type="text"
                    value={profile[f.key]||""}
                    placeholder={f.placeholder}
                    onChange={e => updateMember(i, f.key, e.target.value)}
                  />
                );
              })}
            </div>
          </FieldGroup>

          <FieldGroup title="Pace & practice" icon="ti-gauge" tone="coral">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, alignItems: "start" }}>
            <FancySelect
              tone={paceTone(profile.skillLevel)}
              icon="ti-gauge"
              label="Learning pace"
              hint="Sets time-box, extra work, and Easy vs stretch coding."
              value={profile.skillLevel??3}
              onChange={e => updateMember(i,"skillLevel",Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{SKILL_LABELS[n]}</option>
              ))}
            </FancySelect>
            <FancySelect
              tone="amber"
              icon="ti-language"
              label="Study medium"
              hint="Tamil → Learning videos prefer Tamil explainers."
              value={profile.studyMedium || "auto"}
              onChange={(e) => updateMember(i, "studyMedium", e.target.value)}
            >
              <option value="auto">Auto (from Difficulties)</option>
              <option value="ta">Tamil</option>
              <option value="en">English</option>
              <option value="hi">Hindi</option>
            </FancySelect>
            {(() => {
              const f = fields.find((x) => x.key === "leetcodeUsername");
              const meta = FIELD_META.leetcodeUsername;
              return (
                <FancyInput
                  key="leetcodeUsername"
                  tone={meta.tone}
                  icon={meta.icon}
                  label={f.label}
                  hint="Handle only. Solved problems are skipped."
                  type="text"
                  value={profile.leetcodeUsername||""}
                  placeholder={f.placeholder}
                  onChange={e => updateMember(i, "leetcodeUsername", e.target.value)}
                />
              );
            })()}
            </div>
          </FieldGroup>

          {/* Campus + Year/Sem/Dept (or Class) — one compact row */}
          {(() => {
            const campus = getCampusByName(profile.college);
            const school = isSchoolCampus(profile.college);
            const depts = departmentsForCollege(profile.college);
            const selectedYear = Number(profile.collegeYear || YEAR_FROM_SEM[profile.semester] || 2);
            return (
              <FieldGroup title="Campus & academic level" icon="ti-school" tone="teal">
                <div style={{
                  display: "grid",
                  gridTemplateColumns: school
                    ? "repeat(3, minmax(0, 1fr))"
                    : "repeat(4, minmax(0, 1fr))",
                  gap: 16,
                  alignItems: "start",
                }}>
                  <FancySelect
                    tone="teal"
                    icon="ti-building-community"
                    label="College / Campus"
                    hint=" "
                    value={campus?.name || ""}
                    onChange={(e) => setCampus(i, e.target.value)}
                  >
                    <option value="">Select campus…</option>
                    {SNS_CAMPUSES.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.kind === "school" ? `${c.name} (School)` : c.name}
                      </option>
                    ))}
                  </FancySelect>

                  {school ? (
                    <FancySelect
                      tone="blue"
                      icon="ti-numbers"
                      label="School class (1–12)"
                      hint="Class / std — not marks"
                      value={clampGrade(profile.schoolGrade)}
                      onChange={(e) => updateMember(i, "schoolGrade", Number(e.target.value))}
                    >
                      {(campus?.grades || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).map((g) => (
                        <option key={g} value={g}>Class {g}</option>
                      ))}
                    </FancySelect>
                  ) : (
                    <>
                      <FancySelect
                        tone="blue"
                        icon="ti-calendar-stats"
                        label="Year"
                        hint="Changes the depth of concepts, examples, and product tasks."
                        value={profile.collegeYear || YEAR_FROM_SEM[profile.semester] || 2}
                        onChange={(e) => {
                          const y = Number(e.target.value);
                          const pair = SEMS_FOR_YEAR[y] || [3, 4];
                          const semOk = pair.includes(Number(profile.semester));
                          onChange(list.map((m, idx) =>
                            idx === i
                              ? { ...m, collegeYear: y, semester: semOk ? Number(m.semester) : pair[0] }
                              : m
                          ));
                        }}
                      >
                        <option value={1}>1st year</option>
                        <option value={2}>2nd year</option>
                        <option value={3}>3rd year</option>
                        <option value={4}>4th year</option>
                      </FancySelect>
                      <FancySelect
                        tone="amber"
                        icon="ti-calendar"
                        label="Semester"
                        hint=" "
                        value={profile.semester || (SEMS_FOR_YEAR[profile.collegeYear || 2] || [3, 4])[0]}
                        onChange={(e) => {
                          const sem = Number(e.target.value);
                          onChange(list.map((m, idx) =>
                            idx === i
                              ? { ...m, semester: sem, collegeYear: YEAR_FROM_SEM[sem] || 1 }
                              : m
                          ));
                        }}
                      >
                        {(SEMS_FOR_YEAR[profile.collegeYear || YEAR_FROM_SEM[profile.semester] || 2] || [1, 2]).map((s) => (
                          <option key={s} value={s}>
                            Semester {s} ({YEAR_FROM_SEM[s] === 1 ? "1st" : YEAR_FROM_SEM[s] === 2 ? "2nd" : YEAR_FROM_SEM[s] === 3 ? "3rd" : "4th"} year)
                          </option>
                        ))}
                      </FancySelect>
                    </>
                  )}

                  <FancySelect
                    tone="slate"
                    icon="ti-books"
                    label={school ? "Stream" : "Department"}
                    hint=" "
                    value={depts.includes(profile.department) ? profile.department : (depts[0] || "")}
                    onChange={(e) => updateMember(i, "department", e.target.value)}
                    disabled={!campus}
                  >
                    {!campus && <option value="">Campus first…</option>}
                    {depts.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </FancySelect>
                </div>
                {!school && (
                  <div style={{
                    marginTop: 12, padding: "10px 12px", borderRadius: 8,
                    border: `1px solid ${C.border}`, background: C.surfaceAlt,
                    color: C.muted, fontSize: 12, lineHeight: 1.5,
                  }}>
                    <strong style={{ color: C.text }}>
                      {selectedYear}{selectedYear === 1 ? "st" : selectedYear === 2 ? "nd" : selectedYear === 3 ? "rd" : "th"} year plan
                    </strong>
                    {" · "}
                    {({
                      1: "Guided foundations and a small, complete product flow.",
                      2: "Connected features, basic data and validation, with guided practice.",
                      3: "An end-to-end product slice with modules, testing and practical trade-offs.",
                      4: "A polished MVP with architecture choices, security, testing and a demo rationale.",
                    })[selectedYear]}
                  </div>
                )}
              </FieldGroup>
            );
          })()}

          <FieldGroup title="Placement focus" icon="ti-briefcase" tone="amber" style={{ marginTop: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, alignItems: "start" }}>
            {/* Dream Company — professional grouped picker + shared Other */}
            <FieldShell
              tone="amber"
              icon="ti-building-skyscraper"
              label="Dream Company"
              hint="MAANG · Big Tech · India IT · Consulting"
            >
              <DreamCompanyPicker
                value={profile.targetCompany || ""}
                onChange={(name) => setCompany(i, name)}
                inputStyle={fancyControlStyle("amber")}
              />
            </FieldShell>

            {/* Target Role — RAG roles for selected / typed company */}
            {(() => {
                const company = resolveDreamCompany(profile.targetCompany);
                const roles = getRolesForCompany(company);
                const roleVal = roleSelectValue(profile.targetRole, company);

                if (!company) {
                  return (
                    <FancySelect
                      tone="coral"
                      icon="ti-target"
                      label="Target Role"
                      disabled
                      value=""
                      hint="Pick a dream company first"
                    >
                      <option value="">Pick a dream company first…</option>
                    </FancySelect>
                  );
                }

                return (
                  <FancySelect
                    tone="coral"
                    icon="ti-target"
                    label="Target Role"
                    value={roleVal}
                    onChange={(e) => setRole(i, e.target.value)}
                  >
                    <option value="">Select role for {company}…</option>
                    {roles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </FancySelect>
                );
              })()}
          </div>
          </FieldGroup>

          {/* Per-member assessment — collapsed + one tab at a time */}
          {isTeam && (
            <MemberAssessPanel
              memberIndex={i}
              profile={profile}
              updateMember={updateMember}
              setMemberField={setMemberField}
              setMemberRatings={setMemberRatings}
              files={files}
              setFiles={setFiles}
              defaultOpen={i === 0}
            />
          )}

          {/* Subject like/dislike controls live in Step 6 (Subjects page) only. */}
          </>
          )}
        </div>
        );
      })}
    </>
  );

  if (embedded) return body;
  return <Card>{body}</Card>;
}

// ── Compact calendar plan preview (shown after Build) ─────────────────────────
function CalendarPlanPreview({ data, startDate, endDate }) {
  const weeks = Array.isArray(data?.weeklySummary) ? data.weeklySummary : [];
  const meta = data?.meta || {};
  const ia = meta.iaWeekNums || [];
  const au = meta.auWeekNums || [];
  const mock = meta.mockWeekNum;

  const weekTone = (w) => {
    const n = Number(w?.week ?? w);
    if (au.includes(n)) return { bg: C.auBg, color: C.auColor, tag: "AU" };
    if (mock != null && n === mock) return { bg: C.mockBg, color: C.mockColor, tag: "Mock" };
    if (ia.includes(n)) return { bg: C.warningBg, color: "#8A6A00", tag: "IA" };
    return { bg: C.surfaceAlt, color: C.muted, tag: null };
  };

  const list = weeks.length
    ? weeks
    : Array.from({ length: meta.totalWeeks || 0 }, (_, i) => ({ week: i + 1 }));

  if (!list.length && !meta.totalWeeks) {
    return (
      <Alert severity="success" sx={{ mt: 1.5, py: 0.75 }}>
        Calendar built — weeks will appear in daily / weekly plans.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1.5, mb: 0.5 }}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 750, color: "success.main" }}>
          Plan ready · {meta.totalWeeks || list.length || "—"} weeks
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {startDate} → {endDate}
        </Typography>
        {ia.length > 0 && (
          <Typography variant="caption" color="text.secondary">IA weeks: {ia.join(", ")}</Typography>
        )}
        {mock != null && (
          <Typography variant="caption" color="text.secondary">Mock: W{mock}</Typography>
        )}
        {au.length > 0 && (
          <Typography variant="caption" color="text.secondary">AU: {au.join(", ")}</Typography>
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
          gap: 0.75,
        }}
      >
        {list.map((w) => {
          const tone = weekTone(w);
          const n = w.week ?? w;
          const days = typeof w.workingDays === "number"
            ? w.workingDays
            : Array.isArray(w.workingDays)
              ? w.workingDays.length
              : Array.isArray(w.days)
                ? w.days.length
                : null;
          return (
            <Box
              key={n}
              title={tone.tag ? `Week ${n} · ${tone.tag}` : `Week ${n}`}
              sx={{
                px: 0.75,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: tone.bg,
                border: `1px solid ${tone.tag ? `${tone.color}44` : C.border}`,
                textAlign: "center",
                transition: "transform 0.15s",
                "&:hover": { transform: "translateY(-1px)" },
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: tone.tag ? tone.color : C.text }}>
                W{n}
              </Typography>
              {tone.tag && (
                <Typography sx={{ fontSize: 9, fontWeight: 750, color: tone.color, mt: 0.15 }}>
                  {tone.tag}
                </Typography>
              )}
              {days != null && (
                <Typography sx={{ fontSize: 9, color: C.muted, mt: 0.15 }}>{days}d</Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {Array.isArray(data?.examErrors) && data.examErrors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.25, py: 0.5 }}>
          {data.examErrors.join(" · ")}
        </Alert>
      )}
    </Box>
  );
}

// ── Daily Timing Section ──────────────────────────────────────────────────────
function DailyTiming({
  numDays,
  startTime,
  endTime,
  lunchStart,
  lunchEnd,
  breaks = [],
  onDaysChange,
  onStartChange,
  onEndChange,
  onLunchStartChange,
  onLunchEndChange,
  onBreaksChange,
  embedded = false,
}) {
  const MAX_BREAKS = 4;
  const durationLabel = formatDurationLabel(startTime, endTime);

  const lunchCustom = Boolean(lunchStart || lunchEnd);
  const [lunchOpen, setLunchOpen] = useState(lunchCustom);
  useEffect(() => {
    if (lunchCustom) setLunchOpen(true);
  }, [lunchCustom]);

  const timingCheck = validateDayTiming({
    numDays,
    startTime,
    endTime,
    lunchStart: lunchOpen ? lunchStart : "",
    lunchEnd: lunchOpen ? lunchEnd : "",
    breaks,
  });

  const endErr =
    timeToMinutes(startTime) != null &&
    timeToMinutes(endTime) != null &&
    timeToMinutes(endTime) <= timeToMinutes(startTime)
      ? "Must be after start"
      : "";

  const lunchPairErr = (which) => {
    if (!lunchOpen) return "";
    const a = timeToMinutes(lunchStart);
    const b = timeToMinutes(lunchEnd);
    const dayS = timeToMinutes(startTime);
    const dayE = timeToMinutes(endTime);
    if (which === "start") {
      if (lunchEnd && !lunchStart) return "Pick a start";
      if (a != null && dayS != null && dayE != null && (a < dayS || a >= dayE)) return "Must be during class";
      return "";
    }
    if (lunchStart && !lunchEnd) return "Pick an end";
    if (a != null && b != null && b <= a) return "Must be after start";
    if (b != null && dayS != null && dayE != null && (b > dayE || b <= dayS)) return "Must be during class";
    return "";
  };

  const breakErr = (b, field) => {
    const empty = !String(b?.start || "").trim() && !String(b?.end || "").trim();
    if (empty) return "";
    const a = timeToMinutes(b?.start);
    const e = timeToMinutes(b?.end);
    const dayS = timeToMinutes(startTime);
    const dayE = timeToMinutes(endTime);
    if (field === "start") {
      if (b?.end && !b?.start) return "Pick a start";
      if (a != null && dayS != null && dayE != null && (a < dayS || a >= dayE)) return "Must be during class";
      return "";
    }
    if (b?.start && !b?.end) return "Pick an end";
    if (a != null && e != null && e <= a) return "Must be after start";
    if (e != null && dayS != null && dayE != null && (e > dayE || e <= dayS)) return "Must be during class";
    return "";
  };

  const chip = (active, disabled) => ({
    border: `1px solid ${active ? C.primary : C.border}`,
    background: active ? C.primarySoft : "#fff",
    color: disabled ? C.faint : active ? C.primaryDark : C.text,
    fontSize: 12,
    fontWeight: 650,
    borderRadius: 999,
    padding: "6px 11px",
    cursor: disabled ? "not-allowed" : "pointer",
  });

  const lunchDur = minutesBetween(lunchStart, lunchEnd);
  const applyLunchDur = (mins) => {
    const start = lunchStart || "12:30";
    if (!lunchStart) onLunchStartChange?.(start);
    onLunchEndChange?.(addMinutesToTime(start, mins));
  };

  const applyBreakDur = (idx, mins) => {
    const row = (breaks || [])[idx] || {};
    const start = row.start || "10:45";
    onBreaksChange?.(
      (breaks || []).map((r, i) =>
        i === idx ? { ...r, start, end: addMinutesToTime(start, mins) } : r
      )
    );
  };

  const daySummary = [
    formatTime12(startTime) && formatTime12(endTime)
      ? `${formatTime12(startTime)} – ${formatTime12(endTime)}${durationLabel ? ` · ${durationLabel}` : ""}`
      : "",
    lunchOpen && lunchStart && lunchEnd
      ? `Lunch ${formatTime12(lunchStart)}–${formatTime12(lunchEnd)}`
      : lunchOpen
        ? "Lunch (set From and To)"
        : "Lunch automatic",
    (breaks || []).filter((b) => b.start && b.end).length
      ? (breaks || [])
          .filter((b) => b.start && b.end)
          .map((b) => `${formatTime12(b.start)}–${formatTime12(b.end)}`)
          .join(", ")
      : "Breaks automatic",
  ].filter(Boolean).join(" · ");

  const updateBreak = (idx, field, value) => {
    onBreaksChange?.((breaks || []).map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  const linkBtn = (disabled) => ({
    border: "none",
    background: "transparent",
    color: disabled ? C.faint : C.primary,
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    padding: "4px 0",
  });

  const mainGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    alignItems: "start",
  };

  const pairGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    alignItems: "start",
  };

  const body = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {daySummary && (
        <div style={{
          fontSize: 12.5, color: C.muted, fontWeight: 600, lineHeight: 1.45,
          padding: "10px 12px", borderRadius: 10, background: C.surfaceAlt, border: `1px solid ${C.border}`,
        }}>
          {daySummary}
        </div>
      )}
      <div style={mainGrid}>
        <FancyInput
          tone="blue"
          icon="ti-calendar-event"
          label="Days"
          type="number"
          min={1}
          max={365}
          value={numDays}
          onChange={(e) => onDaysChange(Number(e.target.value))}
        />
        <NativeTimeField
          tone="blue"
          icon="ti-sunrise"
          label="From"
          value={startTime}
          onChange={(e) => onStartChange(e.target.value)}
        />
        <NativeTimeField
          tone="blue"
          icon="ti-sunset"
          label="To"
          value={endTime}
          onChange={(e) => onEndChange(e.target.value)}
          error={endErr}
          hint={!endErr && durationLabel ? durationLabel : undefined}
        />
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
        <div style={{ fontSize: 12.5, fontWeight: 750, color: C.text, marginBottom: 8 }}>Lunch</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
          Set From and To yourself. Changing one does not overwrite the other.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: lunchOpen ? 12 : 0 }}>
          {[
            { id: "auto", label: "Automatic" },
            { id: "set", label: "Set times" },
          ].map((p) => (
            <button
              key={p.id}
              type="button"
              style={chip((p.id === "auto" && !lunchOpen) || (p.id === "set" && lunchOpen), false)}
              onClick={() => {
                if (p.id === "auto") {
                  onLunchStartChange?.("");
                  onLunchEndChange?.("");
                  setLunchOpen(false);
                  return;
                }
                setLunchOpen(true);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        {lunchOpen && (
          <div>
            <div style={pairGrid}>
              <NativeTimeField
                tone="blue"
                icon="ti-tools-kitchen-2"
                label="From"
                allowEmpty
                value={lunchStart || ""}
                onChange={(e) => onLunchStartChange?.(e.target.value)}
                error={lunchPairErr("start")}
              />
              <NativeTimeField
                tone="blue"
                icon="ti-clock"
                label="To"
                allowEmpty
                value={lunchEnd || ""}
                onChange={(e) => onLunchEndChange?.(e.target.value)}
                error={lunchPairErr("end")}
              />
            </div>
            <div style={{ fontSize: 12, fontWeight: 650, color: C.muted, margin: "10px 0 8px" }}>
              Optional length (sets To only)
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[30, 45, 60].map((mins) => (
                <button
                  key={mins}
                  type="button"
                  style={chip(lunchDur === mins, false)}
                  onClick={() => applyLunchDur(mins)}
                >
                  {mins} min
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 750, color: C.text }}>Breaks</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {(breaks || []).length
                ? `${(breaks || []).length} of ${MAX_BREAKS} — set From and To`
                : "Optional. Leave empty and we place one short break."}
            </div>
          </div>
          <button
            type="button"
            style={linkBtn((breaks || []).length >= MAX_BREAKS)}
            disabled={(breaks || []).length >= MAX_BREAKS}
            onClick={() => {
              if ((breaks || []).length >= MAX_BREAKS) return;
              onBreaksChange?.([...(breaks || []), { start: "", end: "" }]);
            }}
          >
            Add break
          </button>
        </div>
        {(breaks || []).length === 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {[
              { label: "Tea 10:45–11:00", start: "10:45", end: "11:00" },
              { label: "Snack 3:30–3:45", start: "15:30", end: "15:45" },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                style={chip(false, false)}
                onClick={() => onBreaksChange?.([...(breaks || []), { start: p.start, end: p.end }])}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {(breaks || []).length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {(breaks || []).map((b, idx) => {
              const dur = minutesBetween(b.start, b.end);
              return (
                <div
                  key={b.id || idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
                    gap: 10,
                    alignItems: "start",
                  }}
                >
                  <NativeTimeField
                    tone="blue"
                    label={`Break ${idx + 1} from`}
                    allowEmpty
                    value={b.start || ""}
                    onChange={(e) => updateBreak(idx, "start", e.target.value)}
                    error={breakErr(b, "start")}
                  />
                  <NativeTimeField
                    tone="blue"
                    label="To"
                    allowEmpty
                    value={b.end || ""}
                    onChange={(e) => updateBreak(idx, "end", e.target.value)}
                    error={breakErr(b, "end")}
                  />
                  <button
                    type="button"
                    title="Remove"
                    onClick={() => onBreaksChange?.((breaks || []).filter((_, i) => i !== idx))}
                    style={{
                      marginTop: 26,
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      border: `1px solid ${C.border}`,
                      background: "#fff",
                      color: C.muted,
                      cursor: "pointer",
                    }}
                  >
                    <i className="ti ti-x" style={{ fontSize: 15 }} />
                  </button>
                  <div style={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 650, color: C.muted }}>Optional length (sets To only)</span>
                    {[10, 15, 20, 30].map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        style={chip(dur === mins, false)}
                        onClick={() => applyBreakDur(idx, mins)}
                      >
                        {mins} min
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!timingCheck.ok && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${C.error}44`,
            background: C.errorBg,
            padding: "10px 12px",
            fontSize: 12,
            color: C.error,
            fontWeight: 650,
            lineHeight: 1.45,
          }}
        >
          {timingCheck.errors.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) return body;
  return <Card>{body}</Card>;
}

// ── 3P Process Info Banner ────────────────────────────────────────────────────
function ThreePBanner() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: "#F8FAFC",
      border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px",
      marginBottom: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 22 }}>🎯</div>
          <div>
              <p style={{ fontWeight: 700, fontSize: 14, color: C.text }}>From problem to working product</p>
            <p style={{ fontSize: 12, color: C.muted }}>
              Understand the user → plan the solution → learn what you need → build, test, and demo it
            </p>
          </div>
        </div>
        <button onClick={() => setOpen(o => !o)} style={{
          background: "none", border: "none", cursor: "pointer", color: C.primary, fontSize: 13, fontWeight: 600,
        }}>
          {open ? "Hide ▲" : "How it works ▼"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 10 }}>
          {[
            { icon: "🧘", title: "Get ready", desc: "Review today's goal and what you already know about the problem." },
            { icon: "🔍", title: "Understand the need", desc: "Use the playbook step to learn about users and define what needs to change." },
            { icon: "📖", title: "Learn one useful concept", desc: "Study one coding or app-design idea that helps with today's product task." },
            { icon: "⚡", title: "Build a small piece", desc: "Apply what you learned to one feature or product decision." },
            { icon: "🔗", title: "Connect the work", desc: "Show how today's learning and playbook activity improve the same product." },
            { icon: "📊", title: "Check progress", desc: "Save the work, test what you built, and note what is next or blocked." },
          ].map((item, i) => (
            <div key={i} style={{
              background: C.surface, borderRadius: 10, padding: "12px 14px",
              border: `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{item.icon}</div>
              <p style={{ fontWeight: 700, fontSize: 12, color: C.text, marginBottom: 4 }}>{item.title}</p>
              <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── InputParamCard ─────────────────────────────────────────────────────────────
const PARAM_FIELD_META = {
  problem: { tone: "blue", icon: "ti-file-text" },
  syllabus: { tone: "teal", icon: "ti-book" },
  questions: { tone: "amber", icon: "ti-help" },
  instructions: { tone: "slate", icon: "ti-list-details" },
  dsaSyllabus: { tone: "coral", icon: "ti-binary-tree" },
  systemDesign: { tone: "green", icon: "ti-topology-star" },
  assessment: { tone: "ink", icon: "ti-flag" },
  capability: { tone: "navy", icon: "ti-user-check" },
  interest: { tone: "coral", icon: "ti-heart" },
  domain: { tone: "teal", icon: "ti-world" },
};

function InputParamCard({ param, textInputs, setTextInputs, files, setFiles, nested = false, forceOpen = false, onProblemVerified, numDays = 5 }) {
  const filled = !!(textInputs[param.key] || (files[param.key] || []).length > 0);
  const [open, setOpen] = useState(filled || param.key === "problem" || forceOpen);
  const showBody = forceOpen || !nested || open;
  const meta = PARAM_FIELD_META[param.key] || { tone: "blue", icon: "ti-forms" };
  const body = (
    <>
      {param.hint ? (
        <p style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{param.hint}</p>
      ) : null}
      <FancyTextarea
          tone={meta.tone}
          value={textInputs[param.key] || ""}
          onChange={(e) => setTextInputs((p) => ({ ...p, [param.key]: e.target.value }))}
          placeholder={
            param.key === "problem"
              ? "Write the problem in plain words, or upload a file…"
              : param.key === "capability"
                ? "Optional notes — or pick from the dropdown below (Communication, Aptitude…)"
                : param.key === "interest"
                  ? "Optional notes — or pick from the dropdown below (Logical thinking…)"
                  : param.key === "domain"
                    ? "Optional notes — or pick from the dropdown below (Web, FinTech…)"
                    : param.label
                      ? `Type your ${param.label.toLowerCase()} here, or upload files below…`
                      : "Type here, or upload files below…"
          }
          rows={param.key === "problem" ? 16 : (["capability", "interest", "domain"].includes(param.key) ? 2 : 5)}
          style={{
            minHeight: param.key === "problem" ? 280 : (["capability", "interest", "domain"].includes(param.key) ? 64 : 110),
          }}
        />
      <UploadZone paramKey={param.key} files={files} setFiles={setFiles} />
      {param.key === "interest" && (
        <>
          <ProfileSuggestionChips
            kind="interest"
            value={textInputs.interest || ""}
            onChange={(v) => setTextInputs((p) => applyFieldWithRatings(p, "interest", v))}
          />
          <SkillRatingsEditor
            kind="interest"
            skills={textInputs.interest || ""}
            ratings={normalizeItemRatings(textInputs).interest}
            onChange={(r) => setTextInputs((p) => applyKindRatings(p, "interest", r))}
          />
        </>
      )}
      {param.key === "capability" && (
        <>
          <ProfileSuggestionChips
            kind="capability"
            value={textInputs.capability || ""}
            onChange={(v) => setTextInputs((p) => applyFieldWithRatings(p, "capability", v))}
          />
          <SkillRatingsEditor
            kind="capability"
            skills={textInputs.capability || ""}
            ratings={normalizeItemRatings(textInputs).capability}
            onChange={(r) => setTextInputs((p) => applyKindRatings(p, "capability", r))}
          />
        </>
      )}
      {param.key === "domain" && (
        <>
          <ProfileSuggestionChips
            kind="domain"
            value={textInputs.domain || ""}
            onChange={(v) => setTextInputs((p) => applyFieldWithRatings(p, "domain", v))}
          />
          <SkillRatingsEditor
            kind="domain"
            skills={textInputs.domain || ""}
            ratings={normalizeItemRatings(textInputs).domain}
            onChange={(r) => setTextInputs((p) => applyKindRatings(p, "domain", r))}
          />
        </>
      )}
      {param.key === "problem" && (
        <ProblemFrameworkPanel
          problemText={textInputs.problem || ""}
          files={files}
          setTextInputs={setTextInputs}
          numDays={numDays}
          onApplyStatement={(statement) =>
            setTextInputs((p) => ({ ...p, problem: statement }))
          }
          onVerified={onProblemVerified}
        />
      )}
    </>
  );

  if (forceOpen) {
    // Standalone field body inside FormSection — keep clear spacing, no collapsed chrome
    return (
      <div style={{ position: "relative", zIndex: 1, minWidth: 0 }}>
        {body}
      </div>
    );
  }

  if (nested) {
    return (
      <div style={{
        border: `1.5px solid ${open ? param.color + "55" : C.border}`,
        borderRadius: 10, overflow: "hidden", background: C.surface,
      }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 10,
            padding: "10px 12px", border: "none", cursor: "pointer", textAlign: "left",
            background: open ? param.color + "12" : "transparent",
          }}
        >
          <i className={`ti ${param.icon}`} style={{ fontSize: 18, color: filled ? param.color : C.muted }} />
          <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: C.text }}>{param.label}</span>
          {filled && <Badge bg={C.successBg} color={C.success}>✓</Badge>}
          <span style={{ color: C.muted, fontWeight: 700 }}>{open ? "▾" : "▸"}</span>
        </button>
        {showBody && <div style={{ padding: "0 12px 12px" }}>{body}</div>}
      </div>
    );
  }

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: filled ? param.color + "18" : C.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: `1px solid ${filled ? param.color + "40" : C.border}`,
        }}>
          <i className={`ti ${param.icon}`} style={{ fontSize: 20, color: filled ? param.color : C.muted }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{param.label}</p>
            {filled && <Badge bg={C.successBg} color={C.success}>✓ Filled</Badge>}
          </div>
          {body}
        </div>
      </div>
    </Card>
  );
}

// ── PlannerPage ───────────────────────────────────────────────────────────────
export default function PlannerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useAppNotify();
  // Auth is handled by App Protected layout; keep local cache in sync
  const [user, setUser]             = useState(() => {
    const fromAp = lsGet(LS.user, null);
    if (fromAp) return fromAp;
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [cloudHydrated, setCloudHydrated] = useState(false);

  const wizardPageIds = buildWizardPages().map((p) => p.id);
  const readPlannerRoute = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab") === "plans" ? "plans" : "inputs";
    let page = params.get("page") || lsGet("ap_inputPage", "basics");
    if (!wizardPageIds.includes(page)) page = "basics";
    return { tab, page };
  };
  const initialRoute = readPlannerRoute();

  const [step, setStep]             = useState(() =>
    initialRoute.tab === "plans" ? 1 : (Number(lsGet(LS.step, 0)) || 0)
  );

  // DSA + DT is the only planner mode
  const plannerMode = "dsa";
  const [enabledModules, setEnabledModules] = useState(() =>
    mergeEnabledModules(lsGet("ap_enabledModules", null))
  );
  const [inputPage, setInputPage] = useState(() => {
    try {
      const raw = localStorage.getItem("ap_inputPage");
      const legacy = ["setup", "team", "timing", "calendar"];
      if (!localStorage.getItem("ap_wizardV4") || (raw && legacy.includes(raw))) {
        localStorage.setItem("ap_wizardV4", "1");
        localStorage.setItem("ap_inputPage", "basics");
        return initialRoute.page === "basics" ? "basics" : initialRoute.page;
      }
    } catch (_) { /* ignore */ }
    return initialRoute.page || lsGet("ap_inputPage", "basics");
  });
  const [problemVerified, setProblemVerified] = useState(() => lsGet("ap_problemVerified", false));

  const syncPlannerUrl = useCallback((nextStep, nextPage) => {
    const tab = nextStep === 1 ? "plans" : "inputs";
    const page = nextPage || inputPage || "basics";
    const qs = tab === "plans"
      ? "?tab=plans"
      : `?tab=inputs&page=${encodeURIComponent(page)}`;
    const next = `/planner${qs}`;
    if (`${location.pathname}${location.search}` !== next) {
      navigate(next, { replace: true });
    }
  }, [inputPage, location.pathname, location.search, navigate]);

  // Sidebar / deep-link → local view state (only when URL has query params)
  useEffect(() => {
    if (!location.search) return;
    const { tab, page } = readPlannerRoute();
    if (tab === "plans") {
      if (step !== 1) setStep(1);
      return;
    }
    if (step !== 0) setStep(0);
    if (page && page !== inputPage) setInputPage(page);
  }, [location.search]);

  const goToView = useCallback((nextStep) => {
    setStep(nextStep);
    syncPlannerUrl(nextStep, inputPage);
  }, [inputPage, syncPlannerUrl]);

  const goToInputPage = useCallback((pageId) => {
    setInputPage(pageId);
    setStep(0);
    syncPlannerUrl(0, pageId);
  }, [syncPlannerUrl]);

  // All text inputs (merged across both modes)
  const [textInputs, setTextInputs] = useState(() => {
    const saved = lsGet(LS.textInputs, {
      problem: "", syllabus: "", questions: "", instructions: "",
      dsaSyllabus: "", systemDesign: "", assessment: "",
      capability: "", difficulties: "", interest: "", dislikes: "", domain: "",
      studyMedium: "auto",
    });
    // Ensure domain / persona fields exist even for older localStorage snapshots
    const merged = {
      domain: "",
      difficulties: "",
      dislikes: "",
      studyMedium: "auto",
      manualPrompt: "",
      skillRatings: {},
      itemRatings: emptyItemRatings(),
      ...saved,
    };
    const ir = normalizeItemRatings(merged);
    return { ...merged, itemRatings: ir, skillRatings: ir.capability };
  });
  const [files, setFiles] = useState({
    problem: [], syllabus: [], questions: [], instructions: [],
    dsaSyllabus: [], systemDesign: [], assessment: [],
    capability: [], interest: [], domain: [], assessmentBank: [],
    manualPrompt: [],
  });
  const [cloudUploads, setCloudUploads] = useState({});

  // Assessment Content banks (Path Builder input)
  const [assessmentConfig, setAssessmentConfig] = useState(() =>
    lsGet("ap_assessmentConfig", defaultAssessmentConfig)
  );
  const [slotOverrides, setSlotOverrides] = useState(() =>
    splitStoredOverrides(lsGet(SLOT_OVERRIDE_KEY, {}))
  );
  const [dayActivityGroups, setDayActivityGroups] = useState(() => lsGet("ap_dayActivityGroups", []));
  const [systemDesignLevel, setSystemDesignLevel] = useState(() => lsGet("ap_systemDesignLevel", "beginner"));
  const [dsaLevel, setDsaLevel] = useState(() => lsGet("ap_dsaLevel", "beginner"));

  // Team / student profiles — array so more than one real team member can be
  // added manually (each with their own skill level), instead of just one.
  const [teamMembers, setTeamMembers] = useState(() => {
    const saved = lsGet("ap_teamMembers", null);
    const blankMember = {
      name: "", rollNumber: "", email: "", leetcodeUsername: "", targetRole: "", targetCompany: "",
      college: "SNS College of Technology",
      collegeYear: 2, semester: 3, schoolGrade: 10, department: "CSE",
      skillLevel: 3, capability: "", difficulties: "", interest: "", dislikes: "", domain: "", assessment: "",
      studyMedium: "auto",
      teamName: "",
      problemStatement: "",
      subjectPreferences: [],
      skillRatings: {},
      itemRatings: emptyItemRatings(),
    };
    if (Array.isArray(saved) && saved.length) {
      return saved.map((m) => {
        const row = { ...blankMember, ...m };
        const ir = normalizeItemRatings(row);
        return { ...row, itemRatings: ir, skillRatings: ir.capability };
      });
    }
    return [{ ...blankMember }];
  });

  // ── Team DB state ────────────────────────────────────────────────────────────
  const [teamName, setTeamName]       = useState(() => lsGet("ap_teamName", "Team A"));
  const [teamDbStatus, setTeamDbStatus] = useState({ saved:false, count:0, names:"" });
  const [teamSchedule, setTeamSchedule] = useState(null);       // loaded from DB
  const [teamActiveMember, setTeamActiveMember] = useState(null); // member id string
  const [teamFilterWeek, setTeamFilterWeek] = useState("all");
  const [teamGenerating, setTeamGenerating] = useState(false);
  // Selected synced member id (preferred) — falls back to first member after sync
  const [teamViewMemberId, setTeamViewMemberId] = useState(null);
  const [weeklyViewMode, setWeeklyViewMode] = useState("team"); // team | individual
  // linked problem statement id (resolved after generate or from saved PS)
  const [linkedPsId, setLinkedPsId]   = useState(() => lsGet("ap_linkedPsId", ""));
  const [psOptions, setPsOptions]     = useState([]); // all problem statements, for explicit linking
  // How many days the TEAM schedule covers — independent of the linked
  // problem statement's own (possibly much longer) duration.
  const [teamDays, setTeamDays]       = useState(() => lsGet("ap_teamDays", 10));
  useEffect(() => { lsSet("ap_teamDays", teamDays); }, [teamDays]);

  // Daily timing
  const [numDays, setNumDays]     = useState(() => lsGet(LS.numDays, 30));
  const [dailyStart, setDailyStart] = useState(() => lsGet(LS.dailyStart, "09:00"));
  const [dailyEnd, setDailyEnd]   = useState(() => lsGet(LS.dailyEnd, "18:00"));
  const [lunchStart, setLunchStart] = useState(() => lsGet(LS.lunchStart, ""));
  const [lunchEnd, setLunchEnd] = useState(() => lsGet(LS.lunchEnd, ""));
  const [breaks, setBreaks] = useState(() => {
    const saved = lsGet(LS.breaks, null);
    if (Array.isArray(saved)) return saved;
    // Migrate legacy single break fields
    try {
      const bs = JSON.parse(localStorage.getItem("ap_breakStart") || "null");
      const be = JSON.parse(localStorage.getItem("ap_breakEnd") || "null");
      if (bs || be) return [{ start: bs || "", end: be || "" }];
    } catch (_) {}
    return [];
  });

  // Active DT stage (used in DSA mode)
  const [activeDTStage, setActiveDTStage] = useState(() => lsGet(LS.dtActivated, "empathize"));
  const [productStagePlan, setProductStagePlan] = useState(() =>
    parseProductStagePlan(lsGet(LS.productStagePlan, []))
  );

  const [plans, setPlans]           = useState(() => lsGet(LS.plans, {}));
  const plansRef = useRef(plans);
  plansRef.current = plans;
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [lastGenInputs, setLastGenInputs] = useState(() => lsGet(LS.lastGenInputs, {}));
  const [loading, setLoading]       = useState({});
  const [activeTab, setActiveTab]   = useState(() => {
    const saved = lsGet(LS.activeTab, "daily");
    // Removed tabs fall back to daily
    if (saved === "team" || saved === "estimation") return "daily";
    if (saved === "weekly" || saved === "homework") return saved;
    return "daily";
  });
  const [dailyJump, setDailyJump] = useState(null); // { week, day, token }
  const [generating, setGenerating] = useState(false);
  const [rosterMode, setRosterMode] = useState(() => lsGet("ap_rosterMode", "team")); // team | individuals
  const [batchIndividualPlans, setBatchIndividualPlans] = useState([]); // [{ name, member, plans }]
  const [batchActiveName, setBatchActiveName] = useState("");
  const [batchTeamPlans, setBatchTeamPlans] = useState([]); // [{ key, teamName, problemStatement, members, plans, teamSchedule, linkedPsId }]
  const [activeTeamKey, setActiveTeamKey] = useState("");
  const [teamProblemVerified, setTeamProblemVerified] = useState({});
  const [batchProgress, setBatchProgress] = useState(null); // { index, total, name }
  const [versionPreview, setVersionPreview] = useState(() => {
    try {
      const raw = localStorage.getItem("ap_version_preview");
      const v = raw ? JSON.parse(raw) : null;
      if (v && (v.memberName || v.label)) return v;
    } catch (_) {}
    return null;
  });
  const [repoMode, setRepoMode] = useState("generate_new"); // existing | generate_new | existing_regen | manual
  const [repoMatch, setRepoMatch] = useState(null);
  const [repoEntryId, setRepoEntryId] = useState("");
  const [repoRegenParts, setRepoRegenParts] = useState([]);
  const [repoAdaptNote, setRepoAdaptNote] = useState("");

  // When switching modes that need an explicit pick, clear stale selection
  useEffect(() => {
    if (repoMode === "manual") setRepoEntryId("");
    if (repoMode === "generate_new") {
      setRepoEntryId("");
      setRepoRegenParts([]);
      setRepoAdaptNote("");
    }
  }, [repoMode]);

  // Lookup common schedule repository when Review page is open
  useEffect(() => {
    if (inputPage !== "generate") return undefined;
    if (!localStorage.getItem("token")) {
      setRepoMatch(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const inputs = buildFinalInputs();
        const res = await lookupScheduleRepo(inputs);
        if (cancelled) return;
        if (res?.success) {
          setRepoMatch(res);
          if (repoMode === "manual") return;
          const recId = res.recommendation?.id || res.match?.id || "";
          setRepoEntryId((prev) => {
            if (prev) return prev;
            return recId || "";
          });
        } else {
          setRepoMatch(null);
        }
      } catch {
        if (!cancelled) setRepoMatch(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    inputPage,
    repoMode,
    textInputs.problem,
    numDays,
    enabledModules,
    teamMembers?.[0]?.college,
    teamMembers?.[0]?.department,
    teamMembers?.[0]?.collegeYear,
    teamMembers?.[0]?.semester,
    plannerMode,
  ]);
  // Calendar
  const [startDate, setStartDate]     = useState(() => lsGet(LS.startDate, ""));
  const [endDate, setEndDate]         = useState(() => lsGet(LS.endDate, ""));
  const [calendarData, setCalendarData] = useState(() => lsGet(LS.calendarData, null));
  const [calLoading, setCalLoading]   = useState(false);

  // Exams
  const [exams, setExams] = useState(() =>
    lsGet(LS.exams, [{ name: "IA1", examType: "IA1", startDate: "", endDate: "" }])
  );

  // ── Persist ──────────────────────────────────────────────────────────────────
  useEffect(() => { lsSet(LS.user, user); },               [user]);
  useEffect(() => { lsSet(LS.step, step); },               [step]);
  useEffect(() => { lsSet(LS.textInputs, textInputs); },   [textInputs]);
  useEffect(() => { lsSet(LS.startDate, startDate); },     [startDate]);
  useEffect(() => { lsSet(LS.endDate, endDate); },         [endDate]);
  useEffect(() => { lsSet(LS.calendarData, calendarData); }, [calendarData]);
  useEffect(() => { lsSet(LS.exams, exams); },             [exams]);
  useEffect(() => { lsSet(LS.plans, plans); },             [plans]);
  useEffect(() => { lsSet(LS.lastGenInputs, lastGenInputs); }, [lastGenInputs]);
  useEffect(() => { lsSet(LS.activeTab, activeTab); },     [activeTab]);
  useEffect(() => { lsSet(LS.plannerMode, "dsa"); }, []);
  useEffect(() => { lsSet("ap_enabledModules", enabledModules); }, [enabledModules]);
  useEffect(() => { lsSet("ap_inputPage", inputPage); }, [inputPage]);
  useEffect(() => { lsSet("ap_problemVerified", problemVerified); }, [problemVerified]);

  // If saved page id is stale, land on basics
  useEffect(() => {
    const pages = buildWizardPages();
    if (!pages.some((p) => p.id === inputPage)) {
      goToInputPage("basics");
    }
  }, [inputPage, goToInputPage]);

  // Keep URL in sync so sidebar sub-items highlight correctly
  useEffect(() => {
    if (!location.pathname.startsWith("/planner")) return;
    if (location.search) return;
    syncPlannerUrl(step, inputPage);
  }, [location.pathname, location.search, step, inputPage, syncPlannerUrl]);

  useEffect(() => { lsSet(LS.numDays, numDays); },         [numDays]);
  useEffect(() => { lsSet(LS.dailyStart, dailyStart); },   [dailyStart]);
  useEffect(() => { lsSet(LS.dailyEnd, dailyEnd); },       [dailyEnd]);
  useEffect(() => { lsSet(LS.lunchStart, lunchStart); },   [lunchStart]);
  useEffect(() => { lsSet(LS.lunchEnd, lunchEnd); },       [lunchEnd]);
  useEffect(() => { lsSet(LS.breaks, breaks); },           [breaks]);
  useEffect(() => { lsSet(LS.dtActivated, activeDTStage); }, [activeDTStage]);
  useEffect(() => { lsSet(LS.productStagePlan, productStagePlan); }, [productStagePlan]);
  useEffect(() => { lsSet("ap_teamMembers", teamMembers); }, [teamMembers]);
  useEffect(() => { lsSet("ap_teamName", teamName); },    [teamName]);
  useEffect(() => { lsSet("ap_linkedPsId", linkedPsId); }, [linkedPsId]);
  useEffect(() => { lsSet("ap_assessmentConfig", assessmentConfig); }, [assessmentConfig]);
  useEffect(() => { lsSet(SLOT_OVERRIDE_KEY, slotOverrides); }, [slotOverrides]);
  useEffect(() => { lsSet("ap_dayActivityGroups", dayActivityGroups); }, [dayActivityGroups]);
  useEffect(() => { lsSet("ap_systemDesignLevel", systemDesignLevel); }, [systemDesignLevel]);
  useEffect(() => { lsSet("ap_dsaLevel", dsaLevel); }, [dsaLevel]);
  useEffect(() => {
    const timing = { dailyStart, dailyEnd, lunchStart, lunchEnd, breaks };
    const key = makeTimingKey(timing);
    setSlotOverrides((prev) => {
      const bundle = splitStoredOverrides(prev);
      if (bundle.timingKey === key) return prev;
      return {
        ...bundle,
        timingKey: key,
        daily: syncDailySlotsToTiming(bundle.daily, timing),
      };
    });
  }, [dailyStart, dailyEnd, lunchStart, lunchEnd, breaks, slotOverrides?.timingKey]);

  // Auto-fill Subjects from R2023 campus syllabus when Subjects toggle is ON
  // and college / dept / year / sem change. Does not overwrite a manual upload.
  const [syllabusAutoKey, setSyllabusAutoKey] = useState(() => lsGet("ap_syllabusAutoKey", ""));
  const [syllabusAutoMeta, setSyllabusAutoMeta] = useState(null);
  useEffect(() => {
    const subjectsOn = enabledModules?.subjects !== false;
    if (!subjectsOn) {
      setTextInputs((prev) => {
        if (!prev.syllabus || (syllabusAutoKey && prev.syllabus.includes("SOURCE: R2023"))) {
          return { ...prev, syllabus: "" };
        }
        return prev;
      });
      setSyllabusAutoKey("");
      setSyllabusAutoMeta(null);
      return;
    }

    const primary = (teamMembers || []).find((m) => m?.name?.trim()) || (teamMembers || [])[0];
    if (!primary) return;
    const college = String(primary.college || "").trim();
    const department = String(primary.department || "").trim();
    const collegeYear = primary.collegeYear != null ? Number(primary.collegeYear) : null;
    const semester = primary.semester != null ? Number(primary.semester) : null;
    if (!college || !department || !semester) return;
    if (/academy/i.test(college) || /arts/i.test(college)) return;

    const key = `${college}|${department}|Y${collegeYear}|S${semester}`;
    const hasUpload = (files.syllabus || []).length > 0;
    if (hasUpload) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchCampusSyllabus({ college, department, collegeYear, semester });
        if (cancelled || !res?.success || !res.syllabusText) return;
        setTextInputs((prev) => {
          const cur = String(prev.syllabus || "").trim();
          const wasAuto =
            !cur ||
            cur.includes("SOURCE: R2023") ||
            (syllabusAutoKey && syllabusAutoKey !== key);
          if (!wasAuto && cur.length >= 40 && syllabusAutoKey && syllabusAutoKey === key) {
            return prev;
          }
          if (!wasAuto && cur.length >= 40 && !cur.includes("SOURCE: R2023") && !syllabusAutoKey) {
            // User typed their own syllabus — keep it
            return prev;
          }
          return { ...prev, syllabus: res.syllabusText };
        });
        setSyllabusAutoKey(key);
        lsSet("ap_syllabusAutoKey", key);
        setSyllabusAutoMeta(res.meta || null);
        // Sync preference rows onto primary member (preserve likes/dislikes)
        if (Array.isArray(res.subjects) && res.subjects.length) {
          setTeamMembers((prev) => {
            if (!prev?.length) return prev;
            return prev.map((m, idx) => {
              if (idx !== 0) return m;
              return {
                ...m,
                _subjectCatalogKey: key,
                subjectPreferences: mergeSubjectPreferences(
                  res.subjects,
                  m.subjectPreferences || []
                ),
              };
            });
          });
        }
      } catch (e) {
        console.warn("Campus syllabus auto-fill:", e?.response?.data?.error || e.message);
        setSyllabusAutoMeta(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    enabledModules?.subjects,
    teamMembers?.[0]?.college,
    teamMembers?.[0]?.department,
    teamMembers?.[0]?.collegeYear,
    teamMembers?.[0]?.semester,
    (files.syllabus || []).length,
  ]);

  // Keep subject rows in sync with pasted syllabus without wiping include / focus / skip.
  useEffect(() => {
    if (enabledModules?.subjects === false) return;
    const catalog = parseSubjectsFromSyllabusText(textInputs.syllabus);
    if (!catalog.length) return;
    setTeamMembers((prev) => {
      if (!prev?.length) return prev;
      return prev.map((m, idx) => {
        if (idx !== 0) return m;
        const merged = mergeSubjectPreferences(catalog, m.subjectPreferences || []);
        const same =
          merged.length === (m.subjectPreferences || []).length &&
          merged.every((p, i) => subjectPrefKey(p) === subjectPrefKey((m.subjectPreferences || [])[i]));
        if (same && (m.subjectPreferences || []).length) return m;
        return { ...m, subjectPreferences: merged };
      });
    });
  }, [textInputs.syllabus, enabledModules?.subjects]);

  // ── Cloud DB: save wizard inputs (debounced) ─────────────────────────────────
  const buildPlannerInputPayload = () => {
    const uploadedFiles = { ...cloudUploads };
    Object.entries(files || {}).forEach(([key, arr]) => {
      if (!Array.isArray(arr) || !arr.length) return;
      if (Array.isArray(uploadedFiles[key]) && uploadedFiles[key].some((f) => f.url || f.key)) return;
      uploadedFiles[key] = arr.map((f) => ({
        filename: f.name || f.filename || "file",
        size: f.size || 0,
        type: f.type || "",
      }));
    });
    return {
      textInputs,
      lastGenInputs,
      calendar: { startDate, endDate, calendarData, exams },
      timing: {
        numDays,
        dailyStart,
        dailyEnd,
        lunchStart,
        lunchEnd,
        breaks,
        dtActivated: activeDTStage,
        productStagePlan,
      },
      assessmentConfig,
      team: { members: teamMembers, teamName, linkedPsId, teamDays },
      enabledModules,
      uploadedFiles,
      problemVerified: !!problemVerified,
    };
  };

  const persistInputsToDb = async (extra = {}) => {
    if (!localStorage.getItem("token") || !cloudHydrated) return;
    try {
      await savePlannerInputs({ ...buildPlannerInputPayload(), ...extra });
    } catch (e) {
      console.warn("Planner inputs DB save failed:", e?.message || e);
    }
  };

  const resetAllPlannerFields = useCallback(() => {
    const ir = emptyItemRatings();
    setTextInputs({ ...EMPTY_TEXT_INPUTS, itemRatings: ir, skillRatings: ir.capability });
    setFiles({ ...EMPTY_FILES });
    setCloudUploads({});
    setAssessmentConfig({ ...defaultAssessmentConfig });
    setTeamMembers([{ ...blankTeamMember() }]);
    setTeamName("Team A");
    setTeamDbStatus({ saved: false, count: 0, names: "" });
    setTeamSchedule(null);
    setTeamActiveMember(null);
    setTeamFilterWeek("all");
    setTeamViewMemberId(null);
    setWeeklyViewMode("team");
    setLinkedPsId("");
    setPsOptions([]);
    setTeamDays(10);
    setNumDays(30);
    setDailyStart("09:00");
    setDailyEnd("18:00");
    setLunchStart("");
    setLunchEnd("");
    setBreaks([]);
    setActiveDTStage("empathize");
    setProductStagePlan(parseProductStagePlan([]));
    setPlans({});
    setLastGenInputs({});
    setActiveTab("daily");
    setDailyJump(null);
    setRosterMode("team");
    setBatchIndividualPlans([]);
    setBatchActiveName("");
    setBatchTeamPlans([]);
    setActiveTeamKey("");
    setTeamProblemVerified({});
    setBatchProgress(null);
    setVersionPreview(null);
    setRepoMode("generate_new");
    setRepoMatch(null);
    setRepoEntryId("");
    setRepoRegenParts([]);
    setRepoAdaptNote("");
    setStartDate("");
    setEndDate("");
    setCalendarData(null);
    setExams([{ name: "IA1", examType: "IA1", startDate: "", endDate: "" }]);
    setEnabledModules(mergeEnabledModules());
    setProblemVerified(false);
    setInputPage("basics");
    setStep(0);
    setSyllabusAutoKey("");
    try {
      localStorage.removeItem("ap_version_preview");
    } catch (_) {
      /* ignore */
    }
  }, []);

  const applyCloudInputs = (inp) => {
    if (!inp || typeof inp !== "object") return;
    if (inp.textInputs && typeof inp.textInputs === "object") {
      const merged = { ...EMPTY_TEXT_INPUTS, ...inp.textInputs };
      const ir = normalizeItemRatings(merged);
      setTextInputs({ ...merged, itemRatings: ir, skillRatings: ir.capability });
    }
    if (inp.lastGenInputs && typeof inp.lastGenInputs === "object") {
      setLastGenInputs(inp.lastGenInputs);
    } else {
      setLastGenInputs({});
    }
    if (inp.calendar) {
      setStartDate(inp.calendar.startDate || "");
      setEndDate(inp.calendar.endDate || "");
      setCalendarData(inp.calendar.calendarData || null);
      setExams(
        Array.isArray(inp.calendar.exams) && inp.calendar.exams.length
          ? inp.calendar.exams
          : [{ name: "IA1", examType: "IA1", startDate: "", endDate: "" }]
      );
    } else {
      setStartDate("");
      setEndDate("");
      setCalendarData(null);
      setExams([{ name: "IA1", examType: "IA1", startDate: "", endDate: "" }]);
    }
    if (inp.timing) {
      if (inp.timing.numDays != null) setNumDays(inp.timing.numDays);
      if (inp.timing.dailyStart) setDailyStart(inp.timing.dailyStart);
      if (inp.timing.dailyEnd) setDailyEnd(inp.timing.dailyEnd);
      setLunchStart(inp.timing.lunchStart || "");
      setLunchEnd(inp.timing.lunchEnd || "");
      if (Array.isArray(inp.timing.breaks)) {
        setBreaks(inp.timing.breaks);
      } else if (inp.timing.breakStart || inp.timing.breakEnd) {
        setBreaks([{
          start: inp.timing.breakStart || "",
          end: inp.timing.breakEnd || "",
        }]);
      } else {
        setBreaks([]);
      }
      if (inp.timing.dtActivated) setActiveDTStage(inp.timing.dtActivated);
      setProductStagePlan(parseProductStagePlan(inp.timing.productStagePlan || []));
    }
    if (inp.assessmentConfig && typeof inp.assessmentConfig === "object") {
      setAssessmentConfig({ ...defaultAssessmentConfig, ...inp.assessmentConfig });
    } else {
      setAssessmentConfig({ ...defaultAssessmentConfig });
    }
    if (inp.team) {
      if (Array.isArray(inp.team.members) && inp.team.members.length) {
        setTeamMembers(
          inp.team.members.map((m) => {
            const row = { ...blankTeamMember(), ...m };
            const ir = normalizeItemRatings(row);
            return { ...row, itemRatings: ir, skillRatings: ir.capability };
          })
        );
      } else {
        setTeamMembers([{ ...blankTeamMember() }]);
      }
      setTeamName(inp.team.teamName || "Team A");
      setLinkedPsId(inp.team.linkedPsId || "");
      if (inp.team.teamDays != null) setTeamDays(inp.team.teamDays);
    } else {
      setTeamMembers([{ ...blankTeamMember() }]);
      setTeamName("Team A");
      setLinkedPsId("");
    }
    if (inp.enabledModules && typeof inp.enabledModules === "object") {
      setEnabledModules(mergeEnabledModules(inp.enabledModules));
    }
    setProblemVerified(typeof inp.problemVerified === "boolean" ? inp.problemVerified : false);
    if (inp.uploadedFiles && typeof inp.uploadedFiles === "object") {
      setCloudUploads(inp.uploadedFiles);
    }
  };

  // Debounce wizard input writes so typing doesn't spam Mongo
  useEffect(() => {
    if (!localStorage.getItem("token") || !user || !cloudHydrated) return undefined;
    const t = setTimeout(() => {
      persistInputsToDb();
    }, 1800);
    return () => clearTimeout(t);
  }, [
    cloudHydrated,
    user,
    textInputs,
    lastGenInputs,
    startDate,
    endDate,
    calendarData,
    exams,
    numDays,
    dailyStart,
    dailyEnd,
    lunchStart,
    lunchEnd,
    breaks,
    activeDTStage,
    assessmentConfig,
    teamMembers,
    teamName,
    linkedPsId,
    teamDays,
    enabledModules,
    problemVerified,
  ]);

  // ── Cloud DB: hydrate THIS account only (never keep another email's cache) ──
  useEffect(() => {
    if (!user || !localStorage.getItem("token")) return undefined;
    adoptWorkspaceUser(user);
    const freshAccount = consumeFreshWorkspace();
    if (freshAccount) resetAllPlannerFields();
    setCloudHydrated(false);
    let cancelled = false;
    (async () => {
      try {
        const [dailyRes, weeklyRes, homeworkRes, inputsRes] = await Promise.all([
          getDailyPlan().catch(() => null),
          getWeeklyPlan().catch(() => null),
          getHomeworkPlan().catch(() => null),
          getPlannerInputs().catch(() => null),
        ]);
        if (cancelled) return;

        const hasCloudPlan = Boolean(
          dailyRes?.plan?.rows?.length || weeklyRes?.plan?.rows?.length
        );
        const skipLeftoverInputs = freshAccount && !hasCloudPlan;

        if (skipLeftoverInputs) {
          setPlans({});
          lsSet(LS.plans, {});
        } else {
          const serverReachable = [dailyRes, weeklyRes, homeworkRes, inputsRes].some(Boolean);
          if (serverReachable) {
            const nextPlans = {};
            if (dailyRes?.plan?.rows?.length) nextPlans.daily = dailyRes.plan;
            if (weeklyRes?.plan?.rows?.length) nextPlans.weekly = weeklyRes.plan;
            if (nextPlans.daily?.rows?.length) {
              nextPlans.daily = { ...nextPlans.daily, rows: withHomeworkRows(nextPlans.daily.rows) };
            }
            if (homeworkHasBody(homeworkRes?.plan?.rows)) {
              nextPlans.homework = homeworkRes.plan;
            } else if (nextPlans.daily?.rows?.length) {
              const filled = homeworkPlanFromDaily(nextPlans.daily);
              if (filled) nextPlans.homework = filled;
            }
            setPlans(nextPlans);
            lsSet(LS.plans, nextPlans);
          }
          if (inputsRes?.inputs) applyCloudInputs(inputsRes.inputs);
        }
      } catch (e) {
        console.warn("DB hydrate failed:", e?.message || e);
      } finally {
        if (!cancelled) setCloudHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, resetAllPlannerFields]);

  // (team / assessment localStorage keys continued below)

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const showToast = (type, msg, title) => {
    const message = friendlyNotice(formatApiError(msg, ""), "");
    if (!message) return;
    notify({ type, title, message });
  };

  // Check if there's enough input to generate
  const hasDSAContent =
    !!(textInputs.dsaSyllabus || (files.dsaSyllabus || []).length > 0) ||
    !!(textInputs.systemDesign || (files.systemDesign || []).length > 0);

  const hasSubjectContent = !!(textInputs.syllabus || (files.syllabus || []).length > 0);
  const rosterTeams = groupRosterTeams(teamMembers);
  const isMultiTeam = rosterMode !== "individuals" && isMultiTeamRoster(teamMembers);
  const teamProblemFilled = (t) =>
    !!(String(t.problemStatement || "").trim() || textInputs.problem || (files.problem || []).length > 0);
  const hasProblem = isMultiTeam
    ? rosterTeams.filter((t) => t.members.length).every(teamProblemFilled)
    : !!(textInputs.problem || (files.problem || []).length > 0);
  const focusTeamKey = activeTeamKey || rosterTeams.find((t) => t.members.length)?.key || "";
  const focusTeam = rosterTeams.find((t) => t.key === focusTeamKey) || null;
  const problemVerifiedAll = isMultiTeam
    ? rosterTeams.filter((t) => t.members.length).every((t) => !!teamProblemVerified[t.key])
    : !!problemVerified;

  const applyProblemToTeam = (teamKey, statement) => {
    const text = String(statement || "");
    if (teamKey) {
      setTeamMembers((prev) =>
        prev.map((m) => {
          const key = String(m.teamName || "").trim() || "Unassigned";
          return key === teamKey ? { ...m, problemStatement: text } : m;
        })
      );
      setTeamProblemVerified((prev) => ({ ...prev, [teamKey]: false }));
    }
    setTextInputs((p) => ({ ...p, problem: text }));
    setProblemVerified(false);
  };

  const selectActiveTeam = (key) => {
    setActiveTeamKey(key);
    const t = rosterTeams.find((x) => x.key === key);
    if (t) setTextInputs((p) => ({ ...p, problem: t.problemStatement || "" }));
  };

  const markTeamVerified = (teamKey) => {
    if (!teamKey || !isMultiTeam) {
      setProblemVerified(true);
      return;
    }
    setTeamProblemVerified((prev) => ({ ...prev, [teamKey]: true }));
  };

  const hasAnyInput = plannerMode === "dsa"
    ? hasProblem
    : Object.entries(textInputs).some(([, v]) => v.trim()) ||
      Object.values(files).some((a) => a.length > 0);

  // Team UI switches as soon as a 2nd member slot exists (not only after names are typed).
  const isTeamMode = (teamMembers || []).length >= 2;
  const currentParams = (() => {
    if (plannerMode === "dsa") {
      const dsaParams = isTeamMode
        ? DSA_SD_PARAMS.filter((p) => p.key !== "assessment")
        : DSA_SD_PARAMS;
      const studentParams = isTeamMode ? [] : STUDENT_PARAMS;
      return [...SHARED_PARAMS, ...dsaParams, ...studentParams];
    }
    return [...SHARED_PARAMS, ...SUBJECT_PARAMS];
  })();

  // ── Logout ───────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    Object.values(LS).forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("ap_teamMembers");
    clearWorkspaceCache({ rememberOwner: true });
    setUser(null);
    setStep(0);
    setInputPage("basics");
    setTextInputs({ problem: "", syllabus: "", questions: "", instructions: "", dsaSyllabus: "", systemDesign: "", assessment: "", capability: "", interest: "", domain: "", manualPrompt: "" });
    setFiles({ problem: [], syllabus: [], questions: [], instructions: [], dsaSyllabus: [], systemDesign: [], assessment: [], capability: [], interest: [], domain: [], assessmentBank: [], manualPrompt: [] });
    setAssessmentConfig(defaultAssessmentConfig);
    setPlans({});
    setLastGenInputs({});
    setStartDate("");
    setEndDate("");
    setCalendarData(null);
    setExams([{ name: "IA1", examType: "IA1", startDate: "", endDate: "" }]);
    setActiveTab("daily");
  };

  // ── Exam management ──────────────────────────────────────────────────────────
  const iaCount    = exams.filter((e) => e.examType !== "AU").length;
  const hasAU      = exams.some((e) => e.examType === "AU");
  const canAddExam = iaCount < MAX_IA || (!hasAU && iaCount === MAX_IA);

  const addExam = () => {
    if (!canAddExam) return;
    if (iaCount < MAX_IA) {
      const nextNum = iaCount + 1;
      const examType = `IA${nextNum}`;
      setExams((p) => [...p, { name: examType, examType, startDate: "", endDate: "" }]);
    } else {
      setExams((p) => [...p, { name: "AU", examType: "AU", startDate: "", endDate: "" }]);
    }
  };

  const removeExam = (i) => setExams((p) => p.filter((_, j) => j !== i));

  const updateExam = (i, field, value) => {
    if ((field === "startDate" || field === "endDate") && value) {
      if (startDate && value < startDate) {
        showToast("warning", `Exam date cannot be before course start date (${toDD(startDate)})`);
        return;
      }
      if (endDate && value > endDate) {
        showToast("warning", `Exam date cannot be after course end date (${toDD(endDate)})`);
        return;
      }
    }
    setExams((p) => p.map((ex, j) => (j === i ? { ...ex, [field]: value } : ex)));
  };

  // ── Calendar ─────────────────────────────────────────────────────────────────
  const handleBuildCalendar = async () => {
    if (!startDate || !endDate) return showToast("error", "Pick both start and end dates");
    if (startDate > endDate) return showToast("error", "Start date must be before end date");
    setCalLoading(true);
    try {
      const validExams = exams
        .filter((ex) => ex.name && ex.startDate)
        .map((ex) => ({ name: ex.name, examType: ex.examType, startDate: ex.startDate, endDate: ex.endDate || ex.startDate }));
      const data = await getCalendar(startDate, endDate, validExams);
      setCalendarData(data);
      showToast("success", "Your calendar is ready.", "Calendar ready");
    } catch (e) {
      showToast("error", "We could not build the calendar. Please try again.", "Calendar");
    }
    setCalLoading(false);
  };

  const handleClearCalendar = () => {
    setStartDate("");
    setEndDate("");
    setCalendarData(null);
    setExams([{ name: "IA1", examType: "IA1", startDate: "", endDate: "" }]);
    showToast("success", "Calendar cleared.", "Calendar");
  };

  // ── Build generation inputs (enriched with mode context) ─────────────────────
  useEffect(() => { lsSet("ap_rosterMode", rosterMode); }, [rosterMode]);

  const MUST_APPLY_START = "HARD USER OVERRIDE — MUST APPLY";
  const MUST_APPLY_END = "---END MUST APPLY---";

  const stripMustApplyBlock = (text) => {
    let src = String(text || "");
    src = src.replace(
      new RegExp(`\\n?${MUST_APPLY_START}[\\s\\S]*?${MUST_APPLY_END}\\n?`, "g"),
      "\n"
    );
    src = src.replace(/\n?MUST APPLY \(verbatim\):[\s\S]*?(?=\n\n[A-Z]|$)/g, "\n");
    return src.replace(/\n{3,}/g, "\n\n").trim();
  };

  const applyMustApplyOverride = (inputs) => {
    const typed = String(inputs.manualPrompt || "").trim();
    const extracted = String(inputs.manualPromptExtracted || "").trim();
    delete inputs._forceApply;
    delete inputs._forceInstructions;
    delete inputs._forceFileNotes;
    delete inputs.manualPromptExtracted;
    inputs._dtInstruction = stripMustApplyBlock(inputs._dtInstruction);
    inputs.instructions = stripMustApplyBlock(inputs.instructions);

    if (!typed && !extracted) {
      delete inputs.manualPrompt;
      delete inputs._mustApplyText;
      return inputs;
    }

    const bits = [];
    if (typed) bits.push(`USER MUST-APPLY TEXT (verbatim — do not rewrite):\n${typed}`);
    if (extracted) bits.push(`USER MUST-APPLY FILES (extracted — do not invent extra rules):\n${extracted}`);
    const verbatim = [typed, extracted].filter(Boolean).join("\n\n");

    // Fields existing generate prompts already read
    inputs.manualPrompt = verbatim;
    inputs._mustApplyText = verbatim;
    inputs._forceApply = "true";
    if (typed) inputs._forceInstructions = typed;
    if (extracted) inputs._forceFileNotes = extracted;

    const extraInstructions = `MUST APPLY (verbatim):\n${verbatim}`;
    inputs.instructions = [String(inputs.instructions || "").trim(), extraInstructions]
      .filter(Boolean)
      .join("\n\n");

    const forceBlock = [
      `${MUST_APPLY_START} TO THE ENTIRE PLAN (daily, weekly, homework, every team).`,
      "These lines are the user's exact rules. Follow them literally in the generated plan.",
      "If they conflict with playbook, timing, slot, or repository rules, the user rules win.",
      "Do not paraphrase, drop, soften, or add extra rules that were not written here.",
      "",
      bits.join("\n\n"),
      "",
      MUST_APPLY_END,
    ].join("\n");

    const existing = String(inputs._dtInstruction || "").trim();
    inputs._dtInstruction = existing
      ? `${existing}\n\n${forceBlock}`
      : forceBlock;

    const adapt = String(inputs._repoAdaptNote || "").trim();
    if (!adapt.includes(verbatim.slice(0, 80))) {
      inputs._repoAdaptNote = [adapt, `MUST APPLY:\n${verbatim}`].filter(Boolean).join("\n\n").slice(0, 2000);
    }
    return inputs;
  };


  const buildFinalInputs = (opts = {}) => {
    const base = { ...textInputs };
    if (String(opts.problemOverride || "").trim()) {
      base.problem = String(opts.problemOverride).trim();
    }
    const rosterForInputs = (opts.teamRoster && opts.teamRoster.length)
      ? opts.teamRoster
      : (teamMembers || []);
    const on = (k) => mergeEnabledModules(enabledModules)[k] !== false;
    base._timeFormat = "12h";
    base._timezone = "IST";

    // Strip modules the user turned OFF
    if (!on("dsa")) base.dsaSyllabus = "";
    if (!on("systemDesign")) base.systemDesign = "";
    if (!on("subjects")) base.syllabus = "";
    if (!on("questions")) base.questions = "";
    if (!on("instructions")) base.instructions = "";
    if (!on("assessment") && rosterForInputs.filter((m) => m.name?.trim()).length < 2) {
      base.assessment = "";
    }
    if (!on("studentProfile")) {
      base.capability = "";
      base.interest = "";
      base.domain = "";
    }
    base._enabledModules = JSON.stringify(enabledModules);
    base._skipDsa = !on("dsa");
    base._skipSystemDesign = !on("systemDesign");
    base._skipSubjects = !on("subjects");
    base._skipQuestions = !on("questions");
    if (on("dtStage")) {
      base._activeDTStage = activeDTStage;
      let checkpoints = parseProductStagePlan(productStagePlan);
      if (!checkpoints.length) checkpoints = spreadProductStages(numDays, activeDTStage);
      base._productStagePlan = JSON.stringify(checkpoints);
      base._productStageBlock = formatProductStageBlock(checkpoints, numDays);
      base._dtPlaybookMap = JSON.stringify(dtPlaybookMapPayload(checkpoints, numDays, activeDTStage));
    }

    const productBuildInstruction = `
PRODUCT OUTCOME — ${numDays} working days:
- Solve the learner's stated problem by delivering a runnable MVP by the final requested day. Scale the feature scope to ${numDays} days; do not substitute research, lessons, wireframes, or a pitch for the product.
- Keep one product goal throughout. Name its target user, main user journey, and 2–4 checks that show the core flow works. Tie every day's learning and activity to that same goal.
- Deliver the working core flow, test it, fix the most important issue, and prepare short setup/run instructions and a demo before the final day.
- Explain technical terms briefly in everyday language. Avoid repeating the full problem or project description in every schedule cell; show only that day's step, task, and deliverable.
`.trim();

    // The product outcome applies to both planner modes; the DT stage map is
    // additional guidance when Design Thinking is enabled.
    base._dtInstruction = productBuildInstruction;

    // Add detailed Design Thinking context for the DSA + playbook mode.
    if (plannerMode === "dsa") {
      base._plannerMode    = "dsa_dt_playbook";
      base._activeDTStage  = on("dtStage") ? activeDTStage : "";
      base._numDays        = numDays;
      base._dailyStart     = dailyStart;
      base._dailyEnd       = dailyEnd;
      if (lunchStart) base._lunchStart = lunchStart;
      if (lunchEnd) base._lunchEnd = lunchEnd;
      {
        const filled = (breaks || []).filter((b) => b?.start && b?.end);
        if (filled.length) {
          base._breaks = JSON.stringify(filled.map((b) => ({ start: b.start, end: b.end })));
          base._breakStart = filled[0].start;
          base._breakEnd = filled[0].end;
        }
      }
      {
        const namedForMode = rosterForInputs.filter((m) => m.name?.trim());
        const teamOn = Boolean(on("team") && namedForMode.length >= 2);
        base._isTeam = teamOn;
        if (teamOn) {
          base._teamMembers = JSON.stringify(
            namedForMode.map((m) => ({
              ...m,
              targetCompany: resolveDreamCompany(m.targetCompany),
            }))
          );
        } else {
          base._teamMembers = "[]";
        }
      }
      base._dtInstruction  = `
${productBuildInstruction}

DESIGN THINKING PLAYBOOK:
Use the learner's problem statement as the product brief. Keep each assigned Playbook step connected to a product decision, feature, or test.

PLAN RULES:
${rosterForInputs.filter((m) => m.name?.trim()).length >= 2 && on("team")
  ? `- MODE = TEAM (${rosterForInputs.filter((m) => m.name?.trim()).length} members). Team check-in and named split work are allowed.
- THIS TEAM ONLY: ${opts.teamNameOverride || teamName || "Team"}. Use ONLY this team's problem statement. Do not mix another team's problem.
- Split APPLY work by member name. Same day, same DT Playbook Step for everyone.`
  : `- MODE = SOLO (one student). FORBIDDEN words/ideas: team, teammates, classmate, peer review, team check-in, "each person", board update for a group.
- Stand-Up must be a PERSONAL check-in only (DONE / TODAY / STUCK for that one student).
- Never invent a team. Never ask the solo student to present to a team.`}
- ONE learnTopic per day only. Do not dump many concepts.
- Problem text must be short, complete sentences (never cut mid-sentence like "…Traditional compliance meth…").
- Reference links must be full https URLs students can open. Ebook/page hints ONLY if they uploaded an ebook/notes file.
- Homework is follow-up work — do not require exact minute timings.
- Use clear everyday English a first-year student can understand.
- Explain any technical term briefly the first time it appears. Prefer a plain-language label followed by the technical term in parentheses.
- Keep the exact DT Playbook stage and step so learners can match the plan to their book. Explain what to do in simple language.
- Use the same daily schedule format throughout; do not refer to unnamed phases or assume the learner saw another plan.
- AI Agent Builder / Agent Workbench: students work in SNS Agent Workbench (n8n-style) on THEIR problem / project — design/build/test an agent that helps that problem. NOT OKRion. NOT LinkedIn. NOT certification.
- OKR / OKRion: career activities from the OKRion tool only (LinkedIn article, certification, skill badge). Never put OKRion tasks in the Agent Builder cell.
- Every technical cell starts with a 1-line Problem (short). End with Deliverable (what the student submits). Do not write "Done when".
- Learning: one topic + Why + Outcome + one real YouTube watch URL (youtube.com/watch?v=) on that topic + one real article URL. No geeksforgeeks /?s= search links. No fake URLs.
- Project Build should show today's one relevant playbook action and the product task. Do not repeat the full playbook, problem statement, or long project description in every schedule cell; keep the full context in the project brief and link back to it.
- Interests / Goals practice only (not Capability dump, not Peer Review).
- Learn BEFORE Project Build. Empathize days = understand + notes (not full product coding).
- Refresh games only 2–3 days per week.

${rosterForInputs.filter((m) => m.name?.trim()).length >= 2 && on("team")
  ? `TEAM PROFILE (${opts.teamNameOverride || teamName || "Team"}): ${JSON.stringify(rosterForInputs.filter((m) => m.name?.trim()))}`
  : "SOLO MODE — single student; no team roster"}
${on("timing")
  ? `DAILY TIMING: ${dailyStart} to ${dailyEnd} (${numDays} total days)` +
    `${lunchStart && lunchEnd ? ` · Lunch ${lunchStart}–${lunchEnd}` : " · Lunch auto"}` +
    `${(breaks || []).filter((b) => b?.start && b?.end).length
      ? ` · Breaks ${(breaks || []).filter((b) => b?.start && b?.end).map((b) => `${b.start}–${b.end}`).join(", ")}`
      : " · Breaks auto"}`
  : ""}
ACTIVE DT STAGE: ${on("dtStage") ? activeDTStage : "auto"}
      `.trim();
    } else {
      base._plannerMode = "subject_syllabus";
      base._numDays     = numDays;
      base._dailyStart  = dailyStart;
      base._dailyEnd    = dailyEnd;
      if (lunchStart) base._lunchStart = lunchStart;
      if (lunchEnd) base._lunchEnd = lunchEnd;
      {
        const filled = (breaks || []).filter((b) => b?.start && b?.end);
        if (filled.length) {
          base._breaks = JSON.stringify(filled.map((b) => ({ start: b.start, end: b.end })));
          base._breakStart = filled[0].start;
          base._breakEnd = filled[0].end;
        }
      }
    }

    const slotTiming = {
      dailyStart,
      dailyEnd,
      lunchStart,
      lunchEnd,
      breaks,
      startDate,
      endDate,
      numDays,
    };
    const slotBlock = slotOverrideInstruction(slotOverrides, slotTiming);
    if (slotBlock) {
      const daily = activeSlotOverrides(slotOverrides.daily, DAY_ACTIVITY_SLOTS);
      const homework = activeSlotOverrides(slotOverrides.homework, HOMEWORK_ACTIVITY_SLOTS);
      if (Object.keys(daily).length) base._slotOverrides = JSON.stringify(daily);
      const blueprint = visibleDailyBlueprint(slotOverrides.daily);
      if (blueprint.length) {
        base._useActivityLayout = "true";
        base._dayTemplate = JSON.stringify(
          blueprint.map((s, i) => ({
            order: i + 1,
            id: s.id,
            title: s.title,
            notes: s.notes || "",
            start: s.start,
            end: s.end,
            clock: s.clock,
          }))
        );
        base._activityBlueprint = JSON.stringify({
          daily: blueprint,
          dayStart: dailyStart,
          dayEnd: dailyEnd,
          lunchStart: lunchStart || "",
          lunchEnd: lunchEnd || "",
          breaks: (breaks || []).filter((b) => b?.start && b?.end),
          startDate: startDate || "",
          endDate: endDate || "",
          numDays,
          timeFormat: "12h",
          timezone: "IST",
        });
      }
      base._timeFormat = "12h";
      base._timezone = "IST";
      if (Object.keys(homework).length) {
        Object.keys(homework).forEach((id) => {
          homework[id] = { ...homework[id], start: "", end: "" };
        });
        base._homeworkOverrides = JSON.stringify(homework);
      }
      const marker = "DAILY SCHEDULE ACTIVITY CRUD";
      const markerHw = "TONIGHT'S HOMEWORK ACTIVITY CRUD";
      const existing = String(base._dtInstruction || "");
      if (!existing.includes(marker) && !existing.includes(markerHw)) {
        base._dtInstruction = `${slotBlock}\n\n${existing}`.trim();
      }
    }

    if (on("calendar")) {
      if (calendarData?.calendarSummaryText) base.calendarSummary = calendarData.calendarSummaryText;
      if (calendarData?.meta)               base.calendarMeta    = calendarData.meta;
      if (calendarData?.calMap)             base.calMap          = calendarData.calMap;
    }

    // Assessment Content config → schedule engines pick LeetCode / PM / cases per day
    // When Practice Banks is OFF, send every bank flag false (empty {} was wrongly merged to defaults).
    base._cohortPhase = "2";
    base._snsAgentWorkbench = "true";
    base._requireLearningLinks = "true";
    if (on("assessmentBanks")) {
      base._skipAssessmentBanks = false;
      base._assessmentConfig = JSON.stringify({
        ...defaultAssessmentConfig,
        ...assessmentConfig,
        banks: {
          ...defaultAssessmentConfig.banks,
          ...(assessmentConfig?.banks || {}),
          agentWorkbench: assessmentConfig?.banks?.agentWorkbench !== false,
        },
      });
    } else {
      base._skipAssessmentBanks = true;
      base._assessmentConfig = JSON.stringify({
        ...defaultAssessmentConfig,
        banks: {
          leetcode: false,
          pm: false,
          cases: false,
          company: false,
          iae: false,
          certifications: false,
          analyticsVidhya: false,
          agentWorkbench: false,
        },
      });
    }
    const dreamCompany = on("team")
      ? rosterForInputs
          .map((m) => resolveDreamCompany(m.targetCompany))
          .find((c) => c && String(c).trim())
      : null;
    if (dreamCompany) base.targetCompany = String(dreamCompany).trim();

    // Team / individuals: fold per-member capability/interest into LLM inputs
    const soloMember = opts.soloMember || null;
    const forceSolo = Boolean(opts.forceSolo || soloMember || rosterMode === "individuals");
    const named = forceSolo
      ? []
      : on("team")
        ? rosterForInputs.filter((m) => m.name?.trim())
        : [];
    const roster = soloMember
      ? [soloMember]
      : named.length >= 1
        ? named
        : rosterForInputs.slice(0, 1);
    const primary = roster[0] || {};

    // Study medium / Tamil YouTube preference (explicit toggle or Excel column)
    const mediumMember = soloMember || primary || {};
    const studyMedium = String(mediumMember.studyMedium || "auto").trim().toLowerCase() || "auto";
    base.studyMedium = studyMedium;
    if (studyMedium === "ta") {
      base.language = "ta";
      base.preferTamilVideos = true;
      base._preferTamilVideos = true;
    } else if (studyMedium && studyMedium !== "auto") {
      base.language = studyMedium;
      base.preferTamilVideos = false;
    }

    // When generating one individual from a batch sheet, use their persona fields
    if (soloMember) {
      base.capability = String(soloMember.capability || "").trim();
      base.interest = String(soloMember.interest || "").trim();
      base.domain = String(soloMember.domain || "").trim();
      base.assessment = String(soloMember.assessment || textInputs.assessment || "").trim();
      if (soloMember.difficulties) base.difficulties = String(soloMember.difficulties).trim();
      if (soloMember.dislikes) base.dislikes = String(soloMember.dislikes).trim();
      if (soloMember.studyMedium && soloMember.studyMedium !== "auto") {
        const tag =
          soloMember.studyMedium === "ta"
            ? "Tamil medium"
            : soloMember.studyMedium === "hi"
              ? "Hindi medium"
              : `${soloMember.studyMedium} medium`;
        if (!String(base.difficulties || "").toLowerCase().includes("medium")) {
          base.difficulties = [base.difficulties, tag].filter(Boolean).join("; ");
        }
      }
    } else if (studyMedium === "ta") {
      if (!String(base.difficulties || "").toLowerCase().includes("tamil")) {
        base.difficulties = [base.difficulties, "Tamil medium"].filter(Boolean).join("; ");
      }
    }

    // Academic profile (Year / Sem / Stream / Dept) — calibrates Learning + SD + Project
    const academicProfile = {
      college: primary.college || "",
      collegeYear: primary.collegeYear != null ? Number(primary.collegeYear) : null,
      semester: primary.semester != null ? Number(primary.semester) : null,
      schoolGrade: primary.schoolGrade != null ? Number(primary.schoolGrade) : null,
      department: primary.department || "",
      skillLevel: Number(primary.skillLevel) || 3,
    };
    base.college = academicProfile.college;
    base.collegeYear = academicProfile.collegeYear;
    base.semester = academicProfile.semester;
    base.schoolGrade = academicProfile.schoolGrade;
    base.department = academicProfile.department;
    base.skillLevel = academicProfile.skillLevel;
    base.leetcodeUsername = String(primary.leetcodeUsername || "").trim();
    base._academicProfile = JSON.stringify(academicProfile);

    // Per-subject include / like / dislike (solo primary + each team member)
    if (on("subjects")) {
      const primaryPrefs = Array.isArray(primary.subjectPreferences)
        ? primary.subjectPreferences
        : [];
      base._subjectPreferences = JSON.stringify(primaryPrefs);
      base._teamSubjectPreferences = JSON.stringify(
        roster.map((m) => ({
          name: m.name || "",
          subjectPreferences: Array.isArray(m.subjectPreferences) ? m.subjectPreferences : [],
        }))
      );
      // Rebuild SUBJECTS block from primary includes (cleaner than line filtering)
      if (primaryPrefs.length) {
        const included = primaryPrefs.filter((p) => p.included !== false);
        const liked = included.filter((p) => p.sentiment === "like").map((p) => p.name);
        const disliked = included.filter((p) => p.sentiment === "dislike").map((p) => p.name);
        const header = [
          `COLLEGE: ${academicProfile.college || ""}`,
          `DEPARTMENT: ${academicProfile.department || ""}`,
          `YEAR: ${academicProfile.collegeYear || ""}`,
          `SEMESTER: ${academicProfile.semester || ""}`,
          `SOURCE: R2023 + student subject preferences`,
          "",
          "SUBJECTS:",
          ...included.map((p) => `- ${p.code ? `${p.code} — ` : ""}${p.name}`),
          "",
          "LEARNING_POOL:",
          ...included.slice(0, 8).map((p) => `- ${p.name}`),
        ];
        if (liked.length) {
          header.push("", "LIKED_SUBJECTS:", ...liked.map((n) => `- ${n}`));
        }
        if (disliked.length) {
          header.push("", "DISLIKED_SUBJECTS:", ...disliked.map((n) => `- ${n}`));
        }
        const docs = included.filter((p) => String(p.syllabusText || "").trim());
        if (docs.length) {
          header.push("", "SUBJECT_DOCS:");
          docs.forEach((p) => {
            const names = (p.docNames || []).join(", ");
            header.push(`### ${p.code ? `${p.code} — ` : ""}${p.name}${names ? ` [${names}]` : ""}`);
            header.push(String(p.syllabusText).slice(0, 12000));
            header.push("");
          });
        }
        base.syllabus = header.join("\n");
      }
    } else {
      base._subjectPreferences = "[]";
      base._teamSubjectPreferences = "[]";
    }

    if (named.length >= 2 && !forceSolo) {
      base.capability = named
        .map((m) => {
          const school = isSchoolCampus(m.college);
          const level = school
            ? `Class ${m.schoolGrade || "?"}`
            : `Y${m.collegeYear || "?"} Sem${m.semester || "?"}`;
          const strengths = m.capability || "(strengths not set)";
          const difficulties = m.difficulties || "(difficulties not set)";
          const ir = normalizeItemRatings(m);
          const rated = formatRatedItems(ir.capability);
          const diffRated = formatRatedItems(ir.difficulties);
          return `${m.name}: skill=${m.skillLevel ?? 3}/5 | college=${m.college || "—"} | ${level} | dept=${m.department || "—"} | Strengths: ${strengths}${rated ? ` | Skill ratings: ${rated}` : ""} | Difficulties: ${difficulties}${diffRated ? ` | Difficulty ratings: ${diffRated}` : ""}`;
        })
        .join("\n");
      base.interest = named
        .map((m) => {
          const ir = normalizeItemRatings(m);
          const likes = m.interest || "(likes not set)";
          const dislikes = m.dislikes || "(dislikes not set)";
          const likeRated = formatRatedItems(ir.interest);
          const dislikeRated = formatRatedItems(ir.dislikes);
          return `${m.name}: Likes/activities: ${likes}${likeRated ? ` | Like ratings: ${likeRated}` : ""} | Dislikes/avoid: ${dislikes}${dislikeRated ? ` | Dislike ratings: ${dislikeRated}` : ""}`;
        })
        .join("\n");
      base.domain = named
        .map((m) => {
          const ir = normalizeItemRatings(m);
          const domainRated = formatRatedItems(ir.domain);
          return `${m.name}: domain=${m.domain || "(not set)"}${domainRated ? ` | Domain ratings: ${domainRated}` : ""} | dept=${m.department || "—"} | role=${m.targetRole || "—"} | company=${resolveDreamCompany(m.targetCompany) || "—"}`;
        })
        .join("\n");
      const individualGoals = named
        .map((m) => `${m.name}: ${m.assessment || "(no individual goals)"}`)
        .join("\n");
      base.assessment = [
        base.assessment ? `TEAM (shared):\n${base.assessment}` : "",
        `INDIVIDUAL:\n${individualGoals}`,
      ].filter(Boolean).join("\n\n");
      base._isTeam = true;
      base._teamMembers = JSON.stringify(
        named.map((m) => ({
          ...m,
          targetCompany: resolveDreamCompany(m.targetCompany),
          itemRatings: normalizeItemRatings(m),
          skillRatings: normalizeItemRatings(m).capability,
        }))
      );
    } else {
      base._isTeam = false;
      // Solo: still persist the single member academic fields for engines
      if (roster[0]) {
        const ir = normalizeItemRatings({
          ...roster[0],
          skillRatings: textInputs.skillRatings || roster[0].skillRatings,
          itemRatings: textInputs.itemRatings || roster[0].itemRatings,
        });
        base._teamMembers = JSON.stringify([{
          ...roster[0],
          targetCompany: resolveDreamCompany(roster[0].targetCompany),
          itemRatings: ir,
          skillRatings: ir.capability,
        }]);
      }
      // Compose solo persona (strengths + difficulties, likes + dislikes)
      const strengths = String(base.capability || textInputs.capability || "").trim();
      const difficulties = String(textInputs.difficulties || roster[0]?.difficulties || "").trim();
      const soloIr = normalizeItemRatings(textInputs.itemRatings ? textInputs : { ...roster[0], ...textInputs });
      const rated = formatRatedItems(soloIr.capability);
      const diffRated = formatRatedItems(soloIr.difficulties);
      const likeRated = formatRatedItems(soloIr.interest);
      const dislikeRated = formatRatedItems(soloIr.dislikes);
      const domainRated = formatRatedItems(soloIr.domain);
      const likes = String(base.interest || textInputs.interest || "").trim();
      const dislikes = String(textInputs.dislikes || roster[0]?.dislikes || "").trim();
      base.dislikes = dislikes;
      base.difficulties = [difficulties, diffRated ? `Difficulty ratings: ${diffRated}` : ""]
        .filter(Boolean)
        .join(" | ");
      if (strengths || difficulties || rated) {
        base.capability = [
          strengths ? `Strengths: ${strengths}` : "",
          rated ? `Skill ratings: ${rated}` : "",
          difficulties ? `Difficulties: ${difficulties}` : "",
          diffRated ? `Difficulty ratings: ${diffRated}` : "",
        ].filter(Boolean).join(" | ");
      }
      if (likes || dislikes || likeRated || dislikeRated) {
        base.interest = [
          likes ? `Likes/activities: ${likes}` : "",
          likeRated ? `Like ratings: ${likeRated}` : "",
          dislikes ? `Dislikes/avoid: ${dislikes}` : "",
          dislikeRated ? `Dislike ratings: ${dislikeRated}` : "",
        ].filter(Boolean).join(" | ");
      }
      if (domainRated) {
        base.domain = [String(base.domain || textInputs.domain || "").trim(), `Domain ratings: ${domainRated}`]
          .filter(Boolean)
          .join(" | ");
      }
      base._itemRatings = JSON.stringify(soloIr);
    }

    base._repoMode = repoMode || "generate_new";
    if (repoEntryId) base._repoEntryId = String(repoEntryId);
    if (Array.isArray(repoRegenParts) && repoRegenParts.length) {
      base._repoRegenParts = JSON.stringify(repoRegenParts);
    }
    if (String(repoAdaptNote || "").trim()) {
      base._repoAdaptNote = String(repoAdaptNote).trim().slice(0, 800);
    }
    base._systemDesignLevel = systemDesignLevel;
    base._dsaLevel = dsaLevel;
    const configuredDayGroups = (dayActivityGroups || [])
      .map((group) => ({
        startDay: Math.max(1, Number(group.startDay) || 1),
        endDay: Math.min(Number(numDays) || 365, Number(group.endDay) || 1),
        template: visibleDailyBlueprint(group.activities || {}),
      }))
      .filter((group) => group.endDay >= group.startDay && group.template.length);
    if (configuredDayGroups.length) base._dayActivityGroups = JSON.stringify(configuredDayGroups);
    applyMustApplyOverride(base);
    return base;
  };

  // ── Generate ─────────────────────────────────────────────────────────────────
  const homeworkFromDaily = (daily) => homeworkPlanFromDaily(daily);

  const missingActivityDetails = () => {
    const issues = [
      { part: "daily", catalog: DAY_ACTIVITY_SLOTS, label: "Daily activity" },
      { part: "homework", catalog: HOMEWORK_ACTIVITY_SLOTS, label: "Homework activity" },
    ].flatMap(({ part, catalog, label }) => {
      const active = activeSlotOverrides(slotOverrides?.[part], catalog, part === "daily");
      return Object.entries(active).flatMap(([id, row]) => {
        if (!row || row.mode === "hide") return [];
        const preset = catalog.find((slot) => slot.id === id);
        const isCustom = !preset || row.mode === "add";
        const visibleTitle = String(row.title || preset?.label || "").trim();
        const renamed = isCustom || (visibleTitle && visibleTitle !== preset?.label);
        if (!renamed || /^(break|lunch|rest|short break)$/i.test(visibleTitle)) return [];
        if (isCustom && !visibleTitle) return [`New ${label.toLowerCase()} needs a name and task/content.`];
        if (!String(row.notes || "").trim()) return [`“${visibleTitle || preset?.label || id}” needs task/content instructions.`];
        return [];
      });
    });
    const validGroups = (dayActivityGroups || []).map((group, i) => ({
      ...group,
      startDay: Number(group.startDay),
      endDay: Number(group.endDay),
      label: `Day group ${i + 1}`,
    }));
    validGroups.forEach((group) => {
      if (!Number.isInteger(group.startDay) || !Number.isInteger(group.endDay) || group.startDay < 1 || group.endDay > Number(numDays) || group.endDay < group.startDay) {
        issues.push(`${group.label} needs a valid, non-reversed range within Days 1–${numDays}.`);
      }
      const active = activeSlotOverrides(group.activities, DAY_ACTIVITY_SLOTS, true);
      Object.entries(active).forEach(([id, row]) => {
        if (!row || row.mode === "hide") return;
        const preset = DAY_ACTIVITY_SLOTS.find((slot) => slot.id === id);
        const title = String(row.title || preset?.label || "").trim();
        const renamed = !preset || (title && title !== preset.label);
        if (renamed && !/^(break|lunch|rest|short break)$/i.test(title) && !String(row.notes || "").trim()) {
          issues.push(`${group.label}: “${title || id}” needs task/content instructions.`);
        }
      });
    });
    for (let i = 0; i < validGroups.length; i += 1) {
      for (let j = i + 1; j < validGroups.length; j += 1) {
        const a = validGroups[i]; const b = validGroups[j];
        if (a.startDay <= b.endDay && b.startDay <= a.endDay) {
          issues.push(`${a.label} and ${b.label} overlap. Give each working day to one group only.`);
        }
      }
    }
    return issues;
  };

  /** Persist + sync homework so Plans never flash misaligned / unsynced content. */
  const alignAndPersistGeneratedPlans = async (rawPlans, memberMeta = null, saveOpts = {}) => {
    const plansOut = { ...(rawPlans || {}) };
    if (!plansOut.daily?.rows?.length) return plansOut;
    plansOut.daily = { ...plansOut.daily, rows: withHomeworkRows(plansOut.daily.rows) };

    if (localStorage.getItem("token")) {
      await setBasePlan(plansOut.daily);
      await syncDailyPlan(plansOut.daily);
      if (plansOut.weekly?.rows?.length) {
        await syncWeeklyPlan(plansOut.weekly).catch(() => null);
      }
      const member = memberMeta || resolveVersionMember();
      await saveVersion(
        plansOut.daily,
        saveOpts.changeType || "Initial Plan",
        saveOpts.reason || "First generated daily schedule",
        member,
        {
          trigger: saveOpts.trigger || "initial_generate",
          changedBy: saveOpts.changedBy || "AI",
        }
      );
      const hwSync = await syncHomeworkPlan(plansOut.daily, {
        source: saveOpts.hwSource || "generate",
      }).catch(() => null);
      if (homeworkHasBody(hwSync?.plan?.rows)) {
        plansOut.homework = hwSync.plan;
      } else {
        plansOut.homework = homeworkFromDaily(plansOut.daily);
      }
    } else {
      plansOut.homework = homeworkFromDaily(plansOut.daily);
    }
    return plansOut;
  };

  const handleGenerate = async () => {
    const activityIssues = missingActivityDetails();
    if (activityIssues.length) {
      goToInputPage("dayActivities");
      showToast("warning", activityIssues[0] + " Add what the learner should do and produce, then generate again.", "Add activity details");
      return null;
    }
    const timingGate = validateDayTiming({
      numDays,
      startTime: dailyStart,
      endTime: dailyEnd,
      lunchStart,
      lunchEnd,
      breaks,
    });
    if (!timingGate.ok) {
      showToast("error", timingGate.errors[0] || "Fix timing before generating.");
      return null;
    }

    setGenerating(true);
    setVersionPreview(null);
    try { localStorage.removeItem("ap_version_preview"); } catch (_) {}
    setLoading({ daily: true, weekly: true, homework: true });
    // Stay on Plans with loading — keep previous aligned plans visible until the new ones are synced
    goToView(1);
    let finalInputs = buildFinalInputs();

    const hasFiles = Object.values(files).some((a) => a.length > 0);
    if (hasFiles) {
      showToast("info", "Reading your files…", "Uploading");
      try {
        const res = await uploadFiles(files);
        if (res.success) {
          if (res.stored && typeof res.stored === "object") {
            setCloudUploads((prev) => ({ ...prev, ...res.stored }));
          }
          const storedCount = Object.values(res.stored || {}).reduce(
            (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
            0
          );
          if (storedCount > 0) {
            showToast("success", "Your files are saved in AI-Path-Builder on your account.", "Files saved");
          } else if (res.storage === "s3") {
            showToast("warning", "We read your files, but could not save them. Please try again.", "Files read");
          } else {
            showToast("warning", "We read your files. Saving to your folder is not available on this server yet.", "Files read");
          }
          const membersAfterUpload = (teamMembers || []).map((m) => ({ ...m }));
          Object.entries(res.extracted).forEach(([key, arr]) => {
            const text = arr.map((f) => "\n[" + f.filename + "]\n" + f.content).join("\n");
            const memberMatch = key.match(/^m(\d+)_(capability|interest|domain|assessment)$/);
            if (memberMatch) {
              const idx = Number(memberMatch[1]);
              const field = memberMatch[2];
              if (membersAfterUpload[idx]) {
                membersAfterUpload[idx][field] = ((membersAfterUpload[idx][field] || "") + text).trim();
              }
            } else if (key === "manualPrompt") {
              finalInputs.manualPromptExtracted = ((finalInputs.manualPromptExtracted || "") + text).trim();
            } else {
              finalInputs[key] = (finalInputs[key] || "") + text;
            }
          });
          // Fold enriched members back into generation inputs
          finalInputs._teamMembers = JSON.stringify(
            membersAfterUpload.map((m) => ({
              ...m,
              targetCompany: resolveDreamCompany(m.targetCompany),
            }))
          );
          const named = membersAfterUpload.filter((m) => m.name?.trim());
          if (named.length >= 2) {
            finalInputs.capability = named
              .map((m) => `${m.name}: skill=${m.skillLevel ?? 3}/5 | ${m.capability || "(not set)"}`)
              .join("\n");
            finalInputs.interest = named
              .map((m) => `${m.name}: ${m.interest || "(not set)"}`)
              .join("\n");
            finalInputs.domain = named
              .map((m) => `${m.name}: domain=${m.domain || "(not set)"} | role=${m.targetRole || "—"} | company=${resolveDreamCompany(m.targetCompany) || "—"}`)
              .join("\n");
          }
          applyMustApplyOverride(finalInputs);
        }
      } catch (e) {
        showToast("error", "We could not read that file. Try another PDF or Word file.", "Upload failed");
      }
    }

    setLastGenInputs(finalInputs);
    const needRag =
      plannerMode === "dsa" &&
      (!(finalInputs.dsaSyllabus || "").trim() || !(finalInputs.systemDesign || "").trim());
    showToast(
      "info",
      needRag
        ? "We are filling in missing topics from your problem, then building your schedule. This can take a few minutes."
        : "Building your schedule. This can take a few minutes.",
      "Working on it"
    );

    let apiRes = null;
    try {
      apiRes = await generateAllPlans(finalInputs);
      const failed = Object.keys(apiRes.errors || {});
      const repo = apiRes.repo || {};

      // Align homework to daily + persist BEFORE swapping UI plans (avoids flash of unsynced content)
      let aligned = apiRes.plans || {};
      if (aligned.daily && localStorage.getItem("token")) {
        try {
          aligned = await alignAndPersistGeneratedPlans(aligned, resolveVersionMember(), {
            reason: repo.reused
              ? repo.regenerated
                ? "Loaded from repository and regenerated"
                : "Loaded from common schedule repository"
              : "First generated daily schedule",
            trigger: "initial_generate",
            changedBy: repo.reused && !repo.regenerated ? "System" : "AI",
          });
          await persistInputsToDb({ lastGenInputs: finalInputs });
        } catch (persistErr) {
          console.warn("Plan DB persist failed:", persistErr?.message || persistErr);
          aligned = {
            ...(apiRes.plans || {}),
            homework: homeworkFromDaily(apiRes.plans?.daily) || apiRes.plans?.homework,
          };
          showToast("warning", "Your plans are ready here. Sign in to keep them on your account.", "Almost saved");
        }
      } else if (aligned.daily) {
        aligned = {
          ...aligned,
          homework: homeworkFromDaily(aligned.daily) || aligned.homework,
        };
      }

      setPlans(aligned);
      if (apiRes) apiRes.plans = aligned;
      const requestedDayCount = Number(finalInputs?._numDays || finalInputs?.numDays || numDays) || 0;
      const generatedDayCount = new Set(
        (aligned.daily?.rows || [])
          .map((row) => String(row?.[0] || "").trim())
          .filter(Boolean)
      ).size;
      const dayCoverage = requestedDayCount
        ? ` Daily plan: ${generatedDayCount}/${requestedDayCount} working days.`
        : "";

      if (failed.length === 0) {
        if (repo.reused && repo.regenerated) {
          showToast(generatedDayCount < requestedDayCount ? "warning" : "success", `Your schedule is ready and tailored to you.${dayCoverage}`, generatedDayCount < requestedDayCount ? "Plan incomplete" : "Schedule ready");
        } else if (repo.reused) {
          showToast(generatedDayCount < requestedDayCount ? "warning" : "success", `We found a matching schedule, so you did not have to wait.${dayCoverage}`, generatedDayCount < requestedDayCount ? "Plan incomplete" : "Schedule ready");
        } else {
          showToast(generatedDayCount < requestedDayCount ? "warning" : "success", `Your daily, weekly, and homework plans are ready.${dayCoverage}`, generatedDayCount < requestedDayCount ? "Plan incomplete" : "Plans ready");
        }
      } else showToast("warning", `We could not finish every plan. Please try again.${dayCoverage}`, "Please try again");
    } catch (e) {
      showToast("error", formatApiError(e, "Generation failed"));
    }
    setLoading({});
    setGenerating(false);
    return apiRes;
  };

  const handleRegenerate = async (moduleKey) => {
    setLoading((p) => ({ ...p, [moduleKey]: true }));
    const inputsToUse = Object.keys(lastGenInputs).length ? lastGenInputs : buildFinalInputs();
    const pretty =
      moduleKey === "daily" ? "daily" : moduleKey === "weekly" ? "weekly" : moduleKey === "homework" ? "homework" : "plan";
    showToast("info", `Refreshing your ${pretty} plan…`, "Working on it");
    try {
      // Weekly must mirror daily content for that week
      const dailyRows = moduleKey === "weekly" ? (plans?.daily?.rows || null) : null;
      const res = await generatePlan(moduleKey, inputsToUse, "", dailyRows);
      if (res.success) {
        setPlans((p) => ({ ...p, [moduleKey]: res.plan }));
        if (moduleKey === "daily" && localStorage.getItem("token")) {
          try {
            await setBasePlan(res.plan);
            await syncDailyPlan(res.plan);
            await saveVersion(res.plan, "Regenerated", "Regenerated daily schedule", resolveVersionMember(), {
              trigger: "regenerate",
              changedBy: "AI",
            });
            // Keep weekly aligned after daily redo
            if (res.plan?.rows?.length) {
              const wk = await generatePlan("weekly", inputsToUse, "", res.plan.rows);
              if (wk.success) {
                setPlans((p) => ({ ...p, weekly: wk.plan }));
                if (wk.plan?.rows?.length) await syncWeeklyPlan(wk.plan);
              }
            }
            await persistInputsToDb();
          } catch (_) {}
        } else if (moduleKey === "weekly" && localStorage.getItem("token") && res.plan?.rows?.length) {
          try {
            await syncWeeklyPlan(res.plan);
          } catch (_) {}
        }
        showToast("success", `Your ${pretty} plan is ready.`, "Updated");
      }
    } catch (e) {
      showToast("error", "We could not refresh that plan. Please try again.", "Please try again");
    }
    setLoading((p) => ({ ...p, [moduleKey]: false }));
  };

  const handlePolish = async (moduleKey) => {
    if (!plans[moduleKey]) return;
    setLoading((p) => ({ ...p, [moduleKey]: true }));
    showToast("info", "Tidying up the wording…", "Working on it");
    try {
      const res = await polishPlan(moduleKey, plans[moduleKey]);
      if (res.success) {
        setPlans((p) => ({ ...p, [moduleKey]: res.plan }));
        showToast("success", "Your plan looks clearer now.", "Updated");
      }
    } catch (e) {
      showToast("error", "We could not tidy that plan. Please try again.", "Please try again");
    }
    setLoading((p) => ({ ...p, [moduleKey]: false }));
  };

  const resolveVersionMember = (override = null) => {
    const slug = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "default";

    if (override?.name || override?.memberName || override?.email || override?.memberEmail) {
      const name = String(override.memberName || override.name || "Solo").trim() || "Solo";
      const email = String(override.memberEmail || override.email || "").trim().toLowerCase();
      return {
        memberName: name,
        memberEmail: email || undefined,
        memberKey: email ? slug(email) : slug(name),
      };
    }

    // Opened from Version Management — keep that student's name on Planner
    if (versionPreview?.memberName || versionPreview?.label) {
      const name =
        String(versionPreview.memberName || "")
          .trim() ||
        String(versionPreview.label || "")
          .split("·")[0]
          ?.trim() ||
        "Student";
      const key = String(versionPreview.memberKey || "").trim();
      return {
        memberName: name,
        memberKey: key || slug(name),
        memberVersion: versionPreview.memberVersion,
        fromVersion: true,
      };
    }

    // Individuals batch: version lane for the person currently being generated / viewed
    if (batchActiveName && Array.isArray(batchIndividualPlans) && batchIndividualPlans.length) {
      const hit = batchIndividualPlans.find(
        (r) => String(r?.name || "").trim() === String(batchActiveName).trim()
      );
      const m = hit?.member;
      if (m?.name || batchActiveName) {
        const name = String(m?.name || batchActiveName).trim() || "Solo";
        const email = String(m?.email || "").trim().toLowerCase();
        return {
          memberName: name,
          memberEmail: email || undefined,
          memberKey: email ? slug(email) : slug(name),
        };
      }
    }

    const named = (teamMembers || []).filter((m) => m?.name?.trim());
    if (named.length >= 2 && teamViewMemberId && teamSchedule?.members?.length) {
      const mm = teamSchedule.members.find((m) => String(m.id) === String(teamViewMemberId));
      if (mm?.name) {
        const roster = named.find(
          (m) => String(m.name).trim().toLowerCase() === String(mm.name).trim().toLowerCase()
        );
        const name = String(mm.name).trim();
        const email = String(roster?.email || mm.email || "").trim().toLowerCase();
        return {
          memberName: name,
          memberEmail: email || undefined,
          memberKey: email ? slug(email) : slug(name),
        };
      }
    }
    const primary = named[0];
    const name = (primary?.name || user?.name || user?.username || "Solo").trim() || "Solo";
    const email = String(primary?.email || user?.email || "").trim().toLowerCase();
    return {
      memberName: name,
      memberEmail: email || undefined,
      memberKey: email ? slug(email) : slug(name),
    };
  };

  const persistDailyToDb = async (dailyPlan, changeType = "Manual Edit", reason = "Plan updated", extras = {}) => {
    if (!dailyPlan?.rows?.length || !localStorage.getItem("token")) return;
    try {
      await setBasePlan(dailyPlan);
      await syncDailyPlan(dailyPlan);
      await saveVersion(dailyPlan, changeType, reason, resolveVersionMember(), {
        trigger: extras.trigger,
        changedBy: extras.changedBy,
        protected: extras.protected,
        manualOverride: extras.manualOverride,
        ...extras,
      });
      const weekly = plans?.weekly;
      if (weekly?.rows?.length) {
        await syncWeeklyPlan(weekly).catch(() => null);
      }
      // Always refresh dedicated homework collection from daily homework rows
      const hw = await syncHomeworkPlan(dailyPlan, { source: "sync-from-daily" }).catch(() => null);
      if (homeworkHasBody(hw?.plan?.rows)) {
        setPlans((p) => ({ ...p, homework: hw.plan }));
      } else {
        const filled = homeworkFromDaily(dailyPlan);
        if (filled) setPlans((p) => ({ ...p, homework: filled }));
      }
      await persistInputsToDb().catch(() => null);
    } catch (e) {
      console.warn("Daily DB persist failed:", e?.message || e);
    }
  };

  /** Save Tonight's Homework as its own DB module (does not replace daily). */
  const persistHomeworkToDb = async (homeworkPlan, alsoMirrorDaily = true) => {
    if (!homeworkPlan?.rows?.length || !localStorage.getItem("token")) return;
    try {
      const hw = await syncHomeworkPlan(homeworkPlan, { source: "edit" });
      if (hw?.plan) setPlans((p) => ({ ...p, homework: hw.plan }));

      // Keep matching daily homework cells in sync so Daily tab stays consistent
      if (alsoMirrorDaily && plans?.daily?.rows?.length) {
        const byDay = new Map(
          homeworkPlan.rows.map((r) => [String(r[0]), r])
        );
        const nextDailyRows = plans.daily.rows.map((r) => {
          if (!Array.isArray(r) || !/homework/i.test(String(r[2] || ""))) return r;
          const hwRow = byDay.get(String(r[0]));
          if (!hwRow) return r;
          return [r[0], r[1], r[2], hwRow[3]];
        });
        const nextDaily = { ...plans.daily, rows: nextDailyRows };
        setPlans((p) => ({ ...p, daily: nextDaily, homework: hw?.plan || homeworkPlan }));
        await setBasePlan(nextDaily).catch(() => null);
        await syncDailyPlan(nextDaily).catch(() => null);
      }
    } catch (e) {
      console.warn("Homework DB persist failed:", e?.message || e);
    }
  };

  const persistTeamSchedule = useCallback((updatedSchedule) => {
    setTeamSchedule((prev) => (prev ? { ...prev, schedule: updatedSchedule } : prev));
    if (linkedPsId && teamName && Array.isArray(updatedSchedule)) {
      saveTeamScheduleEdits(linkedPsId, teamName, updatedSchedule).catch((e) =>
        console.warn("Team schedule save failed:", e?.message || e)
      );
    }
  }, [linkedPsId, teamName]);

  const handleModuleUpdate = (key, plan, meta = {}) => {
    setPlans((p) => ({ ...p, [key]: plan }));
    const changeType = meta.changeType || "Manual Edit";
    const reason = meta.reason || "Edited schedule / homework";
    const extras = {
      trigger: meta.trigger || (
        /refined/i.test(changeType) ? "refine"
          : /regenerat/i.test(changeType) ? "regenerate"
            : /faculty/i.test(changeType) || meta.manualOverride ? "faculty_override"
              : "manual_edit"
      ),
      changedBy: meta.changedBy || (
        /refined|regenerat|initial/i.test(changeType) ? "AI"
          : meta.manualOverride ? "Faculty"
            : "Student"
      ),
      protected: meta.protected || meta.manualOverride || false,
      manualOverride: meta.manualOverride || false,
    };
    if (key === "daily") persistDailyToDb(plan, changeType, reason, extras);
    if (key === "homework") persistHomeworkToDb(plan, true);
    if (key === "weekly" && localStorage.getItem("token")) {
      syncWeeklyPlan(plan).catch((e) => console.warn("Weekly DB save failed:", e?.message || e));
      // Keep a version snapshot when weekly is refined (daily remains source of truth for History)
      if (plans?.daily?.rows?.length) {
        persistDailyToDb(
          plans.daily,
          changeType,
          reason.includes("weekly") ? reason : `${reason} (weekly updated)`,
          extras
        );
      }
    }
    showToast("success", "Your plan is updated.", "Updated");
  };

  const handleDailyPlanReplaced = (newDailyPlan, newVersion) => {
    setPlans((p) => ({ ...p, daily: newDailyPlan }));
    // Already versioned by caller when needed
    showToast("success", "Your daily plan is updated.", "Updated");
  };

  const handleVersionRestored = async (restoredPlan, newVersion) => {
    if (!restoredPlan) return;
    setPlans((p) => ({ ...p, daily: restoredPlan }));
    // restoreVersion already wrote PlanVersion + DailyPlan — avoid a second version save
    try {
      if (localStorage.getItem("token")) {
        await setBasePlan(restoredPlan).catch(() => null);
        const hw = await syncHomeworkPlan(restoredPlan, { source: "sync-from-daily" }).catch(() => null);
        if (hw?.plan?.rows?.length) {
          setPlans((p) => ({ ...p, homework: hw.plan }));
        }
      }
    } catch (e) {
      console.warn("Post-restore sync failed:", e?.message || e);
    }
    showToast("success", "Your earlier plan is restored.", "Restored");
  };

  useEffect(() => {
    const onRestored = (ev) => {
      const plan = ev?.detail?.plan;
      if (!plan) return;
      setPlans((p) => ({ ...p, daily: plan }));
      showToast("success", "Your earlier plan is restored.", "Restored");
    };
    const onPreview = (ev) => {
      const plan = ev?.detail?.plan;
      if (!plan) return;
      const normalized = Array.isArray(plan)
        ? { module: "daily", rows: plan }
        : { ...plan, module: plan.module || "daily" };
      const meta = {
        label: ev?.detail?.label || "",
        memberName:
          ev?.detail?.memberName ||
          String(ev?.detail?.label || "")
            .split("·")[0]
            ?.trim() ||
          "Student",
        memberKey: ev?.detail?.memberKey || "",
        version: ev?.detail?.version,
        memberVersion: ev?.detail?.memberVersion,
        at: Date.now(),
      };
      setVersionPreview(meta);
      try {
        localStorage.setItem("ap_version_preview", JSON.stringify(meta));
      } catch (_) {}
      if (meta.memberName) setBatchActiveName(meta.memberName);
      setPlans((p) => ({ ...p, daily: normalized }));
      showToast(
        "info",
        meta.memberName
          ? `You are viewing ${meta.memberName}’s saved schedule.`
          : "You are viewing a saved schedule.",
        "Preview"
      );
    };
    window.addEventListener("ap:plan-restored", onRestored);
    window.addEventListener("ap:plan-preview", onPreview);
    return () => {
      window.removeEventListener("ap:plan-restored", onRestored);
      window.removeEventListener("ap:plan-preview", onPreview);
    };
  }, []);

  const handleSingleDayRegenerated = (dayId, newRows) => {
    setPlans((p) => {
      if (!p.daily?.rows) return p;
      const rows = p.daily.rows;
      const firstIdx = rows.findIndex((r) => r[0] === dayId);
      const filtered = rows.filter((r) => r[0] !== dayId);
      const insertAt = firstIdx === -1 ? filtered.length : firstIdx;
      const updated = [...filtered.slice(0, insertAt), ...newRows, ...filtered.slice(insertAt)];
      const nextDaily = { ...p.daily, rows: updated };
      persistDailyToDb(nextDaily, "Day Regenerated", `Regenerated ${dayId}`, {
        trigger: "day_regenerate",
        changedBy: "AI",
      });
      return { ...p, daily: nextDaily };
    });
  };

  // ── Team DB helpers ──────────────────────────────────────────────────────────
  // Picks a problem statement to link the team to. The backend now
  // auto-generates a missing dailySchedule on demand (see
  // /api/team/generate-schedule), so this just needs to resolve to *some*
  // valid problem statement id — it doesn't need to check for a schedule
  // itself anymore. We still re-validate a cached `linkedPsId` against the
  // live list (it's persisted in localStorage across sessions and could be
  // pointing at a deleted statement).
  const resolveLinkablePsId = async () => {
    const res = await getProblemStatements();
    const all = res?.data || [];
    setPsOptions(all);

    if (linkedPsId && all.some(s => s._id === linkedPsId)) {
      return { psId: linkedPsId, hadAny: all.length > 0 };
    }

    // Fall back to the MOST RECENTLY created problem statement rather than
    // whichever happens to be first in the array — picking an arbitrary
    // (possibly old, possibly much-longer-duration) statement was the
    // source of confusing "which plan is this team even linked to?" and
    // day-count mismatches. The panel also shows this pick explicitly so
    // you can switch it if it's wrong.
    const sorted = [...all].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return { psId: sorted[0]?._id || "", hadAny: all.length > 0 };
  };

  // (runGenerateTeamSchedule removed — superseded by runAutoTeamSchedule
  //  which also passes the fresh daily rows for the rich rows-based path.)


  useEffect(() => {
    getProblemStatements().then(res => setPsOptions(res?.data || [])).catch(() => {});
  }, []);

  // Load existing team schedule from DB when psId / teamName known
  useEffect(() => {
    if (!linkedPsId) return;
    (async () => {
      try {
        const r = await getTeamSchedule(linkedPsId, teamName);
        if (r?.data) {
          setTeamSchedule(r.data);
          const mid = r.data.members?.[0]?.id;
          if (mid) {
            setTeamActiveMember(mid);
            setTeamViewMemberId((prev) => prev || String(mid));
          }
        }
      } catch (_) {}
      // Also reload dbStatus
      try {
        const r2 = await getTeamMembers(linkedPsId, teamName);
        const members = r2?.data || [];
        if (members.length) setTeamDbStatus({ saved:true, count:members.length, names: members.map(m=>m.name).join(", ") });
      } catch (_) {}
    })();
  }, [linkedPsId, teamName]);

  // When the main plan generates successfully, capture the PS id
  // After main generation, automatically build per-member schedules when
  // there are 2+ real named members. Only fires for multi-member teams —
  // solo users see their own daily/weekly plan directly; no team panel needed.
  const runAutoTeamSchedule = async (dailyRows, psId, overrides = {}) => {
    if (rosterMode === "individuals") return; // batch solo plans — not team split
    const realMembers = (overrides.members || teamMembers).filter(m => m.name?.trim());
    const scheduleTeamName = overrides.teamName || teamName;
    if (realMembers.length < 2) return; // solo: skip entirely
    if (!psId) return;

    setTeamGenerating(true);
    let data = null;
    try {
      // Save members first (idempotent bulk-replace)
      const saved = await saveTeamMembersBulk(psId, scheduleTeamName, realMembers);
      const savedDocs = saved?.data || [];
      if (savedDocs.length) {
        setTeamDbStatus({ saved: true, count: savedDocs.length, names: savedDocs.map(m => m.name).join(", ") });
      }

      // Generate schedule using rich daily rows so output is pixel-identical
      // to the solo plan rather than the boilerplate schedule-based fallback.
      const r = await generateTeamSchedule(
        psId,
        scheduleTeamName,
        teamDays ? Number(teamDays) : undefined,
        dailyRows,
        activeDTStage
      );
      data = r?.data || null;
      setTeamSchedule(data);
      const syncedMembers = data?.members || [];
      const firstMember = syncedMembers[0];
      if (firstMember) {
        setTeamActiveMember(firstMember.id);
        setTeamViewMemberId(String(firstMember.id));
      }
      setTeamFilterWeek("all");
      if (!overrides.quiet) {
        if (syncedMembers.length < 2) {
          showToast("warning", "Add names for both team members, then try again.", "Team");
        } else {
          showToast(
            "success",
            `Team is ready: ${syncedMembers.map((m) => m.name).join(" and ")}. Open Daily and tap a name to switch.`,
            "Team ready"
          );
        }
      }
    } catch (e) {
      showToast("error", "We could not build the team schedule. Please try again.", "Team");
    }
    setTeamGenerating(false);
    return data;
  };

  // Resolves (or auto-creates) the ProblemStatement id that team schedules
  // get stored against. Reusable by both the full-generate flow and the
  // manual "Sync Team Schedules" button, so team linking doesn't depend on
  // which button the user happened to click.
  const ensureLinkedPsId = async () => {
    let psId = "";
    try {
      const res = await getProblemStatements();
      const all = res?.data || [];
      setPsOptions(all);
      if (linkedPsId && all.some(s => s._id === linkedPsId)) {
        psId = linkedPsId;
      } else {
        const sorted = [...all].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        psId = sorted[0]?._id || "";
        if (psId) setLinkedPsId(psId);
      }
    } catch (_) {}

    // Team schedules are stored against a real ProblemStatement record. If
    // the user has never used the separate Problem Statement generator tool,
    // no such record exists yet — silently create a minimal one from the
    // current planner inputs so team members still get their schedules,
    // right here in the Planner tab, no extra tool/tab required.
    if (!psId) {
      try {
        const seedText = (textInputs.problem || textInputs.syllabus || textInputs.dsaSyllabus || "").trim();
        const title = seedText
          ? seedText.slice(0, 80).replace(/\s+/g, " ")
          : `${teamName || "Team"} Project — ${new Date().toLocaleDateString()}`;
        const description = seedText || `Auto-created to link ${teamName || "the team"}'s daily/weekly schedule to this project.`;
        const created = await quickCreateProblemStatement(title, description, { allowDuplicate: true });
        psId = created?.data?._id || "";
        if (psId) {
          setLinkedPsId(psId);
          setPsOptions(p => [created.data, ...p]);
        }
      } catch (e) {
        showToast("error", "We could not link the team project. Please try again.", "Team");
      }
    }
    return psId;
  };

  // Manual "sync now" — uses whatever daily plan already exists in state, so
  // it works right after you add/edit team members WITHOUT re-running the
  // (slow) full plan generation again. This is the button inside the
  // "TEAM MEMBERS' SCHEDULES" panel on the Generated Plans tab.
  const syncTeamSchedulesNow = async () => {
    const focusTeam = isMultiTeam
      ? rosterTeams.find((t) => t.key === (activeTeamKey || rosterTeams[0]?.key))
      : null;
    const realMembers = (focusTeam?.members || teamMembers).filter((m) => m.name?.trim());
    const scheduleTeamName = focusTeam?.name || teamName;
    if (realMembers.length < 2) {
      showToast("warning", "Add at least two team members with names.", "Team");
      return;
    }
    const dailyRows = plans?.daily?.rows;
    if (!Array.isArray(dailyRows) || dailyRows.length === 0) {
      showToast("warning", "Create your daily plan first, then build the team schedule.", "Team");
      return;
    }
    const psId = await ensureLinkedPsId();
    if (!psId) {
      showToast("error", "We could not link the team project. Please try again.", "Team");
      return;
    }
    await runAutoTeamSchedule(dailyRows, psId, {
      members: realMembers,
      teamName: scheduleTeamName,
    });
  };

  const handleGenerateAndCapture = async () => {
    const activityIssues = missingActivityDetails();
    if (activityIssues.length) {
      goToInputPage("dayActivities");
      showToast("warning", activityIssues[0] + " Add what the learner should do and produce, then generate again.", "Add activity details");
      return;
    }
    const timingGate = validateDayTiming({
      numDays,
      startTime: dailyStart,
      endTime: dailyEnd,
      lunchStart,
      lunchEnd,
      breaks,
    });
    if (!timingGate.ok) {
      showToast("error", timingGate.errors[0] || "Fix timing before generating.");
      return;
    }

    const named = (teamMembers || []).filter((m) => m?.name?.trim());
    if (rosterMode === "individuals" && named.length >= 1) {
      await handleBatchIndividualGenerate(named);
      return;
    }
    const teams = groupRosterTeams(teamMembers).filter((t) => t.members.length);
    if (teams.length >= 2) {
      await handleBatchTeamGenerate(teams);
      return;
    }
    const apiRes = await handleGenerate();
    const psId = await ensureLinkedPsId();

    // Auto-generate team schedule from the freshly generated rows (2+ members only)
    const dailyRows = apiRes?.plans?.daily?.rows;
    if (psId && Array.isArray(dailyRows) && dailyRows.length > 0) {
      await runAutoTeamSchedule(dailyRows, psId);
    }
  };

  /** One Generate click → separate solo schedule per imported individual */
  const handleBatchIndividualGenerate = async (namedMembers) => {
    const list = namedMembers || (teamMembers || []).filter((m) => m?.name?.trim());
    if (!list.length) {
      showToast("warning", "Import a list with student names first.", "Individuals");
      return;
    }
    setGenerating(true);
    setLoading({ daily: true, weekly: true, homework: true });
    goToView(1);
    setBatchIndividualPlans([]);
    setBatchProgress({ index: 0, total: list.length, name: list[0].name });
    showToast(
      "info",
      `Building a separate schedule for each person (${list.length}). This can take a few minutes.`,
      "Working on it"
    );

    const results = [];
    for (let i = 0; i < list.length; i++) {
      const member = list[i];
      setBatchProgress({ index: i + 1, total: list.length, name: member.name });
      showToast("info", `Building ${member.name}’s schedule (${i + 1} of ${list.length})…`, "Working on it");
      try {
        const inputs = buildFinalInputs({ soloMember: member, forceSolo: true });
        setLastGenInputs(inputs);
        const apiRes = await generateAllPlans(inputs);
        let plansForMember = apiRes?.plans || {};
        if (plansForMember.daily) {
          try {
            plansForMember = await alignAndPersistGeneratedPlans(
              plansForMember,
              {
                memberName: String(member.name || "").trim() || "Solo",
                name: member.name,
                email: member.email || "",
                memberEmail: member.email || "",
                rollNumber: member.rollNumber || "",
              },
              {
                reason: `Individual schedule for ${member.name}`,
                trigger: "batch_individual",
                changedBy: "AI",
              }
            );
          } catch (_) {
            plansForMember = {
              ...plansForMember,
              homework: homeworkFromDaily(plansForMember.daily) || plansForMember.homework,
            };
          }
        }
        results.push({
          name: member.name,
          member,
          plans: plansForMember,
          error: null,
        });
        // Show only after homework is aligned/synced for this person
        setPlans(plansForMember);
        setBatchActiveName(member.name);
      } catch (e) {
        results.push({
          name: member.name,
          member,
          plans: null,
          error: e?.response?.data?.error || e.message || "Generate failed",
        });
        showToast("error", `We could not finish ${member.name}’s schedule. Please try again.`, "Please try again");
      }
      setBatchIndividualPlans([...results]);
    }

    const ok = results.filter((r) => r.plans?.daily?.rows?.length);
    setBatchIndividualPlans(results);
    if (ok.length) {
      const last = ok[ok.length - 1];
      setPlans(last.plans);
      setBatchActiveName(last.name);
      showToast(
        "success",
        `${ok.length} of ${list.length} student schedules are ready. Tap a name on Plans to review.`,
        "Schedules ready"
      );
    } else {
      showToast("error", "We could not build those individual schedules. Please try again.", "Please try again");
    }
    setBatchProgress(null);
    setLoading({});
    setGenerating(false);
  };

  const applyTeamBatchResult = (entry) => {
    if (!entry) return;
    setActiveTeamKey(entry.key);
    setTeamName(entry.teamName || entry.key);
    if (entry.plans) setPlans(entry.plans);
    setTeamSchedule(entry.teamSchedule || null);
    if (entry.linkedPsId) setLinkedPsId(entry.linkedPsId);
    if (entry.problemStatement) {
      setTextInputs((prev) => ({ ...prev, problem: entry.problemStatement }));
    }
    const mid = entry.teamSchedule?.members?.[0]?.id;
    if (mid) {
      setTeamActiveMember(mid);
      setTeamViewMemberId(String(mid));
    }
  };

  /** One Generate → one plan per imported team, each using that team's problem statement. */
  const handleBatchTeamGenerate = async (teams) => {
    const list = (teams || []).filter((t) => t.members?.length);
    if (list.length < 2) return;
    const missing = list.filter((t) => !String(t.problemStatement || textInputs.problem || "").trim());
    if (missing.length) {
      showToast(
        "error",
        `Add a problem statement for: ${missing.map((t) => t.name || t.key).join(", ")}. Each team needs its own.`,
        "Team mapping"
      );
      return;
    }

    setGenerating(true);
    setLoading({ daily: true, weekly: true, homework: true });
    goToView(1);
    setBatchTeamPlans([]);
    setBatchProgress({ index: 0, total: list.length, name: list[0].name || list[0].key });
    showToast(
      "info",
      `Building ${list.length} team plans — each team keeps its own problem statement.`,
      "Working on it"
    );

    const results = [];
    for (let i = 0; i < list.length; i++) {
      const team = list[i];
      const teamLabel = team.name || team.key;
      const problem = String(team.problemStatement || textInputs.problem || "").trim();
      setBatchProgress({ index: i + 1, total: list.length, name: teamLabel });
      showToast("info", `Building ${teamLabel} (${i + 1} of ${list.length})…`, "Working on it");
      try {
        setTeamName(teamLabel);
        const inputs = buildFinalInputs({
          teamRoster: team.members,
          problemOverride: problem,
          teamNameOverride: teamLabel,
        });
        setLastGenInputs(inputs);
        const apiRes = await generateAllPlans(inputs);
        let plansForTeam = apiRes?.plans || {};
        if (plansForTeam.daily) {
          try {
            plansForTeam = await alignAndPersistGeneratedPlans(
              plansForTeam,
              {
                memberName: teamLabel,
                name: teamLabel,
              },
              {
                reason: `Team schedule for ${teamLabel}`,
                trigger: "batch_team",
                changedBy: "AI",
              }
            );
          } catch (_) {
            plansForTeam = {
              ...plansForTeam,
              homework: homeworkFromDaily(plansForTeam.daily) || plansForTeam.homework,
            };
          }
        }

        let linkedId = "";
        let schedule = null;
        try {
          const created = await quickCreateProblemStatement(
            `${teamLabel}: ${problem.slice(0, 70).replace(/\s+/g, " ")}`,
            problem,
            { allowDuplicate: true }
          );
          linkedId = created?.data?._id || "";
          if (linkedId) {
            setLinkedPsId(linkedId);
            if (created.data) setPsOptions((p) => [created.data, ...p]);
          }
        } catch (_) {}

        const dailyRows = plansForTeam?.daily?.rows;
        if (linkedId && Array.isArray(dailyRows) && dailyRows.length > 0 && team.members.length >= 2) {
          schedule = await runAutoTeamSchedule(dailyRows, linkedId, {
            members: team.members,
            teamName: teamLabel,
            quiet: true,
          });
        }

        const entry = {
          key: team.key,
          teamName: teamLabel,
          problemStatement: problem,
          members: team.members,
          plans: plansForTeam,
          teamSchedule: schedule,
          linkedPsId: linkedId,
          error: null,
        };
        results.push(entry);
        applyTeamBatchResult(entry);
      } catch (e) {
        results.push({
          key: team.key,
          teamName: teamLabel,
          problemStatement: problem,
          members: team.members,
          plans: null,
          teamSchedule: null,
          linkedPsId: "",
          error: e?.response?.data?.error || e.message || "Generate failed",
        });
        showToast("error", `We could not finish ${teamLabel}. Please try again.`, "Please try again");
      }
      setBatchTeamPlans([...results]);
    }

    const ok = results.filter((r) => r.plans?.daily?.rows?.length);
    setBatchTeamPlans(results);
    if (ok.length) {
      applyTeamBatchResult(ok[ok.length - 1]);
      showToast(
        "success",
        `${ok.length} of ${list.length} team plans are ready. Tap a team name to switch — each stays on its own problem.`,
        "Team plans ready"
      );
    } else {
      showToast("error", "We could not build those team plans. Please try again.", "Please try again");
    }
    setBatchProgress(null);
    setLoading({});
    setGenerating(false);
  };

  /** Manually publish current plans into DB + GitHub (daily, weekly, homework, versions). */
  const handleSaveToRepository = async () => {
    const currentPlans = plansRef.current || {};
    const currentDaily = currentPlans.daily;
    if (!currentDaily?.rows?.length) {
      showToast("warning", "Create your daily plan first, then save it.", "Nothing to save yet");
      return;
    }
    const compactDaily = {
      ...currentDaily,
      rows: currentDaily.rows.map((row) => [row[0], row[1], row[2], removeFeedsNext(row[3]), ...row.slice(4)]),
    };
    if (compactDaily.rows.some((row, index) => row[3] !== currentDaily.rows[index][3])) {
      const compactPlans = { ...currentPlans, daily: compactDaily };
      plansRef.current = compactPlans;
      setPlans(compactPlans);
    }
    if (savingSchedule) return;
    if (!localStorage.getItem("token")) {
      showToast("warning", "Sign in to save your schedule.", "Sign in required");
      return;
    }
    setSavingSchedule(true);
    try {
      const inputs = Object.keys(lastGenInputs || {}).length
        ? { ...lastGenInputs, _repoMode: repoMode }
        : buildFinalInputs();
      const member = resolveVersionMember();
      const memberName =
        member?.memberName ||
        (teamMembers || []).find((m) => m?.name?.trim())?.name?.trim() ||
        user?.name ||
        "";

      // Prefer dedicated homework plan; else extract from daily rows
      // Daily is the source of truth: derive tonight's work from its current
      // rows so saved Homework / LeetCode cannot lag behind an edited schedule.
      const homework = homeworkPlanFromDaily(compactDaily);
      const weekly = currentPlans.weekly?.rows?.length
        ? currentPlans.weekly
        : buildWeeklyFromDaily(compactDaily, currentPlans.weekly) || null;

      showToast("info", "Saving the latest daily, weekly, and homework plans…", "Saving schedule");
      // Save the complete archive first. A failure in one of the secondary
      // per-module sync endpoints must not prevent the DB/GitHub archive write.
      const res = await saveScheduleRepo({
        inputs,
        plan: compactDaily,
        weekly,
        homework,
        title: memberName || compactDaily.title || "",
        memberName,
        memberKey: member?.memberKey || undefined,
      });
      if (res?.success) {
        const syncResults = await Promise.allSettled([
          setBasePlan(compactDaily),
          syncDailyPlan(compactDaily),
          homework?.rows?.length ? syncHomeworkPlan(compactDaily, { source: "save-schedule" }) : Promise.resolve(null),
          weekly?.rows?.length ? syncWeeklyPlan(weekly) : Promise.resolve(null),
        ]);
        const syncFailures = syncResults.filter((result) => result.status === "rejected").length;
        const gh = res.github;
        if (gh?.ok) {
          const location = gh.htmlUrl || gh.path || "the configured schedule repository";
          const indexIssue = gh.indexError ? ` The archive file was saved, but the GitHub index update failed: ${gh.indexError}` : "";
          const syncIssue = syncFailures ? ` ${syncFailures} account plan sync${syncFailures === 1 ? "" : "s"} need attention.` : "";
          showToast(indexIssue || syncIssue ? "warning" : "success", `Saved to your account and GitHub (${location}).${indexIssue}${syncIssue}`, indexIssue ? "GitHub index needs attention" : syncIssue ? "Saved with sync warning" : "Saved");
        } else {
          const reason = gh?.reason || gh?.error || "GitHub did not confirm the archive write.";
          showToast("warning", `The schedule is saved in your account database, but GitHub was not updated: ${reason}`, "Saved to account only");
        }
        setRepoMatch((prev) => ({
          ...(prev || {}),
          success: true,
          hasMatch: true,
          recommendation: res.entry
            ? {
                ...res.entry,
                matchTag: "saved",
                reason: "Just saved from your Plans tab",
              }
            : prev?.recommendation || null,
          match: res.entry || prev?.match || null,
        }));
        if (res.entry?.id) setRepoEntryId(String(res.entry.id));
      } else {
        showToast("error", res?.error || "The server did not confirm that the schedule was saved.", "Save failed");
      }
    } catch (e) {
      showToast("error", formatApiError(e, "Could not save the latest schedule. Check the server connection and try again."), "Save failed");
    } finally {
      setSavingSchedule(false);
    }
  };

  const donePlans = Object.values(plans).filter(Boolean).length;

  // ── Auth gate ─────────────────────────────────────────────────────────────────
  // App <Protected> already redirects unauthenticated users; avoid rendering
  // undefined Login/Signup components (was causing a blank page).
  if (!user) {
    return null;
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", pb: 6 }}>

      {/* Sticky: Inputs | Plans + step numbers (content below scrolls) */}
      <PlannerStickyHeader
        view={step}
        onViewChange={goToView}
        planCount={donePlans}
        stepper={
          step === 0 ? (
            <WizardStepper
              embedded
              pages={buildWizardPages()}
              pageId={inputPage}
              onSelect={goToInputPage}
            />
          ) : null
        }
      />

          {/* ── View: Inputs (form cards, same surface as plan cards) ── */}
          {step === 0 && (() => {
              const wizardPages = buildWizardPages();
              let pageIdx = wizardPages.findIndex((p) => p.id === inputPage);
              if (pageIdx < 0) pageIdx = 0;
              const page = wizardPages[pageIdx] || wizardPages[0];
              const goNext = () => {
                const next = wizardPages[pageIdx + 1];
                if (next) goToInputPage(next.id);
              };
              const goBack = () => {
                const prev = wizardPages[pageIdx - 1];
                if (prev) goToInputPage(prev.id);
              };
              const findParam = (key) =>
                [...SHARED_PARAMS, ...DSA_SD_PARAMS, ...SUBJECT_PARAMS, ...STUDENT_PARAMS].find((p) => p.key === key);

              const requiredIssues = getRequiredIssues({
                hasProblem,
                isMultiTeam,
                memberNames: (teamMembers || []).map((m) => m.name),
                teamOn: mergeEnabledModules(enabledModules).team !== false,
                timingOn: mergeEnabledModules(enabledModules).timing !== false,
                numDays,
                dailyStart,
                dailyEnd,
              });
              const activityIssues = missingActivityDetails();
              activityIssues.forEach((detail, index) => requiredIssues.push({
                id: `activity_${index + 1}`,
                page: "dayActivities",
                label: "Activity task/content",
                ok: false,
                detail,
              }));
              let nextDisabledReason = "";
              let canNext = true;
              const basicsMissing = missingOnPage(requiredIssues, "basics");
              if (page?.id === "basics" && basicsMissing.length) {
                canNext = false;
                nextDisabledReason = `Fill required: ${basicsMissing.map((i) => i.label).join(", ")}`;
              }
              if (page?.id === "problem" && !hasProblem) {
                canNext = false;
                nextDisabledReason = isMultiTeam
                  ? "Map a problem statement to every team to continue"
                  : "Enter your problem statement to continue";
              }

              const toggleModule = (key, val) => {
                setEnabledModules((prev) => ({ ...prev, [key]: val }));
              };

              const renderParamPage = (paramKey) => {
                const param = findParam(paramKey);
                if (!param) return null;
                return (
                  <InputParamCard
                    param={{ ...param, label: "", hint: "" }}
                    textInputs={textInputs}
                    setTextInputs={setTextInputs}
                    files={files}
                    setFiles={setFiles}
                    forceOpen
                    numDays={numDays}
                    onProblemVerified={paramKey === "problem" ? () => setProblemVerified(true) : undefined}
                  />
                );
              };

              const pageIncluded = (key) => mergeEnabledModules(enabledModules)[key] !== false;

              const makeIncludeToggle = (p) => {
                if (!p?.toggleKey && p?.id !== "problem") return null;
                if (p.id === "problem") {
                  return <PageIncludeToggle label="Problem statement" enabled required />;
                }
                const meta = FEATURE_TOGGLES.find((t) => t.key === p.toggleKey);
                return (
                  <PageIncludeToggle
                    label={meta?.label || p.short || p.title}
                    enabled={pageIncluded(p.toggleKey)}
                    onChange={(v) => toggleModule(p.toggleKey, v)}
                  />
                );
              };

              const offNotice = (label) => (
                <Alert severity="info" variant="outlined">
                  <strong>{label}</strong> is turned OFF. Turn Include ON to fill this input, or continue.
                </Alert>
              );

              return (
            <WizardLayout>
              {/* Step list lives in app Sidebar under Schedule Planner; sticky header keeps 1…N rail */}

              {page?.id === "basics" && (
                <WizardPageShell page={page}>
                  <RequiredChecklist
                    issues={requiredIssues.filter((i) => i.page === "basics")}
                    onGo={goToInputPage}
                  />
                  <FormSection
                    title="Members"
                    icon="ti-users"
                    tone="navy"
                    action={
                      <PageIncludeToggle
                        compact
                        label="Members"
                        enabled={pageIncluded("team")}
                        onChange={(v) => toggleModule("team", v)}
                      />
                    }
                    muted={!pageIncluded("team")}
                  >
                    <StudentProfile
                      embedded
                      members={teamMembers}
                      onChange={setTeamMembers}
                      soloNotice={!isTeamMode}
                      generateBtnLabel="Generate 3P DT Playbook Plan"
                      showTeamGoals={isTeamMode}
                      assessment={textInputs.assessment || ""}
                      onAssessmentChange={(v) => setTextInputs((prev) => ({ ...prev, assessment: v }))}
                      files={files}
                      setFiles={setFiles}
                      teamName={teamName}
                      onTeamNameChange={setTeamName}
                      showToast={showToast}
                      rosterMode={rosterMode}
                      onRosterImported={(parsed) => {
                        const mode = parsed?.mode === "individuals" ? "individuals" : "team";
                        setRosterMode(mode);
                        const teams = parsed?.teams || groupRosterTeams(parsed?.members || []);
                        if (teams[0]?.key) setActiveTeamKey(teams[0].key);
                        const firstPs = teams.find((t) => String(t.problemStatement || "").trim())?.problemStatement;
                        if (firstPs) {
                          setTextInputs((prev) => ({ ...prev, problem: firstPs }));
                          setProblemVerified(false);
                        }
                        setBatchTeamPlans([]);
                        setTeamProblemVerified({});
                        if (mode === "individuals") {
                          setEnabledModules((prev) => ({
                            ...prev,
                            team: false,
                            assessment: true,
                            studentProfile: true,
                          }));
                          setBatchIndividualPlans([]);
                        } else if ((parsed?.members || []).length >= 2) {
                          setEnabledModules((prev) => ({
                            ...prev,
                            team: true,
                            assessment: true,
                            studentProfile: true,
                          }));
                        }
                      }}
                    />
                  </FormSection>

                  <FormSection
                    title="Timing"
                    icon="ti-clock"
                    tone="teal"
                    action={
                      <PageIncludeToggle
                        compact
                        label="Timing"
                        enabled={pageIncluded("timing")}
                        onChange={(v) => toggleModule("timing", v)}
                      />
                    }
                    muted={!pageIncluded("timing")}
                  >
                    <DailyTiming
                      embedded
                      numDays={numDays}
                      startTime={dailyStart}
                      endTime={dailyEnd}
                      lunchStart={lunchStart}
                      lunchEnd={lunchEnd}
                      breaks={breaks}
                      onDaysChange={setNumDays}
                      onStartChange={setDailyStart}
                      onEndChange={setDailyEnd}
                      onLunchStartChange={setLunchStart}
                      onLunchEndChange={setLunchEnd}
                      onBreaksChange={setBreaks}
                    />
                  </FormSection>

                  <FormSection
                    title="Calendar & exams"
                    icon="ti-calendar"
                    tone="slate"
                    action={
                      <PageIncludeToggle
                        compact
                        label="Calendar"
                        enabled={pageIncluded("calendar")}
                        onChange={(v) => toggleModule("calendar", v)}
                      />
                    }
                    muted={!pageIncluded("calendar")}
                  >
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 12,
                      marginBottom: 14,
                      alignItems: "end",
                    }}>
                      <FancyInput
                        tone="amber"
                        icon="ti-calendar-event"
                        label="Course start"
                        type="date"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); if (!e.target.value) setCalendarData(null); }}
                      />
                      <FancyInput
                        tone="coral"
                        icon="ti-calendar-due"
                        label="Course end"
                        type="date"
                        min={startDate || undefined}
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); if (!e.target.value) setCalendarData(null); }}
                      />
                      <div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <Btn small onClick={handleBuildCalendar} disabled={calLoading || !startDate || !endDate}>
                            {calLoading ? "Building…" : "Build calendar"}
                          </Btn>
                          <Btn
                            small
                            variant="outline"
                            onClick={handleClearCalendar}
                            disabled={calLoading || (!startDate && !endDate && !calendarData && exams.every((e) => !e.startDate))}
                          >
                            Clear
                          </Btn>
                          {calendarData && (
                            <span style={{ fontSize: 12, color: "#0F9F6E", fontWeight: 650 }}>Ready</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {calendarData && (
                      <CalendarPlanPreview data={calendarData} startDate={startDate} endDate={endDate} />
                    )}

                    <p style={{ fontSize: 12.5, fontWeight: 700, color: "#1A2333", margin: "14px 0 8px" }}>Exam windows</p>
                    {exams.map((ex, i) => (
                      <div key={i} style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(90px, 1fr) 1fr 1fr auto",
                        gap: 10,
                        marginBottom: 10,
                        alignItems: "start",
                      }}>
                        <FancyInput
                          tone="slate"
                          icon="ti-file-certificate"
                          label="Exam"
                          type="text"
                          value={ex.name}
                          onChange={(e) => updateExam(i, "name", e.target.value)}
                          placeholder="IA1 / AU"
                        />
                        <FancyInput
                          tone="blue"
                          icon="ti-calendar-event"
                          label="Starts"
                          type="date"
                          value={ex.startDate}
                          onChange={(e) => updateExam(i, "startDate", e.target.value)}
                        />
                        <FancyInput
                          tone="amber"
                          icon="ti-calendar-due"
                          label="Ends"
                          type="date"
                          value={ex.endDate}
                          onChange={(e) => updateExam(i, "endDate", e.target.value)}
                        />
                        <button type="button" onClick={() => removeExam(i)} style={{
                          border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626", cursor: "pointer",
                          width: 40, height: 40, borderRadius: 10, marginTop: 26,
                        }}>
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    ))}
                    {canAddExam && (
                      <Btn small variant="ghost" onClick={addExam}>
                        <i className="ti ti-plus" /> Add exam
                      </Btn>
                    )}
                  </FormSection>
                </WizardPageShell>
              )}

              {page?.id === "problem" && (
                <WizardPageShell
                  page={page}
                  includeToggle={makeIncludeToggle(page)}
                  badge={problemVerifiedAll && hasProblem ? (
                    <span style={{ fontSize: 11, fontWeight: 650, color: "#0F9F6E", background: "#E6F8F1", padding: "2px 7px", borderRadius: 4 }}>
                      {isMultiTeam ? "All teams verified" : "Verified"}
                    </span>
                  ) : null}
                >
                  <RequiredChecklist
                    title="Required: problem statement"
                    issues={requiredIssues.filter((i) => i.page === "problem")}
                  />
                  {isMultiTeam ? (
                    <>
                      <TeamProblemMap
                        teams={rosterTeams.map((t) => ({
                          ...t,
                          verified: !!teamProblemVerified[t.key],
                        }))}
                        activeKey={focusTeamKey}
                        onSelect={selectActiveTeam}
                      />
                      <FancyTextarea
                        tone="ink"
                        label="Problem statement"
                        hint={focusTeam?.name || ""}
                        value={focusTeam?.problemStatement || textInputs.problem || ""}
                        onChange={(e) => applyProblemToTeam(focusTeamKey, e.target.value)}
                        placeholder="Paste the statement, or generate one below"
                        style={{ minHeight: 140 }}
                      />
                      <ProblemDuplicateWatcher
                        text={focusTeam?.problemStatement || textInputs.problem || ""}
                        onChooseDifferent={() => applyProblemToTeam(focusTeamKey, "")}
                      />
                      <ProblemFrameworkPanel
                        key={focusTeamKey || "team-problem"}
                        teamLabel={focusTeam?.name || ""}
                        problemText={focusTeam?.problemStatement || textInputs.problem || ""}
                        files={files}
                        numDays={numDays}
                        setTextInputs={(fn) => {
                          setTextInputs((prev) => {
                            const next = typeof fn === "function" ? fn(prev) : { ...prev, ...fn };
                            if (focusTeamKey && next.problem !== prev.problem) {
                              queueMicrotask(() => applyProblemToTeam(focusTeamKey, next.problem));
                            }
                            return next;
                          });
                        }}
                        onApplyStatement={(statement) => applyProblemToTeam(focusTeamKey, statement)}
                        onVerified={() => markTeamVerified(focusTeamKey)}
                      />
                    </>
                  ) : (
                    <>
                      {renderParamPage("problem")}
                      <ProblemDuplicateWatcher
                        text={textInputs.problem || ""}
                        onChooseDifferent={() => {
                          setTextInputs((prev) => ({ ...prev, problem: "" }));
                          setProblemVerified(false);
                        }}
                      />
                    </>
                  )}
                  {!isMultiTeam && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    <Btn
                      small
                      variant={problemVerified ? "primary" : "outline"}
                      disabled={!hasProblem}
                      onClick={() => setProblemVerified(true)}
                    >
                      {problemVerified ? "Verified" : "Mark verified"}
                    </Btn>
                  </div>
                  )}
                </WizardPageShell>
              )}

              {page?.id === "dtStage" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("dtStage") ? (
                    <>
                      <DTPlaybookFlow activeStage={activeDTStage} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                        {DT_STAGES.map((stage) => {
                          const active = activeDTStage === stage.key;
                          return (
                            <button
                              key={stage.key}
                              type="button"
                              onClick={() => setActiveDTStage(stage.key)}
                              style={{
                                padding: "8px 14px",
                                borderRadius: 10,
                                border: `1.5px solid ${active ? stage.color : C.border}`,
                                background: active ? (stage.soft || `${stage.color}14`) : "#fff",
                                color: active ? stage.color : C.muted,
                                cursor: "pointer",
                                fontSize: 12.5,
                                fontWeight: 650,
                                boxShadow: active ? `inset 3px 0 0 ${stage.color}` : "none",
                              }}
                            >
                              <i className={`ti ${stage.icon}`} style={{ marginRight: 5 }} />
                              {stage.label}
                            </button>
                          );
                        })}
                      </div>
                      <ProductStagePlanPanel
                        plan={productStagePlan}
                        onChange={setProductStagePlan}
                        numDays={numDays}
                        startStage={activeDTStage}
                      />
                    </>
                  ) : offNotice("DT stage")}
                </WizardPageShell>
              )}

              {page?.id === "dsa" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("dsa") ? (
                    <>
                      <LevelSelectionCards title="DSA level" value={dsaLevel} onChange={setDsaLevel} />
                      {renderParamPage("dsaSyllabus")}
                    </>
                  ) : offNotice("DSA syllabus")}
                </WizardPageShell>
              )}
              {page?.id === "systemDesign" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("systemDesign") ? (
                    <>
                      <LevelSelectionCards title="System Design level" value={systemDesignLevel} onChange={setSystemDesignLevel} />
                      {renderParamPage("systemDesign")}
                    </>
                  ) : offNotice("System design")}
                </WizardPageShell>
              )}
              {page?.id === "subjects" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("subjects") ? (
                    <>
                      {syllabusAutoMeta?.count ? (
                        <div style={{
                          marginBottom: 14,
                          padding: "12px 14px",
                          borderRadius: 10,
                          background: "#F8FAFC",
                          border: `1px solid ${C.border}`,
                          fontSize: 13,
                          color: C.text,
                          lineHeight: 1.45,
                        }}>
                          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{
                              width: 28, height: 28, borderRadius: 8, background: "#fff",
                              border: `1px solid ${C.border}`, display: "inline-flex",
                              alignItems: "center", justifyContent: "center",
                            }}>
                              <i className="ti ti-school" style={{ fontSize: 15, color: C.primary }} />
                            </span>
                            {syllabusAutoMeta.department || "Dept"} · Sem {syllabusAutoMeta.semester} · {syllabusAutoMeta.count} subjects loaded
                          </div>
                          <div style={{ marginTop: 6, color: C.muted, fontWeight: 600, fontSize: 12 }}>
                            Toggle <strong>In schedule / Skipped</strong> so Generate uses only the subjects you keep. Focus / Light changes coverage. Concepts (must-learn / omit) apply on the next Generate.
                          </div>
                        </div>
                      ) : null}
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {(isTeamMode
                          ? teamMembers.filter((m) => m.name?.trim())
                          : teamMembers.slice(0, 1)
                        ).map((m, idx) => {
                          const realIdx = isTeamMode
                            ? teamMembers.findIndex((x) => x === m)
                            : 0;
                          const i = realIdx >= 0 ? realIdx : idx;
                          return (
                            <SubjectPreferencesPanel
                              key={`subj-pref-${i}`}
                              title={
                                isTeamMode
                                  ? `${m.name || "Member"}'s subjects`
                                  : "Your subjects"
                              }
                              preferences={
                                (m.subjectPreferences && m.subjectPreferences.length)
                                  ? m.subjectPreferences
                                  : mergeSubjectPreferences(
                                      parseSubjectsFromSyllabusText(textInputs.syllabus),
                                      []
                                    )
                              }
                              onChange={(prefs) => {
                                setTeamMembers((prev) =>
                                  prev.map((row, j) =>
                                    j === i ? { ...row, subjectPreferences: prefs } : row
                                  )
                                );
                              }}
                            />
                          );
                        })}
                      </div>
                    </>
                  ) : offNotice("Subjects")}
                </WizardPageShell>
              )}
              {page?.id === "questions" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("questions") ? renderParamPage("questions") : offNotice("Question banks")}
                </WizardPageShell>
              )}

              {page?.id === "assessment" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("assessment") || pageIncluded("studentProfile") ? (
                    <>
                      {pageIncluded("assessment") && !isTeamMode && (
                        <FormSection title="Assessment & goals" icon="ti-flag" tone="ink">
                          {renderParamPage("assessment")}
                        </FormSection>
                      )}
                      <div style={{
                        margin: "4px 0 14px",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: `1px solid ${C.border}`,
                        background: C.surfaceAlt,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                            Student persona
                          </div>
                          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                            Add real learner details and rate the selected items. The plan uses year and pace to set challenge level, interests to make examples and build quests more engaging, and dislikes to avoid unsuitable formats. Missing details are left out, not guessed.
                          </div>
                        </div>
                        <PageIncludeToggle
                          compact
                          label="Profile"
                          enabled={pageIncluded("studentProfile")}
                          onChange={(v) => toggleModule("studentProfile", v)}
                        />
                      </div>
                      {pageIncluded("studentProfile") && !isTeamMode && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {[
                            {
                              key: "capability",
                              title: "1. Strengths (capability)",
                              icon: "ti-user-check",
                              tone: "navy",
                              kind: "capability",
                              hint: "What you are already good at — soft skills, tech, work style.",
                              placeholder: "Type freely, e.g. Strong in Python & explaining concepts to peers…",
                            },
                            {
                              key: "difficulties",
                              title: "2. Difficulties",
                              icon: "ti-alert-triangle",
                              tone: "slate",
                              kind: "difficulties",
                              hint: "Add “Tamil medium” or “difficulty in English” here if needed — you get Tamil YouTube + English websites (varied each day).",
                              placeholder: "Type freely, e.g. Tamil medium; finds English lectures hard; stuck on recursion…",
                            },
                            {
                              key: "interest",
                              title: "3. Likes & activities",
                              icon: "ti-heart",
                              tone: "coral",
                              kind: "interest",
                              hint: "Add genuine hobbies or interests. The plan can use them for examples, quest themes, or an optional product feature while keeping the project goal unchanged.",
                              placeholder: "Type freely, e.g. Love cricket & guitar; enjoy short film editing…",
                            },
                            {
                              key: "dislikes",
                              title: "4. Dislikes / avoid",
                              icon: "ti-thumb-down",
                              tone: "ink",
                              kind: "dislikes",
                              hint: "Respected in Learning (e.g. skip long videos). Mini Build adds a tiny growth challenge so you still build future-ready skills.",
                              placeholder: "Type freely, e.g. Don’t like long videos; stage fear; long theory…",
                            },
                            {
                              key: "domain",
                              title: "5. Domain / direction",
                              icon: "ti-world",
                              tone: "teal",
                              kind: "domain",
                              hint: "Industry, tech focus, or role path — not hobbies.",
                              placeholder: "Type freely, e.g. Aim FinTech + full-stack; curious about AI agents…",
                            },
                          ].map((f) => (
                            <FormSection key={f.key} title={f.title} icon={f.icon} tone={f.tone}>
                              <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px" }}>{f.hint}</p>
                              <FancyTextarea
                                  tone={f.tone}
                                  value={textInputs[f.key] || ""}
                                  onChange={(e) =>
                                    setTextInputs((p) => applyFieldWithRatings(p, f.key, e.target.value))
                                  }
                                  placeholder={f.placeholder}
                                  rows={3}
                                  style={{ minHeight: 72 }}
                                />
                              <ProfileSuggestionChips
                                kind={f.kind}
                                value={textInputs[f.key] || ""}
                                onChange={(v) =>
                                  setTextInputs((p) => applyFieldWithRatings(p, f.key, v))
                                }
                              />
                              <SkillRatingsEditor
                                kind={f.kind}
                                skills={textInputs[f.key] || ""}
                                ratings={normalizeItemRatings(textInputs)[f.kind]}
                                onChange={(r) => setTextInputs((p) => applyKindRatings(p, f.kind, r))}
                              />
                            </FormSection>
                          ))}
                        </div>
                      )}
                      {isTeamMode && (
                        <p style={{ fontSize: 13, color: "#6B7285" }}>
                          Team assessment lives under Basics → Members: open a member, then Assessment & persona (one tab at a time).
                        </p>
                      )}
                    </>
                  ) : offNotice("Assessment")}
                </WizardPageShell>
              )}

              {page?.id === "banks" && (
                <WizardPageShell page={page} includeToggle={makeIncludeToggle(page)}>
                  {pageIncluded("assessmentBanks") ? (
                    <AssessmentContentPanel
                      config={assessmentConfig}
                      onChange={setAssessmentConfig}
                      files={files}
                      setFiles={setFiles}
                    />
                  ) : offNotice("Practice banks")}
                </WizardPageShell>
              )}

              {page?.id === "dayActivities" && (
                <WizardPageShell page={page}>
                  <FormSection title="Activities — add, edit, delete" icon="ti-layout-grid" tone="blue">
                    <DayActivityEditor
                      value={slotOverrides}
                      onChange={setSlotOverrides}
                      dayGroups={dayActivityGroups}
                      onDayGroupsChange={setDayActivityGroups}
                      timing={{
                        dailyStart,
                        dailyEnd,
                        lunchStart,
                        lunchEnd,
                        breaks,
                        startDate,
                        endDate,
                        numDays,
                      }}
                    />
                  </FormSection>
                </WizardPageShell>
              )}

              {page?.id === "mustApply" && (
                <WizardPageShell page={page}>
                  <FormSection title="Must apply" icon="ti-alert-circle" tone="ink">
                    <p style={{ fontSize: 13, color: C.muted, margin: "0 0 12px", lineHeight: 1.5 }}>
                      Optional. Add a clear instruction you want included in the plan. Say which activity or day it belongs to, what the learner should do, and what they should finish or submit. The AI will place it in the relevant task/content and keep it out of unrelated activities. If the request is unclear, it will make a safe assumption or ask one short question in the plan.
                    </p>
                    <FancyTextarea
                      tone="ink"
                      label="Your rule"
                      value={textInputs.manualPrompt || ""}
                      onChange={(e) => setTextInputs((p) => ({ ...p, manualPrompt: e.target.value }))}
                      placeholder="Example: In Coding Practice each day, solve one beginner array problem and apply the idea to the product search feature. Submit the accepted solution and a short note."
                      style={{ minHeight: 140 }}
                    />
                    <div style={{ marginTop: 12 }}>
                      <UploadZone
                        paramKey="manualPrompt"
                        files={files}
                        setFiles={setFiles}
                        hint="Optional files. Extracted text is applied only if you attach a file here."
                      />
                    </div>
                  </FormSection>
                </WizardPageShell>
              )}

              {page?.id === "generate" && (
                <WizardPageShell page={page}>
                  <PlanReviewPanel
                    memberCount={(teamMembers || []).filter((m) => m.name?.trim()).length || 1}
                    numDays={numDays}
                    dailyStart={dailyStart}
                    dailyEnd={dailyEnd}
                    lunchStart={lunchStart}
                    lunchEnd={lunchEnd}
                    breaks={breaks}
                    startDate={startDate}
                    endDate={endDate}
                    calendarReady={!!calendarData}
                    calendarOn={pageIncluded("calendar")}
                    timingOn={pageIncluded("timing")}
                    teamOn={pageIncluded("team")}
                    dtStageOn={pageIncluded("dtStage")}
                    activeDTStage={activeDTStage}
                    hasProblem={hasProblem}
                    requiredIssues={requiredIssues}
                    problemVerified={problemVerifiedAll}
                    problemText={textInputs.problem || ""}
                    teamProblemMap={isMultiTeam ? rosterTeams.map((t) => ({
                      ...t,
                      verified: !!teamProblemVerified[t.key],
                    })) : []}
                    enabledModules={enabledModules}
                    onToggleModule={toggleModule}
                    hasDsa={!!(textInputs.dsaSyllabus || (files.dsaSyllabus || []).length)}
                    hasSystemDesign={!!(textInputs.systemDesign || (files.systemDesign || []).length)}
                    hasSubjects={hasSubjectContent}
                    hasQuestions={!!(textInputs.questions || (files.questions || []).length)}
                    generating={generating}
                    canGenerate={hasAnyInput && !generating && requiredIssues.every((i) => i.ok)}
                    onGenerate={handleGenerateAndCapture}
                    onJumpToProblem={() => goToInputPage("problem")}
                    repoMode={repoMode}
                    onRepoModeChange={setRepoMode}
                    repoMatch={repoMatch}
                    repoEntryId={repoEntryId}
                    onRepoEntryChange={setRepoEntryId}
                    repoRegenParts={repoRegenParts}
                    onRepoRegenPartsChange={setRepoRegenParts}
                    repoAdaptNote={repoAdaptNote}
                    onRepoAdaptNoteChange={setRepoAdaptNote}
                    repoCampus={teamMembers?.[0]?.college || ""}
                    repoDepartment={teamMembers?.[0]?.department || ""}
                    repoYear={
                      teamMembers?.[0]?.collegeYear != null
                        ? String(teamMembers[0].collegeYear)
                        : teamMembers?.[0]?.schoolGrade != null
                          ? String(teamMembers[0].schoolGrade)
                          : ""
                    }
                    mustApplyText={textInputs.manualPrompt || ""}
                  />
                </WizardPageShell>
              )}

              <WizardFooter
                canBack={pageIdx > 0}
                canNext={page?.id !== "generate" && canNext && pageIdx < wizardPages.length - 1}
                onBack={goBack}
                onNext={() => {
                  if (page?.id === "problem" && hasProblem) setProblemVerified(true);
                  goNext();
                }}
                nextDisabledReason={page?.id === "generate" ? "" : nextDisabledReason}
                nextLabel={
                  page?.id === "basics" ? "Next: Problem"
                    : page?.id === "dayActivities" ? "Next: Must apply"
                    : page?.id === "mustApply" ? "Next: Review"
                    : pageIdx >= wizardPages.length - 3 ? "Next: Activities"
                    : "Next"
                }
                showSkip={!!page?.optional || (page?.toggleKey && !pageIncluded(page.toggleKey))}
                onSkip={goNext}
              />
            </WizardLayout>
              );
          })()}


          {/* ── View: Plans (daily / weekly schedule — different from Inputs forms) ── */}
          {step === 1 && (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderColor: C.border,
                  borderRadius: `${LAYOUT.radius}px`,
                  bgcolor: C.surface,
                  borderTop: `4px solid ${C.dtPurple || "#7C3AED"}`,
                  boxShadow: "0 1px 2px rgba(10, 14, 23, 0.04), 0 8px 24px rgba(10, 14, 23, 0.04)",
                }}
              >
                <Typography variant="subtitle2" fontWeight={750} sx={{ mb: 1, fontSize: 13 }}>
                  Daily · Weekly · Homework
                </Typography>
                {versionPreview?.memberName && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: `1.5px solid ${C.primary}55`,
                      background: C.primarySoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Viewing from Version Management
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 850, color: C.text, marginTop: 2, letterSpacing: "-0.02em" }}>
                        {versionPreview.memberName}
                        {versionPreview.memberVersion
                          ? <span style={{ fontSize: 14, fontWeight: 750, color: C.primary, marginLeft: 8 }}>V{versionPreview.memberVersion}</span>
                          : null}
                      </div>
                    </div>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setVersionPreview(null);
                        try { localStorage.removeItem("ap_version_preview"); } catch (_) {}
                      }}
                      sx={{ borderColor: C.border, fontWeight: 750 }}
                    >
                      Clear name banner
                    </Button>
                  </div>
                )}
                {(generating || loading.daily || loading.homework) && (
                  <div style={{
                    marginBottom: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${C.primary}44`,
                    background: C.primarySoft,
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.primaryDark,
                  }}>
                    {batchProgress
                      ? `Generating ${batchProgress.name} (${batchProgress.index}/${batchProgress.total})… Keeping previous plan until this one is synced.`
                      : plans?.daily?.rows?.length
                        ? "Generating new schedule… Showing your previous plan until homework sync finishes."
                        : "Generating schedule… Daily, weekly, and homework will appear together once synced."}
                  </div>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  {MODULES.map((m) => {
                    const isActive = activeTab === m.key;
                    // Homework is derived from daily — show check when daily exists
                    const isDone   = m.key === "homework" ? !!(plans.homework || plans.daily) : !!plans[m.key];
                    const isLoad   = m.key === "homework" ? !!(loading.daily || loading.homework) : !!loading[m.key];
                    return (
                      <Button
                        key={m.key}
                        size="small"
                        variant={isActive ? "contained" : "outlined"}
                        color={isActive ? "primary" : "inherit"}
                        onClick={() => setActiveTab(m.key)}
                        startIcon={<i className={`ti ${m.icon}`} style={{ fontSize: 15 }} />}
                        endIcon={isDone && !isLoad ? <i className="ti ti-check" style={{ fontSize: 13 }} /> : null}
                        sx={{ borderColor: C.border }}
                      >
                        {m.label}
                        {isLoad ? "…" : ""}
                      </Button>
                    );
                  })}
                  <Box sx={{ flex: 1 }} />
                  {activeTab !== "homework" && (
                    <>
                      <Button size="small" variant="text" onClick={() => handlePolish(activeTab)} disabled={!!loading[activeTab] || !plans[activeTab]}>
                        Polish
                      </Button>
                      <Button size="small" variant="text" onClick={() => handleRegenerate(activeTab)} disabled={!!loading[activeTab]}>
                        Redo this
                      </Button>
                    </>
                  )}
                  <Button size="small" variant="outlined" onClick={handleGenerateAndCapture} disabled={generating || !hasAnyInput}>
                    Redo all
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={handleSaveToRepository}
                    disabled={!plans?.daily?.rows?.length || generating || savingSchedule}
                    startIcon={<i className={`ti ${savingSchedule ? "ti-loader-2" : "ti-database"}`} style={{ fontSize: 14 }} />}
                    title="Save your daily, weekly, and homework plans"
                    sx={{ fontWeight: 800 }}
                  >
                    {savingSchedule ? "Saving latest plan…" : "Save my schedule"}
                  </Button>
                </Stack>
              </Paper>

              {isMultiTeam && (batchTeamPlans.length > 0 || batchProgress) && rosterMode !== "individuals" && (
                <div style={{
                  marginBottom: 14, padding: "12px 14px", borderRadius: 12,
                  border: `1px solid ${C.primary}33`, background: C.primarySoft,
                }}>
                  {batchProgress ? (
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.primaryDark }}>
                      Team plans: {batchProgress.index}/{batchProgress.total} — {batchProgress.name}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.muted, mb: 1, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Team plans ({batchTeamPlans.filter((b) => b.plans?.daily).length}/{batchTeamPlans.length}) — each team keeps its own problem
                    </Typography>
                  )}
                  {batchTeamPlans.length > 0 && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                      {batchTeamPlans.map((b) => {
                        const active = activeTeamKey === b.key;
                        const ok = !!b.plans?.daily?.rows?.length;
                        return (
                          <button
                            key={b.key}
                            type="button"
                            disabled={!ok}
                            title={b.problemStatement || ""}
                            onClick={() => {
                              if (!ok) return;
                              applyTeamBatchResult(b);
                            }}
                            style={{
                              padding: "7px 12px",
                              borderRadius: 999,
                              border: `1.5px solid ${active ? C.primary : C.border}`,
                              background: active ? C.primary : ok ? C.surface : C.surfaceAlt,
                              color: active ? "#fff" : ok ? C.text : C.muted,
                              fontSize: 12.5,
                              fontWeight: 800,
                              cursor: ok ? "pointer" : "not-allowed",
                              maxWidth: 280,
                              textAlign: "left",
                            }}
                          >
                            {b.teamName}{b.error ? " ✕" : ok ? "" : " …"}
                            {ok && (
                              <span style={{ display: "block", fontSize: 10.5, fontWeight: 650, opacity: 0.85 }}>
                                {(b.members || []).length} members · own problem
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {rosterMode === "individuals" && (batchProgress || batchIndividualPlans.length > 0) && (
                <div style={{
                  marginBottom: 14, padding: "12px 14px", borderRadius: 12,
                  border: `1px solid ${C.primary}33`, background: C.primarySoft,
                }}>
                  {batchProgress ? (
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.primaryDark }}>
                      Individuals batch: {batchProgress.index}/{batchProgress.total} — {batchProgress.name}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.muted, mb: 1, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Individual schedules ({batchIndividualPlans.filter((b) => b.plans?.daily).length}/{batchIndividualPlans.length})
                    </Typography>
                  )}
                  {batchIndividualPlans.length > 0 && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                      {batchIndividualPlans.map((b) => {
                        const active = batchActiveName === b.name;
                        const ok = !!b.plans?.daily?.rows?.length;
                        return (
                          <button
                            key={b.name}
                            type="button"
                            disabled={!ok}
                            onClick={() => {
                              if (!ok) return;
                              setBatchActiveName(b.name);
                              setPlans(b.plans);
                            }}
                            style={{
                              padding: "7px 12px",
                              borderRadius: 999,
                              border: `1.5px solid ${active ? C.primary : C.border}`,
                              background: active ? C.primary : ok ? C.surface : C.surfaceAlt,
                              color: active ? "#fff" : ok ? C.text : C.muted,
                              fontSize: 12.5,
                              fontWeight: 800,
                              cursor: ok ? "pointer" : "not-allowed",
                            }}
                          >
                            {b.name}{b.error ? " ✕" : ok ? "" : " …"}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Member switcher + Sync. Weekly: Team shared OR Individual member weekly. */}
              {rosterMode !== "individuals" && (isMultiTeam
                ? ((rosterTeams.find((t) => t.key === (activeTeamKey || rosterTeams[0]?.key))?.members || []).filter((mm) => mm.name?.trim()).length >= 2)
                : teamMembers.filter(mm => mm.name?.trim()).length >= 2) && (
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:14 }}>
                  {(activeTab === "daily" || activeTab === "homework") && (
                    <>
                      <span style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.04em" }}>
                        {activeTab === "homework" ? "Member homework" : "Member plans"}
                        {isMultiTeam ? ` · ${rosterTeams.find((t) => t.key === (activeTeamKey || rosterTeams[0]?.key))?.name || "Team"}` : ""}
                      </span>
                      {(teamSchedule?.members?.length
                        ? teamSchedule.members
                        : (isMultiTeam
                          ? (rosterTeams.find((t) => t.key === (activeTeamKey || rosterTeams[0]?.key))?.members || [])
                          : teamMembers
                        ).filter((mm) => mm.name?.trim()).map((mm, i) => ({
                            id: `local-${i}`,
                            name: mm.name.trim(),
                          }))
                      ).map((mm) => {
                        const mid = String(mm.id);
                        const selectedId = teamViewMemberId
                          || (teamSchedule?.members?.[0] ? String(teamSchedule.members[0].id) : "local-0");
                        const active = selectedId === mid;
                        return (
                          <button
                            key={mid}
                            type="button"
                            onClick={() => setTeamViewMemberId(mid)}
                            style={{
                              padding:"6px 14px", borderRadius:20, cursor:"pointer", fontSize:12, fontWeight:700,
                              border:`1.5px solid ${active ? C.dtPurple : C.border}`,
                              background: active ? C.dtPurple : C.surface,
                              color: active ? "#fff" : C.muted,
                            }}
                          >
                            {mm.name}
                          </button>
                        );
                      })}
                      {teamSchedule?.members?.length >= 2 && (
                        <span style={{ fontSize:11, color:C.muted }}>
                          Showing 1 at a time — click names to switch ({teamSchedule.members.length} synced)
                        </span>
                      )}
                    </>
                  )}
                  {activeTab === "weekly" && (
                    <>
                      <span style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.04em" }}>
                        Weekly view
                      </span>
                      {[
                        { id: "team", label: "Team shared weekly" },
                        { id: "individual", label: "Member weekly" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setWeeklyViewMode(opt.id)}
                          style={{
                            padding:"6px 14px", borderRadius:20, cursor:"pointer", fontSize:12, fontWeight:700,
                            border:`1.5px solid ${weeklyViewMode === opt.id ? C.dtPurple : C.border}`,
                            background: weeklyViewMode === opt.id ? C.dtPurple : C.surface,
                            color: weeklyViewMode === opt.id ? "#fff" : C.muted,
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                      <span style={{ fontSize:11, color:C.muted, maxWidth: 360 }}>
                        {weeklyViewMode === "team"
                          ? "Shared milestones for the whole team — same week goals for everyone."
                          : "Each member’s own weekly summary from their daily plan — avoids role mix-ups."}
                      </span>
                      {weeklyViewMode === "individual" && (teamSchedule?.members || []).map((mm) => {
                        const mid = String(mm.id);
                        const selectedId = teamViewMemberId || (teamSchedule?.members?.[0] ? String(teamSchedule.members[0].id) : "");
                        const active = selectedId === mid;
                        return (
                          <button
                            key={mid}
                            type="button"
                            onClick={() => setTeamViewMemberId(mid)}
                            style={{
                              padding:"6px 14px", borderRadius:20, cursor:"pointer", fontSize:12, fontWeight:700,
                              border:`1.5px solid ${active ? "#0E9F6E" : C.border}`,
                              background: active ? "#0E9F6E" : C.surface,
                              color: active ? "#fff" : C.muted,
                            }}
                          >
                            {mm.name}
                          </button>
                        );
                      })}
                    </>
                  )}
                  {teamGenerating ? (
                    <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#7C3AED", fontWeight:600, marginLeft:"auto" }}>
                      <div style={{ width:14, height:14, border:"2px solid #DDD6FE", borderTopColor:"#7C3AED", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
                      Syncing team…
                    </div>
                  ) : (
                    <Btn small variant="ghost" onClick={syncTeamSchedulesNow} style={{ marginLeft:"auto" }}>
                      <i className="ti ti-refresh" style={{ fontSize:13 }} /> {teamSchedule ? "Re-sync" : "Sync"} Team
                    </Btn>
                  )}
                </div>
              )}

              {MODULES.filter((m) => m.key === activeTab).map((m) => {
                const namedMembers = teamMembers.filter((mm) => mm.name?.trim());
                const isTeam = namedMembers.length >= 2;

                // Homework board — separate HomeworkNight collection (not DailyPlan)
                if (m.key === "homework") {
                  let hwPlan = homeworkHasBody(plans.homework?.rows)
                    ? plans.homework
                    : homeworkPlanFromDaily(plans.daily) || plans.homework;
                  if (isTeam && teamSchedule?.schedule?.length) {
                    const synced = teamSchedule.members || [];
                    const selectedId = teamViewMemberId || (synced[0] ? String(synced[0].id) : null);
                    const memberObj = synced.find((mm) => String(mm.id) === String(selectedId)) || synced[0];
                    if (memberObj) {
                      const memberDaily = [];
                      (teamSchedule.schedule || []).forEach((day) => {
                        const a = (day.assignments || []).find((x) => String(x.memberId) === String(memberObj.id));
                        const dayId = day.dayId || `Week ${day.week || 1} - ${day.dayName || "Monday"}`;
                        (a?.rows || []).forEach((r) => {
                          if (Array.isArray(r) && r.length >= 3) {
                            memberDaily.push([dayId, r[1], r[2], r[3]]);
                          } else if (r && typeof r === "object") {
                            memberDaily.push([dayId, r.time || "", r.activity || "", r.content || ""]);
                          }
                        });
                      });
                      const filled = homeworkPlanFromDaily({ rows: memberDaily });
                      if (homeworkHasBody(filled?.rows)) hwPlan = filled;
                    }
                  }
                  return (
                    <HomeworkPlanView
                      key="homework"
                      plan={hwPlan}
                      dailyPlan={plans.daily || null}
                      isTeam={isTeam}
                      memberMeta={resolveVersionMember()}
                      onPlanChange={(next) => {
                        const homeworkPlan = {
                          module: "homework",
                          title: "Tonight's Homework",
                          ...(next || {}),
                          rows: next?.rows || [],
                        };
                        setPlans((p) => ({ ...p, homework: homeworkPlan }));
                        persistHomeworkToDb(homeworkPlan, true);
                      }}
                      onDayRegenerated={handleSingleDayRegenerated}
                      onSyncTeam={isTeam ? syncTeamSchedulesNow : undefined}
                      onOpenDaily={({ week, day } = {}) => {
                        setDailyJump({
                          week: week ?? 1,
                          day: day || "Monday",
                          token: Date.now(),
                        });
                        setActiveTab("daily");
                      }}
                      emptyHint={
                        generating || loading.homework || loading.daily
                          ? "Generating… homework will load after it syncs with today's schedule."
                          : isTeam && !teamSchedule?.schedule?.length
                          ? "Generate the daily plan and Sync Team — Tonight's Homework will show here for each member."
                          : "Generate a daily schedule first. Tonight's Homework is stored in its own Homework DB collection."
                      }
                    />
                  );
                }

                // Weekly + team individual view
                if (m.key === "weekly" && isTeam && weeklyViewMode === "individual" && teamSchedule?.schedule?.length) {
                  const synced = teamSchedule.members || [];
                  const selectedId = teamViewMemberId || (synced[0] ? String(synced[0].id) : null);
                  const memberObj = synced.find((mm) => String(mm.id) === String(selectedId)) || synced[0];
                  if (!memberObj) {
                    return (
                      <div key={m.key} style={{ padding: 24, color: C.muted, fontSize: 13 }}>
                        Sync team first to see individual weekly plans.
                      </div>
                    );
                  }
                  return (
                    <TeamMemberPlanCard
                      key={`${m.key}-indiv-${memberObj.id}`}
                      member={memberObj}
                      activityOverrides={slotOverrides.daily}
                      schedule={teamSchedule.schedule}
                      module="weekly"
                      teamName={teamName}
                      sharedWeeklyPlan={null}
                      onScheduleUpdate={persistTeamSchedule}
                    />
                  );
                }

                // Weekly + team individual but not synced yet
                if (m.key === "weekly" && isTeam && weeklyViewMode === "individual" && !teamSchedule?.schedule?.length) {
                  return (
                    <div key={`${m.key}-indiv-empty`} style={{
                      padding: "24px 18px", borderRadius: 12,
                      background: "#E7F0F6", border: "1.5px solid #93C5E4", color: "#143B56", fontSize: 13,
                    }}>
                      Generate the daily plan, then click <strong>Sync Team</strong> to unlock each member’s individual weekly schedule.
                      Until then, use <strong>Team shared weekly</strong>.
                    </div>
                  );
                }

                // Weekly + team shared (or solo weekly)
                if (m.key === "weekly") {
                  return (
                    <ModuleCard
                      key={m.key}
                      moduleKey={m.key}
                      label={isTeam ? "Team shared weekly plan" : m.label}
                      icon={m.icon}
                      plan={
                        (plans.daily?.rows?.length && buildWeeklyFromDaily(plans.daily, plans.weekly)) ||
                        plans[m.key]
                      }
                      inputs={textInputs}
                      calMap={calendarData?.calMap}
                      calendarMeta={calendarData?.meta}
                      onUpdate={handleModuleUpdate}
                      loading={!!loading[m.key]}
                      onDayRegenerated={handleSingleDayRegenerated}
                      memberMeta={resolveVersionMember()}
                      fileLabel={isTeam ? (teamName || "Team") : (namedMembers[0]?.name || user?.name || "Solo")}
                    />
                  );
                }

                // Daily + team + synced schedule → per-member card
                if (m.key === "daily" && isTeam && teamSchedule?.schedule?.length > 0) {
                  const synced = teamSchedule.members || [];
                  const selectedId = teamViewMemberId || (synced[0] ? String(synced[0].id) : null);
                  const memberObj = synced.find((mm) => String(mm.id) === String(selectedId))
                    || synced.find((mm) => String(mm.name || "").trim().toLowerCase() === String(namedMembers[0]?.name || "").trim().toLowerCase())
                    || synced[0];
                  if (!memberObj) {
                    return (
                      <div key={m.key} style={{ padding:"32px 20px", borderRadius:12,
                        background:"#F9FAFB", border:`2px dashed ${C.border}`,
                        textAlign:"center", color:C.muted, fontSize:13 }}>
                        Sync completed but no member data found. Click <strong style={{ color:C.text }}>Re-sync Team</strong>.
                      </div>
                    );
                  }
                  const isFirst = synced[0] && String(synced[0].id) === String(memberObj.id);
                  return (
                    <TeamMemberPlanCard
                      key={`${m.key}-${memberObj.id}`}
                      member={memberObj}
                      schedule={teamSchedule.schedule}
                      module="daily"
                      teamName={teamName}
                      enableDayActions={isFirst}
                      calMap={calendarData?.calMap}
                      calendarMeta={calendarData?.meta}
                      sharedWeeklyPlan={plans.weekly}
                      onScheduleUpdate={persistTeamSchedule}
                      onRestored={handleVersionRestored}
                      focusWeek={dailyJump?.week}
                      focusDay={dailyJump?.day}
                      focusToken={dailyJump?.token}
                    />
                  );
                }

                // Daily + team but not synced yet
                if (m.key === "daily" && isTeam && !teamSchedule?.schedule?.length) {
                  return (
                    <div key={m.key}>
                      <div style={{ padding:"16px 18px", borderRadius:10, marginBottom:12,
                        background:"#E7F0F6", border:`1.5px solid #93C5E4`, color:"#143B56", fontSize:13 }}>
                        Add <strong>names for both members</strong>, generate the daily plan, then click <strong>Sync Team</strong>.
                        After sync, use the name buttons above to open each person’s schedule.
                      </div>
                      <ModuleCard
                        moduleKey={m.key}
                        label={m.label}
                        icon={m.icon}
                        plan={plans[m.key]}
                      activityOverrides={slotOverrides.daily}
                        inputs={textInputs}
                        calMap={calendarData?.calMap}
                        calendarMeta={calendarData?.meta}
                        onUpdate={handleModuleUpdate}
                        onRestored={handleVersionRestored}
                        loading={!!loading[m.key]}
                        onDayRegenerated={handleSingleDayRegenerated}
                        memberMeta={resolveVersionMember()}
                        fileLabel={teamName || "Team"}
                        focusWeek={dailyJump?.week}
                        focusDay={dailyJump?.day}
                        focusToken={dailyJump?.token}
                      />
                    </div>
                  );
                }

                // Solo (or other modules)
                return (
                  <ModuleCard
                    key={m.key}
                    moduleKey={m.key}
                    label={m.label}
                    icon={m.icon}
                    plan={plans[m.key]}
                    activityOverrides={m.key === "daily" ? slotOverrides.daily : null}
                    inputs={textInputs}
                    calMap={calendarData?.calMap}
                    calendarMeta={calendarData?.meta}
                    onUpdate={handleModuleUpdate}
                    onRestored={handleVersionRestored}
                    loading={!!loading[m.key]}
                    onDayRegenerated={handleSingleDayRegenerated}
                    memberMeta={resolveVersionMember()}
                    focusWeek={m.key === "daily" ? dailyJump?.week : null}
                    focusDay={m.key === "daily" ? dailyJump?.day : null}
                    focusToken={m.key === "daily" ? dailyJump?.token : null}
                    fileLabel={namedMembers[0]?.name || user?.name || user?.username || user?.email || "Solo"}
                  />
                );
              })}
            </Box>
          )}

      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
      `}</style>
    </Box>
  );
}
